// Copyright (c) Ping. All rights reserved.
//
// Web host shim around the shared `<ChatRoom>` from `@ping/ui`.
// All presentation moved into packages/ui/src/chat/ChatRoom.tsx;
// the host's job is to thread the Dexie-backed `useMessages`
// subscription, the MLS `conv.send` orchestration, and the
// Dexie-backed local-echo persisters into the shared component.
//
// The previous monolithic file (bubble list + composer + retry UI)
// was retired in favour of `<ChatRoom>`, which now renders bubbles
// via `<MessageRow>` from @ping/ui — that path already has the
// failed/retry UI baked in.

import { type RefObject,useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, Text, View } from 'react-native';

import {
  appEventTypeForKind,
  AttachmentPreviewOverlay,
  buildMeetingCancelPayload,
  buildMeetingRsvpPayload,
  buildMeetingUpdatePayload,
  CameraCaptureOverlay,
  type ChatInfoMediaFile,
  type ChatInfoMediaLink,
  type ChatInfoMember,
  type ChatInfoMessagePreview,
  ChatInfoModal,
  type ChatInfoNotificationLevel,
  type ChatInfoThread,
  type ChatMessageVariant,
  ChatRoom,
  commitSentMessage as sharedCommitSentMessage,
  deriveVariantFromBody,
  DragDropOverlay,
  EMPTY_MEETING_STATE,
  encodeEnvelopeJsonBytes,
  fileToImageSource,
  forgetScheduled,
  ForwardOverlay,
  type GifItem,
  type GifSearchProvider,
  GroupJoinRequestBanner,
  groupSenderAvatarFromLabel,
  isUnresolvedGroupSenderLabel,
  lastMessagePreview,
  type LinkPreviewBody,
  listMirrorForConversation,
  markMessageFailed as sharedMarkMessageFailed,
  type MediaPreviewItem,
  MediaPreviewScreen,
  type MeetingEvent,
  type MeetingRsvpResponse,
  type MeetingState,
  meetingStatus,
  mergeScheduledMeta,
  type Message,
  mintEventId,
  myRsvp as meetingMyRsvp,
  parseEnvelope,
  persistFailedMessage as sharedPersistFailedMessage,
  persistPendingMessage as sharedPersistPendingMessage,
  persistSentMessage as sharedPersistSentMessage,
  PollVotesPanel,
  type ReactionReactor,
  ReactionReactorResolverProvider,
  reduceMeeting,
  rememberScheduled,
  type ReportReason,
  ReportReasonSheet,
  resolveGroupSenderAccountId,
  resolveGroupSenderLabel,
  rsvpCounts as meetingRsvpCounts,
  type ScheduledMessage,
  sendReadReceipt as sharedSendReadReceipt,
  serializeMentions,
  type ShareProfileContact,
  type ShareResult,
  type StickerSearchProvider,
  StrandedRepairBanner,
  type ThreadIdentifier,
  useChatRoomShellHandlers,
  useConversations,
  useGameSessions,
  useMessages,
  useReactions,
  type UserId,
  useSession,
  type VoiceSource,
} from '@ping/ui';
import type { AppEventBody } from '@ping/ui/src/appEvent/envelope';
import type {
  GameAbortBody,
  GameCreateBody,
  GameJoinBody,
  GameMoveBody,
  GameOutcomeBody,
  GameRevealBody,
} from '@ping/ui/src/appEvent/types';
import type { ChatActiveCallStatus,ChatPresence } from '@ping/ui/src/chat/ChatRoom.types';
import { buildGroupConversationMetaName } from '@ping/ui/src/conversation/groupConversation';
import {
  deviceKeyFromBytes,
  useEphemeralPresence,
  useEphemeralTyping,
} from '@ping/ui/src/ephemeral';
import { getCurrentUserAccountId, isSelfMlsUserIdHex } from '@ping/ui/src/sync/registerCurrentUser';
import type { GameEvent } from '@ping/ui/src/sync/useGameSessions';

import { messageSoundManager } from '@app/notifications/MessageSoundManager';
import { useCallStore } from '@calls/store/callStore';
import { dataUrlToBlob } from '@core/dataUrl';
import { isFeatureEnabled } from '@core/featureFlags';
import { log } from '@core/log/log';
import { post } from '@core/network/api';
import { addContact } from '@core/network/contacts';
import { postReport } from '@core/network/reports';
import {
  claimAllDevicesByAccountId,
  getUserProfile,
  getUserProfiles,
  searchUsers,
} from '@core/network/users';
import {
  clearConversationMessages,
  get as getConversationRow,
  markConversationLeft,
  markConversationLeftLocal,
  setConversationAvatar,
  setConversationAvatarMediaId,
  setConversationLeftEpoch,
  setConversationName,
  setDisappearingTtl as setConversationTtl,
  setFavorite as setConversationFavorite,
  setMemberRole as setConversationMemberRole,
  setNickname as setConversationNickname,
  setNotificationLevel,
  setSafetyVerified as setConversationSafetyVerified,
  sweepExpiredMessages,
} from '@core/persistence/conversations';
import {
  type CallNote,
  webCallNotesPersister,
  webMediaThumbCache,
  webMeetingRemindersPersister,
  webPollVotesPersister,
  webReactionsPersister,
  webReadPointersPersister,
} from '@core/persistence/db';
import { retrieveIdentity } from '@core/persistence/identity';
import { dequeueOutbox, enqueueOutbox } from '@core/persistence/outbox';
import { decodeGroupJoinRequest } from '@features/chat/groupInvite';
import { buildGroupInviteLink, buildUserInviteLink } from '@features/chat/inviteLink';
import { resendDirectProfileIntro } from '@features/chat/resendDirectProfileIntro';
import { ensureRecoveredPeerDevicesAdmittedForConversation } from '@features/history/recoveredPeerAdmit';
import {
  type MeetingDraft,
  MeetingModal,
  type MeetingModalInitial,
  MeetingResponsesModal,
} from '@features/meetings';
import { useSelfProfile } from '@features/profile/selfProfileStore';
import { usePreferences } from '@features/settings/usePreferences';
import {
  type AvatarContentType,
  requestAvatarUploadUrl,
  uploadAvatarBytes,
} from '@features/settings/web/profilesApi';
import { sendStar } from '@features/tasks/deviceGroupTaskSync';
import { toastStore } from '@features/toasts/toastStore';
import { getCurrentEphemeralClient } from '@session/ephemeralSession';
import { commitPendingProposalsAndSurfaceStranded } from '@session/pendingProposalCommitter';
import { getCurrentTransport } from '@session/sessionBootstrap';
import {
  clearStrandedConversation,
  subscribeStrandedConversations,
} from '@session/strandedConversationsStore';

import { AddGroupMembersModal } from './AddGroupMembersModal';
import { addWebGroupMembers, EPOCH_DESYNC_MESSAGE, isEpochConflictError } from './addWebGroupMembers';
import { clearChatJump, peekChatJump, subscribeChatJump } from './chatJumpTarget';
import { ensureConversation } from './ensureConversation';
import { fetchAndDecryptMediaBlob } from './fetchMediaBlob';
import { InChatSearchOverlay, type InChatSearchResult } from './InChatSearchOverlay';
import { createKlipyGifProvider, createKlipyStickerProvider } from './klipyProvider';
import { extractFirstUrl, type UnfurledLink,unfurlLink } from './linkUnfurl';
import { addLocallyRemovedMessage } from './locallyRemovedMessages';
import { fetchMediaDownloadUrl } from './mediaUpload';
import { backfillGroupDeviceMaps, hydrateMemberProfiles } from './memberProfileHydration';
import { getOrCreateMlsConversation } from './mlsConversation';
import { webConversationsPersister, webMessagesPersister } from './persistenceAdapters';
import { persistLocalRoomEvent } from './persistLocalRoomEvent';
import { webStarredPersister } from './personalBookmarks';
import { pickAudioFile } from './pickAudioFile';
import { pickAvatarDataUrl } from './pickAvatarDataUrl';
import { formatVideoDuration, type ProbedVideo,probeVideoFile } from './probeVideoFile';
import { removeWebGroupMembers } from './removeWebGroupMembers';
import { canPreviewDoc, renderDocThumbnail } from './renderPdfThumbnail';
import { parseFileEnvelope, sendFileMessage } from './sendFileMessage';
import { parseGifEnvelope, sendGifMessage } from './sendGifMessage';
import {
  bundleForSource,
  isImageEnvelopeBody,
  parseImageEnvelope,
  sendImageMessage,
} from './sendImageMessage';
import { drainOutbox } from './sendOutbox';
import { parseVideoEnvelope, sendVideoMessage } from './sendVideoMessage';
import {
  parseVoiceEnvelope,
  sendVoiceMessage,
  VoiceNoteTooLargeError,
} from './sendVoiceMessage';
import { WebVoiceRecorder } from './voiceRecorder';
import { registerWebImageUpload } from './webImageUploadBridge';
import { THREAD_READ_POINTER_PREFIX } from './webThreadGateway';

// Chat-room toasts are intentionally suppressed: per product direction NO
// toast fires for any action performed in the chat room (copy / pin /
// remove / forward AND send/upload failures — failures surface inline at
// the message bubble instead). This no-op shim keeps the existing
// `pingToast.*` call-sites compiling while emitting nothing; it shadows
// the real `pingToast` from `@ping/ui`. Errors are still logged via `log`.
const pingToast = {
  success: (_args?: unknown): void => {},
  info: (_args?: unknown): void => {},
  error: (_args?: unknown): void => {},
};

// Process-lifetime cache of resolved group-member avatar URIs (accountId →
// uri | null). The directory has no batch endpoint, so avatars are resolved one
// member at a time; without this cache the resolution effect re-fires on every
// render (its inputs are fresh object refs) and floods /v1/users/search into
// 429s. `null` = resolved-but-no-avatar (don't retry); a missing key = not yet
// resolved (may retry when the member set next changes).
const memberAvatarCache = new Map<string, string | null>();

interface WebChatRoomProps {
  readonly conversationId: string;
  readonly conversationName?: string | undefined;
  /** Leave/delete this conversation (Chat Info → Settings). Soft-deletes the
   *  local row + navigates back to the empty state. */
  readonly onDeleteConversation?: (() => void) | undefined;
}

// Mirror of the SDK's MessageEnvelope return from conv.send. Only the
// fields we use for local-echo are typed.
interface SentMessageEnvelope {
  conversation_id: Uint8Array;
  epoch: number | bigint;
  content_hash: Uint8Array;
  hlc: { wall_ms: number | bigint; logical: number };
}

interface SendableConversation {
  send(plaintext: Uint8Array): Promise<SentMessageEnvelope>;
  /** Bulletproof group rename / avatar-id change via a GroupContextExtensions
   *  commit (ping-openmls-sdk ≥ 0.7.6). Optional: absent on older bindings, so
   *  callers fall back to the `com.ping.group_name` / `group_avatar` broadcast. */
  setConversationName?(name: string): Promise<void>;
}

/** 1:1 conversation-meta prefix — the SDK conversation NAME carries the peer's
 *  `{inviter,peer}` identity as `ping:meta:v1:<json>` (mirrors NewChatModal /
 *  GlobalSearchModalHost). Used when starting a chat with a directory-searched
 *  user from the Share modal. */
const CONV_META_PREFIX = 'ping:meta:v1:';

/** Rebuild the full `ping:meta:v1:` group name (preserving the roster) with an
 *  updated title and/or avatar-media-id, for the bulletproof `setConversationName`
 *  SDK op. The inviter is set to self — `parseGroupConvMeta` filters the local
 *  account out of the parsed roster, so this never pollutes membership. */
function buildUpdatedGroupMetaName(params: {
  readonly memberAccountIds?: readonly string[] | undefined;
  readonly memberDisplayNames?: Record<string, string> | undefined;
  readonly selfAccountId: string;
  readonly selfDisplayName: string;
  readonly title?: string | undefined;
  readonly avatarMediaId?: string | undefined;
}): string {
  const members = (params.memberAccountIds ?? []).map((id) => ({
    accountId: id,
    displayName: params.memberDisplayNames?.[id] ?? '',
  }));
  return buildGroupConversationMetaName({
    inviter: { accountId: params.selfAccountId, displayName: params.selfDisplayName },
    members,
    ...(params.title !== undefined && params.title.length > 0 ? { title: params.title } : {}),
    ...(params.avatarMediaId !== undefined && params.avatarMediaId.length > 0
      ? { avatarMediaId: params.avatarMediaId }
      : {}),
  });
}

/** Upload a (data-URL) group avatar to media → media id, for embedding in the
 *  group meta. Best-effort: returns `undefined` on any failure. */
async function uploadGroupAvatarDataUrl(dataUrl: string): Promise<string | undefined> {
  try {
    // Decode in-process — `fetch('data:…')` is CSP-blocked (`connect-src` has no
    // `data:` scheme), which silently failed avatar upload. See `core/dataUrl.ts`.
    const blob = dataUrlToBlob(dataUrl);
    const contentType: AvatarContentType =
      blob.type === 'image/webp' ? 'image/webp' : 'image/jpeg';
    const upload = await requestAvatarUploadUrl(contentType);
    await uploadAvatarBytes(upload, blob);
    return upload.blobKey;
  } catch {
    return undefined;
  }
}

/** Serialize saved Call-Notes to Markdown for download. */
function callNotesToMarkdown(note: CallNote): string {
  const lines: string[] = [`# ${note.title || 'Call notes'}`, ''];
  if (note.text.trim().length > 0) lines.push(note.text.trim(), '');
  if (note.items.length > 0) {
    lines.push('## Action items', '');
    for (const it of note.items) lines.push(`- [${it.done ? 'x' : ' '}] ${it.text}`);
  }
  return `${lines.join('\n')}\n`;
}

/** A decoded group-join request folded out of the 1-1 timeline. */
interface GroupJoinRequestItem {
  readonly id: string;
  /** True if WE sent it (we're the requester) — no Approve banner then. */
  readonly isSelf: boolean;
  readonly senderId: string;
  readonly groupId: string;
  readonly requesterDisplayName?: string | undefined;
  readonly timestamp: number;
}

export function WebChatRoom({
  conversationId,
  conversationName,
  onDeleteConversation,
}: WebChatRoomProps) {
  const messagesState = useMessages(conversationId);
  const { client } = useSession();
  const { conversations: allConversations } = useConversations();
  // Honor the "Press Enter to send" preference (persisted in the web
  // preferences singleton; toggled in Settings → Preferences). Forwarded to
  // the shared composer as `<ChatRoom pressEnterToSend>`.
  const { enterToSend } = usePreferences();
  // Shell overlay handlers (thread panel, poll/location composers, …)
  // published by `ChatRouteHost` via `ChatRoomShellHandlersProvider`.
  // Declared up-front so earlier callbacks (`handleMenuAction`) can
  // dispatch `openThread` without a use-before-declaration error.
  const shellHandlers = useChatRoomShellHandlers();

  const localConv = useMemo(
    () => allConversations.find((c) => c.id === conversationId),
    [allConversations, conversationId],
  );
  const isGroup = localConv?.kind === 'group';
  // True once the local user has LEFT this group (non-destructive `leftAt`
  // marker). The row + history stay visible; the composer goes read-only.
  const hasLeftGroup = ((localConv as { leftAt?: number } | undefined)?.leftAt ?? 0) > 0;
  const headerTitle = useMemo(() => {
    if (localConv?.name !== undefined && localConv.name.length > 0) {
      return localConv.name;
    }
    if (conversationName !== undefined && conversationName.length > 0) {
      return conversationName;
    }
    return isGroup ? 'Group chat' : 'New chat';
  }, [localConv?.name, conversationName, isGroup]);

  // Conversation-name resilience across delete+resume. Deleting a chat drops the
  // Dexie row; the peer's next message re-creates it with a placeholder name
  // ("New chat" for 1:1, "Group chat" for groups) because the original
  // name source (1:1 `ping.profile`, group `group_name` hydration) won't
  // re-arrive. We keep a per-device name cache (localStorage) that OUTLIVES the
  // row delete, so resume restores the real name instantly — for BOTH 1:1 and
  // groups. As a fallback for a name we've never cached on this device, a 1:1
  // also fetches the peer's directory display name.
  const convName = localConv?.name;
  const convPeerId = localConv?.peerAccountId;
  // (a) Persist a known-good name so a future resume can restore it.
  useEffect(() => {
    if (convName !== undefined && !isPlaceholderConvName(convName)) {
      writeCachedConvName(conversationId, convName);
    }
  }, [conversationId, convName]);
  // (b) Restore the name when the current one is a placeholder.
  useEffect(() => {
    if (convName !== undefined && !isPlaceholderConvName(convName)) return;
    const cached = readCachedConvName(conversationId);
    if (cached !== null) {
      void setConversationName(conversationId, cached);
      return;
    }
    // No cache (e.g. a freshly-linked device): 1:1 can still recover the peer
    // name from the directory; groups have no other source → keep placeholder.
    if (isGroup || convPeerId === undefined || convPeerId.length === 0) return;
    let alive = true;
    void getUserProfile(convPeerId)
      .then((p) => {
        if (!alive || p === null || p.displayName.length === 0) return;
        writeCachedConvName(conversationId, p.displayName);
        void setConversationName(conversationId, p.displayName);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [conversationId, convName, convPeerId, isGroup]);

  // Peer count + 1 for ourselves — but NOT once we've left (we're no longer a
  // member, so we shouldn't be counted; the peers are still there for history).
  // `memberCount` / `authoritativeNonSelfCount` are defined below, once the LIVE
  // MLS roster (`mlsRosterMembers`) is in scope — they prefer it over the Dexie
  // `memberAccountIds`, which can lag an add/remove (see D4).

  // Roster to ring when a call starts: the group's peer accounts, or the 1:1
  // peer. Names feed the per-call group claim + participant tile labels.
  const callPeerIds = useMemo<string[]>(
    () =>
      isGroup
        ? [...(localConv?.memberAccountIds ?? [])]
        : convPeerId !== undefined && convPeerId.length > 0
          ? [convPeerId]
          : [],
    [isGroup, localConv?.memberAccountIds, convPeerId],
  );
  const callPeerNames = useMemo<Record<string, string>>(() => {
    if (isGroup) return { ...(localConv?.memberDisplayNames ?? {}) };
    return convPeerId !== undefined && convPeerId.length > 0
      ? { [convPeerId]: headerTitle }
      : {};
  }, [isGroup, localConv?.memberDisplayNames, convPeerId, headerTitle]);

  const groupCreateEnabled = isFeatureEnabled('FF_GROUP_CREATE');
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [chatSettingsOpen, setChatSettingsOpen] = useState(false);
  const [reportSheetOpen, setReportSheetOpen] = useState(false);
  // Local Chat-Info state — read straight from Dexie (per-device prefs not on
  // the shared conversation type).
  const [chatInfoFavorite, setChatInfoFavorite] = useState(false);
  const [chatInfoNickname, setChatInfoNickname] = useState('');
  const [chatInfoTtl, setChatInfoTtl] = useState(0);
  const [chatInfoRoles, setChatInfoRoles] = useState<Record<string, 'owner' | 'admin' | 'member'>>(
    {},
  );
  const [chatInfoVerified, setChatInfoVerified] = useState(false);
  const [chatInfoAvatar, setChatInfoAvatar] = useState<string | undefined>(undefined);
  // In-conversation message search (opened from the header / Chat Info "Search").
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Group members derived from the LIVE MLS roster (accurate on every member's
  // device, unlike the Dexie roster which is only complete on the creator until
  // hydration lands). Populated when the Chat Info modal opens.
  const [mlsRosterMembers, setMlsRosterMembers] = useState<ChatInfoMember[]>([]);
  // CANONICAL non-self roster — the single deduped, self-excluded member set that
  // BOTH the header "N members" count and the Chat Info members list are built
  // from, so they can never disagree (the complaint: header showed a different
  // count than the modal). Self-exclusion here is what fixes the off-by-one: the
  // Dexie `memberAccountIds` can transiently INCLUDE self (the `com.ping.invite` /
  // `com.ping.group_name` union-merges carry the full roster incl. self, and a
  // null-identity moment during hydration can leak it), so counting the raw array
  // over-counted by one. `getCurrentUserAccountId()` is the synchronous canonical
  // self id (16-byte account id), resolved well before a roster exists.
  const nonSelfMemberAccountIds = useMemo<string[]>(() => {
    const self = getCurrentUserAccountId();
    const seen = new Set<string>();
    const out: string[] = [];
    for (const id of localConv?.memberAccountIds ?? []) {
      if (id.length === 0 || id === self || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    return out;
  }, [localConv?.memberAccountIds]);
  // Authoritative non-self member count. When Chat Info is open, `mlsRosterMembers`
  // is the enriched (also self-excluded) roster and has the same length; otherwise
  // fall back to the canonical set above. Drives the header "N members" subtitle
  // AND the all-read ✓✓ threshold (`groupNonSelfMemberCount`).
  const authoritativeNonSelfCount = isGroup
    ? mlsRosterMembers.length > 0
      ? mlsRosterMembers.length
      : nonSelfMemberAccountIds.length
    : 0;
  const memberCount = isGroup
    ? authoritativeNonSelfCount + (hasLeftGroup ? 0 : 1)
    : 0;
  // Per-conversation notification sound: `chatInfoSound` is the saved choice,
  // `pendingSound` is a picked-but-unsaved custom tone (enables "Save").
  const [chatInfoSound, setChatInfoSound] = useState<CustomSound | null>(null);
  const [pendingSound, setPendingSound] = useState<CustomSound | null>(null);
  // Header avatar (group image). Optimistic Chat-Info edit value wins; falls
  // back to the persisted conversation row's avatar.
  const headerAvatarUri = chatInfoAvatar ?? localConv?.avatarUri;
  // Clear the optimistic Chat-Info avatar override whenever the OPEN conversation
  // changes. `chatInfoAvatar` is scoped to one chat's edit session; without this
  // reset it persists across chat switches and `headerAvatarUri` (which prefers
  // it) keeps rendering the PREVIOUS chat's avatar. Resetting makes the header
  // fall back to the active row's `avatarUri` until Chat Info is reopened.
  useEffect(() => {
    setChatInfoAvatar(undefined);
  }, [conversationId]);
  useEffect(() => {
    if (!chatSettingsOpen) return;
    let alive = true;
    void getConversationRow(conversationId).then((row) => {
      if (!alive || row === undefined) return;
      setChatInfoFavorite(row.favorite === true);
      setChatInfoNickname(row.nickname ?? '');
      setChatInfoTtl(row.disappearingTtlSeconds ?? 0);
      setChatInfoRoles(row.memberRoles ?? {});
      setChatInfoVerified(row.safetyVerified === true);
      // Do NOT seed `chatInfoAvatar` from the row here — leaving it undefined
      // lets `headerAvatarUri` / the Chat-Info avatar fall back to the LIVE
      // `localConv.avatarUri`, so a remote avatar change reflects immediately
      // while the panel is open. `chatInfoAvatar` is set only as an OPTIMISTIC
      // override when THIS user picks a new photo.
      setChatInfoSound(loadCustomSound(conversationId));
      setPendingSound(null);
    });
    return () => {
      alive = false;
    };
  }, [chatSettingsOpen, conversationId]);

  // Re-resolve the header avatar to a FRESH presigned URL whenever Chat Info
  // opens. Group/peer avatars are stored as a stable `avatarMediaId` and
  // rendered via an expiring SigV4 presigned URL cached on the row — once that
  // lapses (~1h) it 403s and the header goes blank. Resolving on open guarantees
  // a live URL. Guarded: a failed/empty resolve NEVER clobbers the cached value
  // (we only ever SET a non-empty fresh URL, and `headerAvatarUri` prefers it).
  useEffect(() => {
    if (!chatSettingsOpen) return;
    const mediaId = localConv?.avatarMediaId;
    if (mediaId === undefined || mediaId.length === 0) return;
    let alive = true;
    void fetchMediaDownloadUrl(mediaId)
      .then((url) => {
        if (alive && url.length > 0) setChatInfoAvatar(url);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [chatSettingsOpen, localConv?.avatarMediaId, conversationId]);

  // Disappearing-messages sweep: hard-delete any locally-cached message whose
  // `expiresAt` has passed. Runs on mount + every 20s (self-destruct is a
  // best-effort local enforcement — peers run their own sweeps).
  useEffect(() => {
    let alive = true;
    const tick = (): void => {
      void sweepExpiredMessages(Date.now()).catch(() => undefined);
    };
    tick();
    const handle = setInterval(() => {
      if (alive) tick();
    }, 20_000);
    return () => {
      alive = false;
      clearInterval(handle);
    };
  }, []);

  // Group roster for the Chat Settings → Members tab. Names resolve from the
  // conversation roster; avatars fall back to initials (the roster stores media
  // ids, not URLs). Roles/admin aren't modelled yet, so they're left blank.
  /** Local accountId for reaction attribution + self-exclusion in the roster.
   *  Loaded async on mount; held in state (not just a ref) so `useReactions`
   *  re-projects the `reactedByMe` flag once identity resolves. Declared here
   *  (above the roster memos that reference it) to stay before first use. */
  const [localAccountId, setLocalAccountId] = useState<string | null>(null);
  const chatSettingsMembers = useMemo<ChatInfoMember[]>(() => {
    if (!isGroup) return [];
    const names = localConv?.memberDisplayNames ?? {};
    const avatars = localConv?.memberAvatarUris ?? {};
    // Built from the SAME canonical self-excluded/deduped set as the header count
    // (`nonSelfMemberAccountIds`), so the modal member list length + 1 always
    // equals the header "N members" — the two can never diverge.
    return nonSelfMemberAccountIds.map((id) => {
      const role = chatInfoRoles[id] ?? 'member';
      const isAdmin = role === 'admin' || role === 'owner';
      const avatarUri = avatars[id];
      return {
        id,
        name: names[id] ?? 'Member',
        role: role === 'member' ? undefined : role === 'owner' ? 'Owner' : 'Admin',
        isAdmin,
        ...(avatarUri !== undefined && avatarUri.length > 0 ? { avatarUri } : {}),
      };
    });
  }, [
    isGroup,
    localConv?.memberDisplayNames,
    localConv?.memberAvatarUris,
    nonSelfMemberAccountIds,
    chatInfoRoles,
  ]);

  // Chat Info → Notifications: only per-conversation MUTE is modelled today, so
  // Chat Info → Notifications: the per-conversation level ('all' | 'mentions' |
  // 'off') is read straight off `localConv.notificationLevel` at the prop site
  // below and persisted via `setNotificationLevel`.
  const conversationIdBytes = useMemo(
    () => hexToBytesLocal(conversationId),
    [conversationId],
  );
  const notifyComposerSentRef = useRef<() => void>(() => {});
  /** Mirror of the `mentionCandidates` useMemo (declared further down)
   *  so `handleSend` — a useCallback created earlier in the component
   *  body — can read the latest candidate set at send time without
   *  pulling the (later-declared) const into its closure / dep array. */
  const mentionCandidatesRef = useRef<{ userId: string; displayName: string }[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);
  // Sticky non-blocking banner — set when the most recent send went
  // through but the peer-invite step failed (or hasn't completed).
  const [inviteWarning, setInviteWarning] = useState<string | null>(null);
  /** Active reply target — drives the composer chip (`replyTo` /
   *  `onCancelReply` props on ChatRoom) and rides on the next
   *  outgoing envelope's `parent_id`. Cleared after each send. */
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  /** The message currently being edited (composer edit mode), or null. */
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  // Clear the module-global member-avatar cache on account switch so a previous
  // account's resolved (presigned) avatars can't leak into a new session (D7).
  // Avatars are content-addressed per account id, so this is belt-and-braces.
  useEffect(() => {
    memberAvatarCache.clear();
  }, [localAccountId]);
  /** Reaction projection — keyed by message id. Now sourced from the
   *  Dexie-backed OR-set via `useReactions` (live subscription) rather
   *  than a volatile in-memory map. Self toggles persist optimistically
   *  in `handleToggleReactionForMessage`; received reactions are
   *  persisted by the shared `useIncomingMessages` hook. */
  const reactionsByMessageId = useReactions(
    conversationId,
    webReactionsPersister,
    localAccountId ?? undefined,
  );
  // In-memory game-session projection for this conversation. The host
  // optimistically applies its own `game_create` (via the picker /
  // composer path); the `onMessage` tap below folds in PEER game
  // envelopes so both sides converge on the same session state.
  const { applyGameEvent, replaceFromEvents, sessions: gameSessions, makeMove } =
    useGameSessions(conversationId);

  // Build the Chat Info members list when the modal opens. We key off the
  // ACCOUNT-id roster (`memberAccountIds`), NOT the MLS roster: the SDK's
  // `members()` returns each member's `user_id` = sha256(identity_pubkey) (32
  // bytes), which is UNRELATED to the backend's random 16-byte `account_id`, so
  // `getUserProfile(user_id)` 400s and the local name maps (keyed by account id)
  // never match → every row collapsed to "Member"/"M". Account ids ARE
  // resolvable: `getUserProfile(accountId)` returns the real display name +
  // avatar, and roles/contact actions are account-keyed. (memberAccountIds is
  // seeded at creation + hydrated from each member's profile-intro on join.)
  useEffect(() => {
    if (!chatSettingsOpen || !isGroup) {
      setMlsRosterMembers([]);
      return;
    }
    const names = localConv?.memberDisplayNames ?? {};
    const persistedAvatars = localConv?.memberAvatarUris ?? {};
    // Same canonical self-excluded/deduped set as the header count so the enriched
    // modal list length matches `authoritativeNonSelfCount` exactly.
    const ids = nonSelfMemberAccountIds;
    const buildMember = (
      id: string,
      name: string,
      avatarUri: string | undefined,
    ): ChatInfoMember => {
      const role = chatInfoRoles[id] ?? 'member';
      const isAdmin = role === 'admin' || role === 'owner';
      return {
        id,
        name,
        role: role === 'member' ? undefined : role === 'owner' ? 'Owner' : 'Admin',
        isAdmin,
        ...(avatarUri !== undefined && avatarUri.length > 0 ? { avatarUri } : {}),
      };
    };
    // Instant render from the PERSISTED roster (names + avatars survive reload /
    // offline); enrich with a fresh directory profile below.
    setMlsRosterMembers(ids.map((id) => buildMember(id, names[id] ?? 'Member', persistedAvatars[id])));
    if (ids.length === 0) return;
    let alive = true;
    void (async () => {
      const byId = await getUserProfiles(ids).catch(() => new Map());
      const enriched = await Promise.all(
        ids.map(async (id): Promise<ChatInfoMember> => {
          const p = byId.get(id) ?? null;
          let avatarUri: string | undefined;
          if (p?.avatarMediaId !== undefined && p.avatarMediaId !== null) {
            avatarUri = await fetchMediaDownloadUrl(p.avatarMediaId).catch(() => undefined);
          }
          const name =
            p?.displayName !== undefined && p.displayName.length > 0
              ? p.displayName
              : names[id] ?? 'Member';
          return buildMember(id, name, avatarUri);
        }),
      );
      if (alive) setMlsRosterMembers(enriched);
    })();
    return () => {
      alive = false;
    };
  }, [
    chatSettingsOpen,
    isGroup,
    nonSelfMemberAccountIds,
    localConv?.memberDisplayNames,
    localConv?.memberAvatarUris,
    chatInfoRoles,
  ]);

  // Cache the resolved MLS Conversation proxy per BE conversationId.
  const convProxyRef = useRef<{ id: string; conv: SendableConversation } | null>(null);

  // Web MediaRecorder wrapper. Kept on a ref so re-renders don't
  // tear it down between user actions (the same instance is reused
  // across conversations — only one recording can be active at a
  // time anyway).
  const recorderRef = useRef<WebVoiceRecorder | null>(null);
  if (recorderRef.current === null) {
    recorderRef.current = new WebVoiceRecorder();
  }

  // KLIPY adapter — wraps the public KLIPY v1 REST API behind the
  // shared `GifSearchProvider` contract. Tenor v2 was sunset by
  // Google on 2026-06-30; KLIPY is the ex-Tenor team's drop-in
  // replacement. Falls back to the `emptyGifProvider` when
  // `VITE_KLIPY_API_KEY` is unset. Memoized once per session so
  // React state doesn't churn between renders.
  const gifProviderRef = useRef<GifSearchProvider | null>(null);
  if (gifProviderRef.current === null) {
    gifProviderRef.current = createKlipyGifProvider();
  }

  // Sticker adapter — KLIPY stickers endpoint, same auth + envelope
  // shape as the GIF provider.
  const stickerProviderRef = useRef<StickerSearchProvider | null>(null);
  if (stickerProviderRef.current === null) {
    stickerProviderRef.current = createKlipyStickerProvider();
  }

  // Reset transient state when the user picks a different conversation.
  useEffect(() => {
    setSendError(null);
    setInviteWarning(null);
  }, [conversationId]);

  // [no-QR recovery / PEER side] When this conversation opens — and again when
  // the app returns to the foreground while it's open — admit + history-seed any
  // peer device that recovered and advertised a history-delivery key. The
  // routine is idempotent, best-effort, and session-rate-limited (re-runs only
  // when the advertising-device set changed), so this is NOT a tight loop. It
  // never blocks rendering and swallows its own errors.
  useEffect(() => {
    if (client === null) return;
    let cancelled = false;
    const run = (): void => {
      if (cancelled) return;
      void ensureRecoveredPeerDevicesAdmittedForConversation(conversationId).catch(() => {
        /* best-effort — already logged inside */
      });
    };
    run(); // on open
    const onForeground = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      run();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onForeground);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onForeground);
    }
    return () => {
      cancelled = true;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onForeground);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onForeground);
      }
    };
  }, [client, conversationId]);

  // Read-receipt emitter. Fires `com.ping.read` for the latest peer
  // message in the chat whenever (a) the user opens this chat, or (b)
  // a fresh peer message arrives while the chat is in focus. The
  // sender's `useIncomingMessages.persistIncoming` will see the
  // `com.ping.read` envelope and patch the target row's `readBy`
  // array, flipping the MessageBubble's glyph from single-check
  // (delivered) to double-check (read).
  //
  // Dedupe via a ref: only re-emit when the latest peer contentHash
  // actually changes, so a same-chat re-render (typing in the
  // composer, scrolling) doesn't burn a network round-trip every
  // tick.
  const lastReadReceiptRef = useRef<{ convId: string; hash: string } | null>(null);
  useEffect(() => {
    if (client === null) return;
    if (messagesState.kind !== 'populated') return;
    // Find the most recent peer message (skip our own self bubbles
    // and any "failed"-state retry placeholders that don't represent
    // a real peer send).
    const items = messagesState.messages;
    let latestPeerHash: string | null = null;
    for (let i = items.length - 1; i >= 0; i--) {
      const m = items[i]!;
      if (m.isSelf) continue;
      if (!m.contentHash || m.contentHash.length === 0) continue;
      latestPeerHash = m.contentHash;
      break;
    }
    if (latestPeerHash === null) return;
    const prev = lastReadReceiptRef.current;
    if (prev !== null && prev.convId === conversationId && prev.hash === latestPeerHash) {
      return;
    }
    void (async () => {
      try {
        await sharedSendReadReceipt({
          client,
          conversationIdBytes: hexToBytesLocal(conversationId),
          contentHashHex: latestPeerHash,
        });
        lastReadReceiptRef.current = {
          convId: conversationId,
          hash: latestPeerHash,
        };
      } catch {
        // receipt failed even after retries — leave ref unchanged so the next
        // effect run retries this hash
      }
    })();
  }, [client, conversationId, messagesState]);

  // Cancel any in-flight recording when the component unmounts so
  // the microphone is released.
  useEffect(() => {
    return () => {
      recorderRef.current?.cancel();
    };
  }, []);

  const handleSend = useCallback(
    async (
      text: string,
      retryRowId?: string,
      linkPreview?: UnfurledLink,
    ): Promise<void> => {
      const trimmed = text.trim();
      if (trimmed.length === 0) return;
      notifyComposerSentRef.current();
      if (client === null) {
        setSendError('Session not ready. Try again in a moment.');
        return;
      }
      setSendError(null);
      try {
        // Resolve (or lazily mint) the MLS Conversation that backs
        // this BE conversationId.
        //
        // GROUPS: never reuse a cached proxy. After a send the SDK advances
        // the group epoch + `self.seq` and snapshots it to storage; a
        // cached JS proxy keeps the OLD in-memory seq, so the 2nd+ send
        // emits a stale-seq message the projection dedupes away ("only the
        // first message appears"). Re-resolving calls `client.getConversation`
        // which reloads seq from the cursor (the SDK load invariant), so each
        // group send is fresh. 1:1 keeps the fast cache (its proxy is stable).
        const cached = convProxyRef.current;
        let conv: SendableConversation;
        if (cached?.id === conversationId && !isGroup) {
          conv = cached.conv;
        } else {
          const transport = getCurrentTransport();
          const result = await getOrCreateMlsConversation({
            client: client as unknown as Parameters<typeof getOrCreateMlsConversation>[0]['client'],
            transport,
            beConversationId: conversationId,
            conversationName,
            log,
          });
          conv = result.conv;
          if (result.inviteStatus.kind === 'ok') {
            setInviteWarning(null);
          } else if (
            result.inviteStatus.kind === 'failed' &&
            result.inviteStatus.reason === 'peer_pool_empty'
          ) {
            setInviteWarning(
              'Waiting for the other person to come online. Your messages will reach them once they open Ping.',
            );
          } else if (
            result.inviteStatus.kind === 'failed' &&
            result.inviteStatus.reason === 'no_peer_devices'
          ) {
            setInviteWarning(
              "This person hasn't set up Ping yet. Your messages will reach them after they sign up.",
            );
          } else if (
            result.inviteStatus.kind === 'failed' &&
            result.inviteStatus.reason === 'no_transport'
          ) {
            // Transient: we couldn't reach the server to deliver the
            // invite. We deliberately did NOT advance the MLS epoch, so
            // the next send (once reconnected) re-runs the invite cleanly.
            setInviteWarning(
              "You appear to be offline. This chat will be set up as soon as you reconnect.",
            );
          } else if (result.inviteStatus.kind === 'failed') {
            setInviteWarning(
              "Couldn't invite the other person to this chat. Your messages won't reach them.",
            );
          }
        }
        convProxyRef.current = { id: conversationId, conv };

        // Capture + clear the reply target up-front so a slow send
        // doesn't leave the chip dangling on the next bubble.
        const replyTarget = replyTo;
        if (replyTarget !== null) setReplyTo(null);
        // Reuse the failed bubble's row id on retry so (a) the on-screen
        // position stays put and (b) the wire `event_id` matches — a
        // true re-POST then hits server send-idempotency instead of
        // landing as a second peer-visible message.
        const eventId = retryRowId ?? mintEventId();
        const payload: Record<string, unknown> = { body: trimmed };
        // Scan the trimmed body for @mentions of this conversation's
        // candidates and stamp the wire-shape mention array onto the
        // payload so receivers (readMentions) round-trip the pills. We
        // read the latest candidate set off the ref because the
        // `mentionCandidates` useMemo is declared after this callback.
        const mentions = serializeMentions(trimmed, mentionCandidatesRef.current);
        if (mentions.length > 0) {
          payload.mentions = mentions;
        }
        // Smart link preview rides INSIDE the text envelope so it renders
        // as one unified bubble (text + preview card) on both sender and
        // receiver — never a second standalone message.
        if (linkPreview !== undefined) {
          payload.link_preview = {
            url: linkPreview.url,
            title: linkPreview.title,
            ...(linkPreview.description !== undefined ? { desc: linkPreview.description } : {}),
            ...(linkPreview.imageUrl !== undefined ? { image_url: linkPreview.imageUrl } : {}),
          };
        }
        if (replyTarget !== null) {
          // Receivers that don't yet decode replyPreview ignore it; the
          // registry's `parseEnvelope` lifts `parent_id` onto the
          // `ParsedAppEvent.parent_id` field.
          payload.replyPreview = lastMessagePreview(replyTarget.body);
        }
        // `event_id` goes ON THE WIRE (not just Dexie). CR-6 hashes
        // plaintext for content_hash; without a unique id here, two
        // identical texts share a hash and message-service skips fanout
        // on the second send (treats it as a retry).
        const messageBytes = encodeEnvelopeJsonBytes({
          kind: 'com.ping.text',
          event_id: eventId,
          payload,
          ...(replyTarget !== null ? { parent_id: replyTarget.contentHash } : {}),
        });

        // ── Local-echo: write the bubble to Dexie BEFORE conv.send so
        // it shows up in the chat list within ~20ms (Dexie + liveQuery
        // fire) instead of waiting 200-700ms for the SDK worker +
        // network round-trip. The row's `id` is captured here and
        // patched in place after the SDK returns — the bubble stays
        // at the same screen position, only the status glyph swaps
        // from clock → single-check (or → retry on failure).
        // Mirror the reply parent onto the local-echo row so the
        // sender's own bubble shows the reply quote (peers rebuild it
        // from the envelope's `parent_id`).
        const replyInset =
          replyTarget !== null
            ? {
                messageId: replyTarget.contentHash,
                preview: lastMessagePreview(replyTarget.body) ?? '',
                senderName: replyTarget.isSelf ? 'You' : headerTitle,
              }
            : undefined;
        // Persist the FULL envelope JSON as the local-echo body when it
        // carries side-channel data the sender's own bubble needs to
        // render: a link-preview card, OR ranged @mentions (so a
        // multi-word display name like "@aik 11" renders as one pill
        // instead of being cut at the space). Plain text with neither
        // stays bare. Received messages already persist the envelope.
        const echoBody =
          linkPreview !== undefined || mentions.length > 0
            ? new TextDecoder().decode(messageBytes)
            : trimmed;
        const localId = await persistPendingMessage({
          conversationId,
          body: echoBody,
          // Align Dexie / outbox row id with wire event_id so retries
          // re-POST the same plaintext identity.
          retryRowId: eventId,
          ...(replyInset !== undefined ? { inReplyTo: replyInset } : {}),
        });

        // Write-ahead enqueue: stash the exact send bytes in the outbox BEFORE
        // conv.send, so a send made offline (or interrupted by a crash) is
        // replayed by the drainer on reconnect. Removed on confirmed send.
        if (localId !== null) {
          await enqueueOutbox({
            id: localId,
            conversationId,
            ...(conversationName !== undefined ? { conversationName } : {}),
            envelopeBytes: messageBytes,
            body: trimmed,
            createdAt: Date.now(),
            attempts: 0,
            nextAttemptAt: 0,
          });
        }

        let sentEnvelope: SentMessageEnvelope;
        try {
          // Announce our direct profile FIRST — BEFORE the message — so it gets a
          // LOWER server seq. The receiver processes its FIFO inbox in order, so
          // the device→account mapping lands before this message: a sibling
          // device that bakes direction at receive time (iOS) then renders this
          // (and every later) message as self, not as the peer. Also renames a
          // peer's "New chat" row that missed the creation-time intro. Once per
          // conversation per session; awaited (best-effort, never throws); no-op
          // for groups.
          await resendDirectProfileIntro(conv, conversationId);
          sentEnvelope = await conv.send(messageBytes);
        } catch (sendErr) {
          const sendMsg = (sendErr as Error).message ?? '';
          // FATAL errors (group gone / unrecoverable MLS state) must NOT be
          // retried — they'd loop forever and the outer catch needs to handle
          // the orphan/reload UX. Everything else (network/transport blips,
          // offline) is RETRYABLE: keep the bubble 'pending' (a clock, not an
          // error) and leave the outbox row in place so the drainer replays it
          // on reconnect / `online`.
          const fatal =
            (sendErr as Error).name === 'MlsStateUnrecoverableError' ||
            /not_found|not found|orphan/i.test(sendMsg);
          if (localId !== null && !fatal) {
            // Kick a drain now in case it was a one-off blip; otherwise the
            // reconnect/online triggers will flush it. No throw — the queued
            // 'pending' bubble already communicates state, and re-throwing
            // would surface a spurious error for a perfectly normal offline send.
            void drainOutbox();
            return;
          }
          if (localId !== null) {
            // Fatal: flip to 'failed' (manual retry affordance) + drop the
            // outbox row so the drainer doesn't keep hammering a dead group.
            await markMessageFailed({ id: localId });
            await dequeueOutbox(localId);
          } else {
            // No identity at persist time — legacy single-shot fallback.
            await persistFailedMessage({
              conversationId,
              body: trimmed,
              attemptId: retryRowId ?? null,
            });
          }
          throw sendErr;
        }

        // SDK send succeeded — drop the outbox row (dequeue BEFORE commit so a
        // crash in the gap leaves the bubble at worst 'pending', never a
        // peer-visible duplicate), then patch the existing row with the real
        // content hash / epoch / server seq. The bubble's id stays the same so
        // React reuses the same DOM node.
        if (localId !== null) {
          await dequeueOutbox(localId);
          await commitSentMessage({
            id: localId,
            conversationId,
            envelope: sentEnvelope,
            body: trimmed,
          });
        } else {
          // No identity at persist time (rare) — fall back to the
          // legacy single-shot insert so the bubble at least appears
          // post-send.
          await persistSentMessage({
            conversationId,
            envelope: sentEnvelope,
            body: trimmed,
            retryRowId: retryRowId ?? null,
          });
        }

        log.debug({
          event: 'chatroom.send.ok',
          conversationIdPrefix: conversationId.slice(0, 6),
          messageLen: trimmed.length,
        });
      } catch (err) {
        const message = (err as Error).message;
        // MlsStateUnrecoverableError fires when getOrCreateMlsConversation
        // detects the cached MLS group_id isn't in the SDK after init
        // rehydrate. Refusing to mint-fresh is the right call (mint-fresh
        // → 404 → orphan-prune destroys the chat). Surface a recoverable
        // toast and bail before the generic "send.failed" handler can
        // mark the conversation orphaned.
        if ((err as Error).name === 'MlsStateUnrecoverableError') {
          log.warn({
            event: 'chatroom.send.mlsStateUnrecoverable',
            conversationIdPrefix: conversationId.slice(0, 6),
          });
          setInviteWarning(
            "We couldn't load this chat from local storage. Reload the page; "
              + "if that doesn't help, start a new chat with this person.",
          );
          return;
        }
        log.error({
          event: 'chatroom.send.failed',
          err: message,
        });

        // The SDK wraps every transport error in
        // `"transport error: JsValue(Error: NetworkError: ...)"`. We
        // peek at the wrapped message to tell genuine network blips
        // apart from `not_found` — which means the conversation row
        // in the local chat list points at an MLS group the BE
        // no longer knows about (orphan). This is what an old
        // sign-in-after-StorageCorruption flow leaves behind; with
        // the wrap-key persistence fix in PingStorage.web.ts it
        // won't recur, but existing orphan rows still need user-
        // friendly handling.
        const isNotFound =
          message.includes('http 404') ||
          message.includes('"not_found"') ||
          message.includes(' not_found');
        if (isNotFound) {
          pingToast.error({
            title: "This chat isn't available",
            description:
              'It looks like local state got out of sync. Refreshing the page should fix it.',
          });
          setSendError(
            "This chat isn't available. Refresh the page and try again.",
          );
        } else {
          pingToast.error({
            title: "Couldn't send message",
            description: 'Check your connection and try again.',
          });
          setSendError("Couldn't send. Try again.");
        }
      }
    },
    [client, conversationId, conversationName, replyTo, isGroup, headerTitle],
  );

  // Retry handler exposed to the bubble. Same pipeline as `handleSend`;
  // passes the failed row's id so persistSentMessage upserts in-place.
  // Set below once `handleMediaRetry` is defined (it references send helpers
  // declared later). Lets the message-level retry (the bubble's error-row)
  // re-run a failed media UPLOAD instead of re-sending the envelope body as
  // text (which would never re-upload the bytes).
  const handleMediaRetryRef = useRef<(m: Message) => void>(() => undefined);
  const handleRetry = useCallback(
    (failedMessage: Message): void => {
      const body = failedMessage.body;
      if (body.length === 0) return;
      // A failed MEDIA message (image/video/file) needs a re-upload, not a
      // text re-send — route it to the media-retry path.
      if (
        isImageEnvelopeBody(body) ||
        parseVideoEnvelope(body) !== undefined ||
        parseFileEnvelope(body) !== undefined
      ) {
        handleMediaRetryRef.current(failedMessage);
        return;
      }
      void handleSend(body, failedMessage.id);
    },
    [handleSend],
  );

  // Lazily resolve the MLS Conversation handle for the active
  // conversation. Shares the same cache as `handleSend` so a voice
  // send right after a text send doesn't trigger a second
  // `getOrCreateMlsConversation` round-trip.
  const resolveConv = useCallback(async (): Promise<SendableConversation | null> => {
    if (client === null) return null;
    const cached = convProxyRef.current;
    // Groups always re-resolve (fresh seq from cursor) — see handleSend.
    if (cached?.id === conversationId && !isGroup) return cached.conv;
    const transport = getCurrentTransport();
    const result = await getOrCreateMlsConversation({
      client: client as unknown as Parameters<typeof getOrCreateMlsConversation>[0]['client'],
      transport,
      beConversationId: conversationId,
      conversationName,
      log,
    });
    convProxyRef.current = { id: conversationId, conv: result.conv };
    return result.conv;
  }, [client, conversationId, conversationName, isGroup]);

  // ── Backend-backed scheduled messages ───────────────────────────────────
  // Pending list surfaced in the composer's scheduled bar. Fetched from
  // `GET /v1/messages/scheduled` and paired with the local preview mirror
  // (the server returns opaque metadata only).
  const [scheduledPending, setScheduledPending] = useState<ScheduledMessage[]>(
    [],
  );
  // Re-list the pending queue AND reconcile deliveries. The server drops a row
  // once its worker delivers it, but nothing pushes that to the client — so a
  // delivered message would otherwise linger in the bar forever and never
  // appear in the SENDER's own chat (MLS can't self-decrypt; the plaintext
  // lives only in the local mirror). Here: any mirror row whose send_at has
  // passed and is no longer in the server list was delivered → self-echo it
  // into the timeline (idempotent) and forget the mirror row; then reflect the
  // still-pending list into the bar (which clears the delivered item).
  const refreshScheduled = useCallback(async (): Promise<void> => {
    const transport = getCurrentTransport();
    if (transport?.listScheduledMessages === undefined) {
      setScheduledPending([]);
      return;
    }
    const meta = await transport.listScheduledMessages(conversationId);
    const next = mergeScheduledMeta(meta, conversationId);
    const now = Date.now();
    const liveSendAts = new Set(next.map((n) => n.sendAtMs));
    for (const entry of listMirrorForConversation(conversationId)) {
      // Not due yet, or still queued server-side → leave it pending.
      if (entry.sendAtMs > now || liveSendAts.has(entry.sendAtMs)) continue;
      await persistDeliveredScheduledMessage({
        conversationId,
        body: entry.text,
        sentAtMs: entry.sendAtMs,
        ...(entry.contentHash !== undefined
          ? { contentHash: entry.contentHash }
          : {}),
      });
      forgetScheduled(conversationId, entry.sendAtMs);
      log.debug({
        event: 'chatroom.schedule.delivered',
        conversationIdPrefix: conversationId.slice(0, 6),
      });
    }
    setScheduledPending(next);
  }, [conversationId]);
  // Refresh on mount + whenever the active conversation changes.
  useEffect(() => {
    void refreshScheduled();
  }, [refreshScheduled]);
  // Delivery watcher: nothing pushes a "delivered" signal, so arm a timer for
  // just after the soonest pending send_at (plus a buffer for the gateway
  // worker's tick) and re-reconcile then. If the worker is slow the item is
  // still pending after the poll, `scheduledPending` updates, and this effect
  // re-arms — polling every buffer until it clears. Future items get a single
  // long timer (no busy loop).
  useEffect(() => {
    if (scheduledPending.length === 0) return undefined;
    const soonest = Math.min(...scheduledPending.map((m) => m.sendAtMs));
    const delay =
      Math.max(0, soonest - Date.now()) + SCHEDULED_DELIVERY_BUFFER_MS;
    const timer = setTimeout(() => {
      void refreshScheduled();
    }, delay);
    return () => clearTimeout(timer);
  }, [scheduledPending, refreshScheduled]);

  // Host override for the composer's "Schedule message" action. Pre-encrypts
  // the message NOW (MLS has no encrypt-without-send) and routes that one send
  // to the backend scheduled queue via `scheduleNextSend`, so it delivers even
  // after this device goes offline. NO local echo — it's queued, not sent.
  const handleScheduleMessage = useCallback(
    async (rawText: string, sendAtISO: string, replyTo?: string): Promise<void> => {
      const trimmed = rawText.trim();
      if (trimmed.length === 0 || client === null) return;
      const conv = await resolveConv();
      if (conv === null) throw new Error('conversation not ready');
      const transport = getCurrentTransport();
      if (transport?.scheduleNextSend === undefined) {
        throw new Error('scheduled send unsupported by this transport');
      }
      const payload: Record<string, unknown> = { body: trimmed };
      const mentions = serializeMentions(trimmed, mentionCandidatesRef.current);
      if (mentions.length > 0) payload.mentions = mentions;
      const messageBytes = encodeEnvelopeJsonBytes({
        kind: 'com.ping.text',
        event_id: mintEventId(),
        payload,
        ...(replyTo !== undefined && replyTo.length > 0
          ? { parent_id: replyTo }
          : {}),
      });
      // Arm the one-shot route IMMEDIATELY before the send (after conv
      // resolution, whose own Commit/Welcome sends never consume the flag —
      // it's Application-only).
      transport.scheduleNextSend(sendAtISO);
      // `conv.send` still returns the real SentMessageEnvelope (content hash is
      // computed from plaintext, independent of the scheduled routing) — capture
      // its content hash so the later delivery self-echo carries the SAME hash
      // the recipient will compute, letting read/delivery receipts match.
      const scheduledEnvelope = await conv.send(messageBytes);
      const sendAtMs = Date.parse(sendAtISO);
      if (!Number.isNaN(sendAtMs)) {
        rememberScheduled(
          conversationId,
          sendAtMs,
          trimmed,
          replyTo,
          bytesToHexLocal(scheduledEnvelope.content_hash),
        );
      }
      await refreshScheduled();
      log.debug({
        event: 'chatroom.schedule.ok',
        conversationIdPrefix: conversationId.slice(0, 6),
      });
    },
    [client, conversationId, resolveConv, refreshScheduled],
  );

  // Live snapshot so `handleCancelScheduled` can resolve a canceled id → its
  // send instant (to forget the mirror row) without re-creating on every list
  // change.
  const scheduledPendingRef = useRef<ScheduledMessage[]>(scheduledPending);
  scheduledPendingRef.current = scheduledPending;
  const handleCancelScheduled = useCallback(
    (id: string): void => {
      // Forget the local preview mirror row up-front so the delivery
      // reconciler never mistakes a user-cancel for a delivery and self-echoes
      // it into the timeline.
      const item = scheduledPendingRef.current.find((m) => m.id === id);
      if (item !== undefined) forgetScheduled(conversationId, item.sendAtMs);
      const transport = getCurrentTransport();
      void (async () => {
        try {
          await transport?.cancelScheduledMessage?.(id);
        } catch (err) {
          log.warn({
            event: 'chatroom.schedule.cancel.failed',
            err: (err as Error).message,
          });
        } finally {
          void refreshScheduled();
        }
      })();
    },
    [conversationId, refreshScheduled],
  );

  // Forward flow — the source message being forwarded (null = picker
  // closed). Picking a target conversation re-sends the message there.
  const [forwardSource, setForwardSource] = useState<Message | null>(null);
  const handleForwardPick = useCallback(
    (targetConversationId: string): void => {
      const source = forwardSource;
      setForwardSource(null);
      if (source === null || client === null) return;
      const target = allConversations.find((conv) => conv.id === targetConversationId);
      void (async () => {
        try {
          const transport = getCurrentTransport();
          const result = await getOrCreateMlsConversation({
            client: client as unknown as Parameters<typeof getOrCreateMlsConversation>[0]['client'],
            transport,
            beConversationId: targetConversationId,
            conversationName: target?.name,
            log,
          });
          // Forward as a first-class `com.ping.forward` envelope wrapping the
          // source content, so BOTH the forwarder's own echo and every
          // recipient's copy render the inner content PLUS a "Forwarded" label
          // (the messages adapter unwraps it). Text is carried verbatim; rich
          // content falls back to its preview text for now (per-target media
          // re-encryption is a follow-up).
          const parsed = parseEnvelope(source.body);
          let innerPayload: Record<string, unknown>;
          if (parsed && parsed.kind === 'com.ping.text') {
            innerPayload = parsed.body as Record<string, unknown>;
          } else {
            const preview = lastMessagePreview(source.body) ?? '';
            if (preview.length === 0) return;
            innerPayload = { body: preview };
          }
          const forwardEnvelope = {
            kind: 'com.ping.forward' as const,
            payload: {
              source_event_id:
                source.contentHash.length > 0 ? source.contentHash : source.id,
              source_conversation_id: conversationId,
              content_type: appEventTypeForKind('com.ping.text') ?? 0x11,
              content_body: innerPayload,
              ...(source.senderId.length > 0
                ? { original_sender: source.senderId }
                : {}),
            },
          };
          const sent = (await result.conv.send(
            encodeEnvelopeJsonBytes(forwardEnvelope),
          )) as { content_hash: Uint8Array; epoch: number | bigint };
          // Persist a local echo into the TARGET conversation so the FORWARDER
          // sees their own forwarded message — self-sends don't echo back via
          // onMessage, so without this the forwarder never saw the forward land.
          const identity = await retrieveIdentity();
          if (identity !== null) {
            const sentAt = Date.now();
            await sharedPersistSentMessage({
              messages: webMessagesPersister,
              conversations: webConversationsPersister,
              record: {
                id:
                  typeof crypto !== 'undefined' &&
                  typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID()
                    : `local-forward-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
                conversationId: targetConversationId,
                epoch:
                  typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch,
                serverSeq: sentAt,
                contentHash: bytesToHexLocal(sent.content_hash),
                senderAccountId: identity.accountId,
                body: JSON.stringify(forwardEnvelope),
                sentAt,
                deliveryState: 'sent',
              },
            });
          }
        } catch (err) {
          log.warn({ event: 'chatroom.forward.failed', err: (err as Error).message });
        }
      })();
    },
    [forwardSource, client, allConversations, conversationId],
  );
  const forwardTargets = useMemo(
    () =>
      allConversations
        .filter((conv) => conv.id !== conversationId)
        .map((conv) => ({ id: conv.id, name: conv.name ?? 'Chat' })),
    [allConversations, conversationId],
  );

  // Emit a game move: send `com.ping.game_move` to the peer + optimistically
  // fold it locally (self-sends don't echo via onMessage). `moveIdx` is the
  // player's seat (0 = creator, 1 = opponent); the tile owns the payload.
  const handleGameMove = useCallback(
    (sessionId: string, moveIdx: number, payload: string): void => {
      void (async () => {
        try {
          const conv = await resolveConv();
          if (conv === null) return;
          const body = makeMove(sessionId, { moveIdx, payload });
          const envelopeBody = { kind: 'com.ping.game_move' as const, payload: { ...body } };
          const sent = (await conv.send(encodeEnvelopeJsonBytes(envelopeBody))) as {
            content_hash: Uint8Array;
            epoch: number | bigint;
          };
          // Optimistic instant fold for snappy UI; the authoritative
          // state is re-derived from persisted messages below.
          applyGameEvent({ type: 'move', sessionId, moveIdx, payload, by: '__self__' });
          // Persist a LOCAL echo of OUR OWN move so the projection can be
          // rebuilt after a reload (self-sends don't echo via onMessage,
          // so without this the move — and thus the winner — was lost).
          const identity = await retrieveIdentity();
          if (identity) {
            const sentAt = Date.now();
            await sharedPersistSentMessage({
              messages: webMessagesPersister,
              conversations: webConversationsPersister,
              record: {
                id:
                  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID()
                    : `local-gamemove-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
                conversationId,
                epoch: typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch,
                serverSeq: sentAt,
                contentHash: bytesToHexLocal(sent.content_hash),
                senderAccountId: identity.accountId,
                body: JSON.stringify(envelopeBody),
                sentAt,
                deliveryState: 'sent',
              },
            });
          }
        } catch (err) {
          log.warn({ event: 'chatroom.game.move.failed', err: (err as Error).message });
        }
      })();
    },
    [resolveConv, makeMove, applyGameEvent, conversationId],
  );

  // ---------------------------------------------------------------
  // Hover-strip, reactions, and reply wiring (Figma 2804:27483).
  // Threads three host-supplied callbacks through to the
  // MessageBubble so the React / Reply / More chip appears on
  // pointer hover. Without these the bubble's `hasHoverActions`
  // gate stays false and the strip stays hidden.
  // ---------------------------------------------------------------

  /** Resolved local accountId — needed so peer reactions can be
   *  attributed to "not me". Loaded once on mount; identity is
   *  stable for the lifetime of the session. */
  const localAccountIdRef = useRef<string | null>(null);
  useEffect(() => {
    let alive = true;
    void retrieveIdentity().then((id) => {
      if (alive && id !== null) {
        localAccountIdRef.current = id.accountId;
        setLocalAccountId(id.accountId);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  /**
   * Toggle a reaction locally + emit the canonical
   * `com.ping.reaction` / `com.ping.reaction_remove` envelope on
   * the wire. Local state flips first so the pill updates instantly;
   * the wire send is fire-and-forget.
   */
  const handleToggleReactionForMessage = useCallback(
    (messageId: string, emoji: string): void => {
      const me = localAccountIdRef.current ?? '__local__';
      // Resolve the target's contentHash — reactions are anchored on
      // the content hash in the OR-set (so an early-arriving reaction
      // still lands in the right bucket once its target's row resolves).
      const targetRow =
        messagesState.kind === 'populated'
          ? messagesState.messages.find((m) => m.id === messageId)
          : undefined;
      const targetHash = targetRow?.contentHash ?? messageId;
      // Determine the current self-state from the live projection so
      // the toggle direction matches what's on screen.
      const current = reactionsByMessageId[messageId] ?? [];
      const reactedByMeNow = current.find((r) => r.emoji === emoji)?.reactedByMe === true;
      const nextReactedByMe = !reactedByMeNow;

      // Optimistic local persist for the SELF actor. The Dexie
      // liveQuery the persister wraps re-projects `useReactions` so the
      // pill updates without a wire round-trip.
      void (async () => {
        try {
          if (nextReactedByMe) {
            await webReactionsPersister.put({
              id: `${targetHash}:${emoji}:${me}`,
              conversationId,
              targetContentHash: targetHash,
              ...(targetRow !== undefined ? { targetMessageId: messageId } : {}),
              emoji,
              actorAccountId: me,
              addedAt: Date.now(),
            });
          } else {
            await webReactionsPersister.deleteByKey(conversationId, targetHash, emoji, me);
          }
        } catch (err) {
          log.warn({
            event: 'chatroom.reaction.persist.failed',
            err: (err as Error).message,
          });
        }
      })();

      // Fire-and-forget wire send.
      void (async () => {
        try {
          const conv = await resolveConv();
          if (conv === null) return;
          // Reactions target the receiver-known event id. The
          // current schema uses `contentHash` as the cross-device
          // stable handle for a message; reaction targets ride on
          // that field until a proper `event_id` lands.
          const envelope = encodeEnvelopeJsonBytes({
            kind: nextReactedByMe
              ? 'com.ping.reaction'
              : 'com.ping.reaction_remove',
            payload: { target: targetHash, emoji, actorAccountId: me },
          });
          await conv.send(envelope);
        } catch (err) {
          log.warn({
            event: 'chatroom.reaction.send.failed',
            err: (err as Error).message,
          });
        }
      })();
    },
    [resolveConv, messagesState, reactionsByMessageId, conversationId],
  );

  /** Pop the default "thumbs-up" reaction when the user taps the
   *  hover-strip React chip. A full emoji popover lives in a
   *  follow-up — for now the single-tap reaction matches every
   *  shipping chat app's quick-react default. */
  const handleReactPress = useCallback(
    (message: Message): void => {
      handleToggleReactionForMessage(message.id, '👍');
    },
    [handleToggleReactionForMessage],
  );

  /** Set the reply target — the composer chip + the next send
   *  picks it up via `replyTo`. */
  const handleReplyPress = useCallback((message: Message): void => {
    setReplyTo(message);
  }, []);

  // Edit: enter composer edit mode for this (own text) message. Clearing reply
  // mode first so the two chips never stack.
  const handleEditPress = useCallback((message: Message): void => {
    setReplyTo(null);
    setEditingMessage(message);
  }, []);
  const handleCancelEdit = useCallback((): void => {
    setEditingMessage(null);
  }, []);

  // Submit an edit — send a `com.ping.edit` envelope (payload.body = new text)
  // to peers, then apply it optimistically to the author's own row (self-sends
  // don't echo back via onMessage, mirroring the redact flow). The receiver-side
  // `com.ping.edit` handler in `useIncomingMessages` updates the peers' rows.
  const handleSubmitEdit = useCallback(
    async (newText: string): Promise<void> => {
      const target = editingMessage;
      if (target === null) return;
      const conv = await resolveConv();
      if (conv === null) throw new Error('Not connected — try again in a moment.');
      await conv.send(
        encodeEnvelopeJsonBytes({
          kind: 'com.ping.edit',
          payload: { target: target.contentHash, body: newText },
        }),
      );
      // Optimistic local update: rewrite the body + stamp editedAt on the
      // author's own row (by content hash, falling back to the row id).
      try {
        const row =
          target.contentHash.length > 0
            ? await webMessagesPersister.getByContentHash(target.contentHash)
            : undefined;
        await webMessagesPersister.update(row?.id ?? target.id, {
          body: newText,
          editedAt: Date.now(),
        });
      } catch (err) {
        log.warn({ event: 'chatroom.edit.localUpdateFailed', err: (err as Error).message });
      }
    },
    [editingMessage, resolveConv],
  );

  /** Action-menu dispatch — driven by `MessageActionMenu`'s row taps.
   *  Handles every entry in the Figma menu: Copy, Reply in Thread,
   *  Forward, Pin/Unpin, Save/Unsave, Star/Unstar, Remove. Pin/Save/Star
   *  toggle against the committed local set so the label + the outgoing
   *  envelope agree on state. */
  const handleMenuAction = useCallback(
    (
      action: 'copy' | 'replyInThread' | 'forward' | 'pin' | 'save' | 'star' | 'remove',
      message: Message,
    ): void => {
      switch (action) {
        case 'copy': {
          const body = message.body;
          try {
            const parsed = parseEnvelope(body);
            const text =
              parsed && parsed.kind === 'com.ping.text'
                ? (parsed.body as { body: string }).body
                : body;
            if (typeof navigator !== 'undefined' && navigator.clipboard) {
              void navigator.clipboard.writeText(text);
            }
            pingToast.success({ title: 'Message copied' });
          } catch (err) {
            log.debug({
              event: 'chatroom.menu.copy_failed',
              err: (err as Error).message,
            });
          }
          return;
        }
        case 'replyInThread': {
          // Open the dedicated thread panel anchored on this message's
          // contentHash (the thread root). The panel's reply composer
          // sends `com.ping.text` with `parent_id` = this hash so
          // replies nest under the root (bug 4).
          const rootHash = message.contentHash;
          if (rootHash.length > 0 && shellHandlers?.openThread) {
            shellHandlers.openThread(rootHash as ThreadIdentifier);
          } else {
            // No content hash yet (still pending) — fall back to the
            // inline reply chip so the affordance isn't a dead end.
            setReplyTo(message);
          }
          return;
        }
        case 'forward': {
          // Open the conversation picker; the actual re-send happens in
          // handleForwardPick once a target is chosen.
          setForwardSource(message);
          return;
        }
        case 'pin': {
          // Toggle the pinned-set + emit `com.ping.pin`/`com.ping.unpin`; the
          // local pinned-set projection + the pinned bar take it from there.
          // Read the committed set synchronously (loadPinnedTargets mirrors the
          // localStorage the effect keeps in sync) so the label — Pin vs Unpin —
          // and the outgoing envelope agree on the current state.
          const target = message.contentHash;
          if (target.length === 0) return;
          const willPin = !loadPinnedTargets(conversationId).has(target);
          // Optimistic local toggle (self-sends don't echo via onMessage).
          setPinnedTargets((prev) => {
            const next = new Set(prev);
            if (willPin) next.add(target);
            else next.delete(target);
            return next;
          });
          void (async () => {
            try {
              const [identity, conv] = await Promise.all([retrieveIdentity(), resolveConv()]);
              if (conv === null) return;
              const envelope = encodeEnvelopeJsonBytes({
                kind: willPin ? 'com.ping.pin' : 'com.ping.unpin',
                payload: {
                  target,
                  ...(identity !== null ? { actorDisplayName: identity.displayName } : {}),
                },
              });
              await conv.send(envelope);
              pingToast.success({ title: willPin ? 'Message pinned' : 'Message unpinned' });
            } catch (err) {
              log.warn({
                event: 'chatroom.menu.pin.failed',
                err: (err as Error).message,
              });
              pingToast.error({
                title: willPin ? "Couldn't pin" : "Couldn't unpin",
                description: 'Try again in a moment.',
              });
            }
          })();
          return;
        }
        case 'save': {
          // Toggle the local saved-set + emit `com.ping.save`/`com.ping.unsave`
          // (mirrors pin) so the user's other devices converge.
          const target = message.contentHash;
          if (target.length === 0) return;
          // Read the committed set synchronously (the effect keeps localStorage
          // in sync) so this callback needn't depend on `savedTargets` — which
          // is declared later in the body (TDZ in the dep array otherwise).
          const willSave = !loadSavedTargets(conversationId).has(target);
          setSavedTargets((prev) => {
            const next = new Set(prev);
            if (willSave) next.add(target);
            else next.delete(target);
            return next;
          });
          void (async () => {
            try {
              const conv = await resolveConv();
              if (conv === null) return;
              await conv.send(
                encodeEnvelopeJsonBytes({
                  kind: willSave ? 'com.ping.save' : 'com.ping.unsave',
                  payload: { target },
                }),
              );
              pingToast.success({ title: willSave ? 'Message saved' : 'Removed from saved' });
            } catch (err) {
              log.warn({ event: 'chatroom.menu.save.failed', err: (err as Error).message });
            }
          })();
          return;
        }
        case 'star': {
          // Star/unstar (§2.7 0x64) — DeviceGroup-synced. Optimistic local write
          // (authoritative) + best-effort broadcast to the user's other devices.
          const target = message.contentHash;
          if (target.length === 0) return;
          const willStar = !starredTargetsRef.current.has(target);
          void webStarredPersister.set(target, willStar);
          void sendStar(client, target, willStar).catch(() => undefined);
          pingToast.success({ title: willStar ? 'Message starred' : 'Removed from starred' });
          return;
        }
        case 'remove': {
          // Delete scoping:
          //  - MY OWN message → redact for EVERYONE (com.ping.redact) so all
          //    members collapse the row to "Message deleted", then local echo.
          //  - SOMEONE ELSE'S message → delete-for-ME only: remove the row from
          //    THIS client's IndexedDB and record a local tombstone so a resume/
          //    replay can't resurrect it. NEVER broadcast a redact for a peer's
          //    message — that would wrongly delete it for the author + everyone.
          const isOwn = message.isSelf === true;
          void (async () => {
            // Local removal (shared by both branches). Delete by content hash,
            // falling back to the row id for still-pending messages with no hash.
            const removeLocal = async (): Promise<void> => {
              try {
                if (message.contentHash.length > 0) {
                  // Tombstone first so an in-flight replay that re-inserts before
                  // our delete lands is still hidden on the next pass.
                  await addLocallyRemovedMessage(conversationId, message.contentHash);
                }
                const target =
                  message.contentHash.length > 0
                    ? await webMessagesPersister.getByContentHash(message.contentHash)
                    : undefined;
                await webMessagesPersister.delete(target?.id ?? message.id);
              } catch (delErr) {
                log.warn({
                  event: 'chatroom.menu.remove.localDeleteFailed',
                  err: (delErr as Error).message,
                });
              }
            };
            try {
              if (!isOwn) {
                // Delete-for-me only — no network broadcast.
                await removeLocal();
                log.info({ event: 'chatroom.menu.remove.localOnly' });
                return;
              }
              // Own message → redact for everyone, then local echo (self-sends
              // don't echo back via onMessage).
              const conv = await resolveConv();
              if (conv === null) return;
              const envelope = encodeEnvelopeJsonBytes({
                kind: 'com.ping.redact',
                payload: { target: message.contentHash, reason: 'user' },
              });
              await conv.send(envelope);
              await removeLocal();
            } catch (err) {
              log.warn({
                event: 'chatroom.menu.remove.failed',
                err: (err as Error).message,
              });
              pingToast.error({
                title: "Couldn't remove",
                description: 'Try again in a moment.',
              });
            }
          })();
          return;
        }
      }
    },
    [resolveConv, shellHandlers, conversationId, client],
  );

  const handleCancelReply = useCallback((): void => {
    setReplyTo(null);
  }, []);

  // Receiver-side reaction projection now lives in the shared
  // `useIncomingMessages` hook (it persists `com.ping.reaction` /
  // `com.ping.reaction_remove` envelopes into the same
  // `webReactionsPersister` OR-set this host reads through
  // `useReactions`). No chat-room-scope `client.onMessage` listener is
  // needed any more — the live Dexie subscription keeps the pills in
  // sync for both self toggles and peer arrivals.

  // Peer-game projection. Reactions migrated to the Dexie OR-set, but
  // game sessions are an in-memory projection (`useGameSessions`) with
  // no persistence layer — so we still need a chat-room-scope
  // `client.onMessage` tap to fold PEER game envelopes into local
  // state. We decode each incoming plaintext via `parseEnvelope`,
  // filter to this conversation + the `com.ping.game_*` kinds, dedupe
  // by content_hash (the same guard the old reaction listener used so a
  // BE re-delivery doesn't double-apply), and map to a `GameEvent` —
  // attributing `by` / `createdBy` from the sender device when the
  // body itself doesn't carry it.
  const appliedGameHashesRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    appliedGameHashesRef.current = new Set();
  }, [conversationId]);
  useEffect(() => {
    if (client === null) return undefined;
    const c = client as unknown as {
      onMessage?(handler: (msg: unknown) => void): () => void;
    };
    if (typeof c.onMessage !== 'function') return undefined;

    const unsubscribe = c.onMessage((raw: unknown) => {
      if (raw === null || typeof raw !== 'object') return;
      const m = raw as {
        conversation_id?: Uint8Array | number[];
        sender_device?: Uint8Array | number[];
        sender_user_id?: Uint8Array | number[];
        plaintext?: Uint8Array | number[];
        content_hash?: Uint8Array | number[];
      };
      if (m.plaintext === undefined || m.conversation_id === undefined) return;

      // Scope to the active conversation.
      const convHex = bytesLikeToHex(m.conversation_id);
      if (convHex !== conversationId) return;

      const parsed = parseEnvelope(toUint8(m.plaintext));
      if (parsed === undefined) return;

      // Pinned-set projection — fold com.ping.pin / unpin into a local
      // Set keyed by the target message's content hash (drives PinnedBar).
      if (parsed.kind === 'com.ping.pin' || parsed.kind === 'com.ping.unpin') {
        const target =
          'body' in parsed
            ? (parsed.body as { target?: unknown }).target
            : undefined;
        if (typeof target === 'string' && target.length > 0) {
          const pin = parsed.kind === 'com.ping.pin';
          setPinnedTargets((prev) => {
            const next = new Set(prev);
            if (pin) next.add(target);
            else next.delete(target);
            return next;
          });
        }
        return;
      }

      // Saved-set projection — saving is PERSONAL (unlike pins, which are shared
      // by the whole group), so fold com.ping.save / unsave ONLY when the sender
      // is one of MY OWN linked devices (they share my account MLS user id). A
      // peer's save must never mark a message saved in my list. Without the
      // shared-identity `sender_user_id` we conservatively skip (never
      // mis-attribute a peer's save as mine).
      if (parsed.kind === 'com.ping.save' || parsed.kind === 'com.ping.unsave') {
        const senderUserHex =
          m.sender_user_id !== undefined ? bytesLikeToHex(m.sender_user_id) : '';
        if (senderUserHex.length === 0 || !isSelfMlsUserIdHex(senderUserHex)) return;
        const target =
          'body' in parsed
            ? (parsed.body as { target?: unknown }).target
            : undefined;
        if (typeof target === 'string' && target.length > 0) {
          const save = parsed.kind === 'com.ping.save';
          setSavedTargets((prev) => {
            const next = new Set(prev);
            if (save) next.add(target);
            else next.delete(target);
            return next;
          });
        }
        return;
      }

      if (!isGameKind(parsed.kind)) return;
      if (!('body' in parsed)) return;

      // Dedupe by content_hash so a re-delivery doesn't re-fold the
      // same event into the projection.
      const hashHex =
        m.content_hash !== undefined ? bytesLikeToHex(m.content_hash) : '';
      if (hashHex.length > 0) {
        if (appliedGameHashesRef.current.has(hashHex)) return;
        appliedGameHashesRef.current.add(hashHex);
      }

      const senderHex =
        m.sender_device !== undefined ? bytesLikeToHex(m.sender_device) : '';
      const evt = gameEventFromParsed(parsed.kind, parsed.body, senderHex);
      if (evt !== null) applyGameEvent(evt);
    });

    return () => {
      try {
        unsubscribe();
      } catch (err) {
        log.warn({
          event: 'chatroom.game.unsubscribe.failed',
          err: (err as Error).message,
        });
      }
    };
  }, [client, conversationId, applyGameEvent]);

  const handleStartVoice = useCallback((): void => {
    const recorder = recorderRef.current;
    if (recorder === null) return;
    void recorder.start().catch((err) => {
      log.error({
        event: 'chatroom.voice.start.failed',
        err: (err as Error).message,
      });
      setSendError('Microphone unavailable. Check browser permissions.');
    });
  }, []);

  const handleSendVoice = useCallback(
    async (durationMs: number): Promise<void> => {
      const recorder = recorderRef.current;
      if (recorder === null) return;
      try {
        const blob = await recorder.stop();
        if (blob.size === 0) {
          log.warn({ event: 'chatroom.voice.empty_blob' });
          return;
        }
        // Read the record-time transcript captured alongside the audio.
        const transcript = recorder.currentTranscript;
        const conv = await resolveConv();
        if (conv === null) {
          setSendError('Session not ready. Try again in a moment.');
          return;
        }
        await sendVoiceMessage({
          conv,
          conversationId,
          blob,
          durationMs,
          ...(transcript.length > 0 ? { transcript } : {}),
        });
      } catch (err) {
        // Size cap exceeded — surface a clear actionable toast and
        // skip the generic "try again" banner (retry won't help; the
        // user needs to record something shorter).
        if (err instanceof VoiceNoteTooLargeError) {
          log.warn({
            event: 'chatroom.voice.too_large',
            rawBytes: err.rawBytes,
            limitBytes: err.limitBytes,
          });
          pingToast.error({
            title: 'Voice note too long',
            description:
              'Record a shorter clip (about 1 minute or less). Longer notes will ride a blob upload in a follow-up.',
          });
          return;
        }
        // Server-side payload-too-large mapped to a friendly toast
        // instead of the raw "transport error: 413" string.
        const message = (err as Error).message;
        const isTooLarge =
          message.includes('413') ||
          message.toLowerCase().includes('payload too large') ||
          message.toLowerCase().includes('payload_too_large');
        log.error({
          event: 'chatroom.voice.send.failed',
          err: message,
          isTooLarge,
        });
        if (isTooLarge) {
          pingToast.error({
            title: 'Voice note too long',
            description: 'Record a shorter clip and try again.',
          });
        } else {
          pingToast.error({
            title: "Couldn't send voice message",
            description: 'Check your connection and try again.',
          });
          setSendError("Couldn't send voice message. Try again.");
        }
      }
    },
    [conversationId, resolveConv],
  );

  const handleCancelVoice = useCallback((): void => {
    const recorder = recorderRef.current;
    if (recorder === null) return;
    recorder.cancel();
  }, []);

  // Pipe the recorder's live RMS amplitude into the composer's
  // waveform. We forward subscription registration straight through —
  // the recorder retains the listener set internally so the composer
  // can subscribe before `onStartVoice` runs without missing the
  // first samples.
  const subscribeAmplitudes = useCallback((listener: (amplitude: number) => void): (() => void) => {
    const recorder = recorderRef.current;
    if (recorder === null) return () => {};
    return recorder.onAmplitude(listener);
  }, []);

  const handleSendGifOrSticker = useCallback(
    async (gif: GifItem, isSticker: boolean): Promise<void> => {
      try {
        const conv = await resolveConv();
        if (conv === null) {
          setSendError('Session not ready. Try again in a moment.');
          return;
        }
        await sendGifMessage({
          conv,
          conversationId,
          gif,
          ...(isSticker ? { isSticker: true } : {}),
        });
      } catch (err) {
        log.error({
          event: isSticker ? 'chatroom.sticker.send.failed' : 'chatroom.gif.send.failed',
          err: (err as Error).message,
        });
        setSendError(isSticker ? "Couldn't send sticker. Try again." : "Couldn't send GIF. Try again.");
      }
    },
    [conversationId, resolveConv],
  );
  const handleSendGif = useCallback(
    (gif: GifItem): Promise<void> => handleSendGifOrSticker(gif, false),
    [handleSendGifOrSticker],
  );
  const handleSendSticker = useCallback(
    (gif: GifItem): Promise<void> => handleSendGifOrSticker(gif, true),
    [handleSendGifOrSticker],
  );

  const [imagePreviewByMessageId, setImagePreviewByMessageId] = useState<Record<string, string>>(
    () => ({}),
  );
  // Received images whose fetch+decrypt failed (expired presigned GET, decrypt
  // error, network blip). Drives the tile's `state: 'failed'` + Retry overlay
  // instead of an infinite shimmer. Cleared on a successful (re)resolve.
  const [failedImageIds, setFailedImageIds] = useState<ReadonlySet<string>>(() => new Set());
  // Source files for a FAILED-UPLOAD retry, keyed by the local-echo row id.
  // Populated in the echo phase (the original `File` isn't persisted on the
  // row), so a `deliveryState:'failed'` media tile can re-run the encrypt +
  // upload + send in-session. In-memory only — a reload clears it (the tile
  // then falls back to a re-attach hint on retry).
  const mediaRetrySourceRef = useRef<
    Map<
      string,
      {
        readonly kind: 'image' | 'file' | 'video';
        readonly file: File;
        readonly caption?: string | undefined;
        readonly posterUri?: string | undefined;
        readonly probed?: ProbedVideo | undefined;
      }
    >
  >(new Map());
  // Decrypted/object URLs for video tiles. `posterUriByMessageId` is
  // resolved eagerly (small thumbnail); `videoUriByMessageId` lazily on
  // first play (the full blob is large). Sender-side echoes seed both
  // immediately from the local file.
  const [videoUriByMessageId, setVideoUriByMessageId] = useState<Record<string, string>>(
    () => ({}),
  );
  const [posterUriByMessageId, setPosterUriByMessageId] = useState<Record<string, string>>(
    () => ({}),
  );
  // 0..1 download progress for a received video being lazily fetched on
  // play; drives the spinner overlay. Absent once resolved (videoUri set).
  const [videoDownloadProgressById, setVideoDownloadProgressById] = useState<
    Record<string, number>
  >(() => ({}));
  // Tracks which received videos have begun (or finished) a lazy blob
  // fetch so a tap doesn't kick off duplicate downloads.
  const videoFetchStartedRef = useRef<Set<string>>(new Set());

  // Staged-attachment tray (Design-system 4058:6355). Picked / pasted /
  // dropped images queue here as removable tiles above the composer and
  // send together when the user submits. `stagedImagesRef` mirrors the
  // state so the send wrapper reads the latest set without re-creating
  // its callback on every stage.
  const [stagedImages, setStagedImages] = useState<
    ReadonlyArray<{
      readonly id: string;
      readonly file: File;
      readonly previewUri: string;
      readonly caption?: string | undefined;
    }>
  >([]);
  const stagedImagesRef = useRef(stagedImages);
  useEffect(() => {
    stagedImagesRef.current = stagedImages;
  }, [stagedImages]);

  // Parallel staging tray for NON-image files (documents, archives,
  // etc.) picked via "+ → Add files" or dropped onto the chat panel.
  // Rendered as file tiles in `stagedTrayItems`; flushed via
  // `sendFileMessage` on submit.
  const [stagedFiles, setStagedFiles] = useState<
    ReadonlyArray<{
      readonly id: string;
      readonly file: File;
      readonly caption?: string | undefined;
      /** Rendered page thumbnail (PDF first page / text page) when available. */
      readonly previewUri?: string | undefined;
    }>
  >([]);
  const stagedFilesRef = useRef(stagedFiles);
  useEffect(() => {
    stagedFilesRef.current = stagedFiles;
  }, [stagedFiles]);
  // Object URLs of staged-doc page thumbnails — revoked on unmount.
  const stagedFileThumbUrlsRef = useRef<string[]>([]);
  useEffect(() => {
    return () => {
      for (const url of stagedFileThumbUrlsRef.current) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* already revoked */
        }
      }
      stagedFileThumbUrlsRef.current = [];
    };
  }, []);

  const stageFiles = useCallback((files: ReadonlyArray<File>): void => {
    // Documents only — images route through `stageImageFiles`, videos
    // through `stageVideoFiles`.
    const docFiles = files.filter(
      (f) => !f.type.startsWith('image/') && !f.type.startsWith('video/'),
    );
    if (docFiles.length === 0) return;
    const accepted: Array<{ id: string; file: File }> = [];
    setStagedFiles((prev) => {
      const room = 10 - prev.length;
      if (room <= 0) {
        pingToast.error({
          title: 'Attachment limit reached',
          description: 'You can attach up to 10 files at once.',
        });
        return prev;
      }
      const next = docFiles.slice(0, room).map((file) => {
        const id =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `staged-file-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        accepted.push({ id, file });
        return { id, file };
      });
      return [...prev, ...next];
    });
    // Render a page thumbnail off the render path (PDF first page / text
    // page) and patch it in when ready — mirrors the video poster probe.
    for (const { id, file } of accepted) {
      if (!canPreviewDoc(file.type, file.name)) continue;
      void renderDocThumbnail(file, file.type, file.name)
        .then((previewUri) => {
          if (previewUri === undefined) return;
          stagedFileThumbUrlsRef.current.push(previewUri);
          setStagedFiles((prev) =>
            prev.map((s) => (s.id === id ? { ...s, previewUri } : s)),
          );
        })
        .catch(() => undefined);
    }
  }, []);

  const stageImageFiles = useCallback((files: ReadonlyArray<File>): void => {
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    setStagedImages((prev) => {
      const room = 10 - prev.length;
      if (room <= 0) {
        pingToast.error({
          title: 'Attachment limit reached',
          description: 'You can attach up to 10 images at once.',
        });
        return prev;
      }
      const next = imageFiles.slice(0, room).map((file) => ({
        id:
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `staged-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        previewUri: URL.createObjectURL(file),
      }));
      return [...prev, ...next];
    });
  }, []);

  // Parallel staging tray for VIDEO files. Each entry is probed
  // asynchronously (poster frame + duration) so the tray tile shows a
  // thumbnail; flushed via `sendVideoMessage` on submit.
  const [stagedVideos, setStagedVideos] = useState<
    ReadonlyArray<{
      readonly id: string;
      readonly file: File;
      readonly caption?: string | undefined;
      readonly posterUri?: string | undefined;
      readonly durationLabel?: string | undefined;
      readonly probed?: ProbedVideo | undefined;
    }>
  >([]);
  const stagedVideosRef = useRef(stagedVideos);
  useEffect(() => {
    stagedVideosRef.current = stagedVideos;
  }, [stagedVideos]);

  const stageVideoFiles = useCallback((files: ReadonlyArray<File>): void => {
    const videoFiles = files.filter((f) => f.type.startsWith('video/'));
    if (videoFiles.length === 0) return;
    const accepted: Array<{ id: string; file: File }> = [];
    setStagedVideos((prev) => {
      const room = 10 - prev.length;
      if (room <= 0) {
        pingToast.error({
          title: 'Attachment limit reached',
          description: 'You can attach up to 10 files at once.',
        });
        return prev;
      }
      const next = videoFiles.slice(0, room).map((file) => {
        const id =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `staged-video-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        accepted.push({ id, file });
        return { id, file };
      });
      return [...prev, ...next];
    });
    // Probe each accepted video off the render path; patch in the poster
    // + duration when ready.
    for (const { id, file } of accepted) {
      void probeVideoFile(file)
        .then((probed) => {
          setStagedVideos((prev) =>
            prev.map((s) =>
              s.id === id
                ? {
                    ...s,
                    probed,
                    ...(probed.posterObjectUrl !== undefined
                      ? { posterUri: probed.posterObjectUrl }
                      : {}),
                    durationLabel: formatVideoDuration(probed.durationMs),
                  }
                : s,
            ),
          );
        })
        .catch(() => undefined);
    }
  }, []);

  const handleRemoveStagedAttachment = useCallback((id: string): void => {
    setStagedImages((prev) => {
      const found = prev.find((s) => s.id === id);
      if (found !== undefined) {
        URL.revokeObjectURL(found.previewUri);
        return prev.filter((s) => s.id !== id);
      }
      return prev;
    });
    setStagedVideos((prev) => {
      const found = prev.find((s) => s.id === id);
      if (found !== undefined) {
        if (found.posterUri !== undefined) URL.revokeObjectURL(found.posterUri);
        if (found.probed?.objectUrl !== undefined) URL.revokeObjectURL(found.probed.objectUrl);
        return prev.filter((s) => s.id !== id);
      }
      return prev;
    });
    setStagedFiles((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // Tray shape consumed by the shared `<ChatRoom>` (id + kind +
  // preview for images / name + sizeLabel for files).
  const stagedTrayItems = useMemo(
    () => [
      ...stagedImages.map((s) => ({
        id: s.id,
        kind: 'image' as const,
        previewUri: s.previewUri,
        ...(s.caption !== undefined && s.caption.length > 0 ? { caption: s.caption } : {}),
      })),
      ...stagedVideos.map((s) => ({
        id: s.id,
        kind: 'video' as const,
        ...(s.posterUri !== undefined ? { previewUri: s.posterUri } : {}),
        ...(s.durationLabel !== undefined ? { durationLabel: s.durationLabel } : {}),
        ...(s.caption !== undefined && s.caption.length > 0 ? { caption: s.caption } : {}),
      })),
      ...stagedFiles.map((s) => ({
        id: s.id,
        kind: 'file' as const,
        name: s.file.name,
        sizeLabel: formatFileSize(s.file.size),
        ...(s.previewUri !== undefined ? { previewUri: s.previewUri } : {}),
        ...(s.caption !== undefined && s.caption.length > 0 ? { caption: s.caption } : {}),
      })),
    ],
    [stagedImages, stagedVideos, stagedFiles],
  );

  // ── Attachment caption overlay (bug 3b). Tapping a staged tray tile
  // opens <AttachmentPreviewOverlay> for that id; the host owns the
  // caption draft + applies it back onto the staged item on confirm.
  const [captionOverlayId, setCaptionOverlayId] = useState<string | null>(null);

  const handlePressStagedAttachment = useCallback(
    (id: string): void => {
      setCaptionOverlayId(id);
    },
    [],
  );

  // The caption now comes from the overlay's rich composer (formatting +
  // mentions), passed in directly — no separate draft state / stale-read.
  const handleConfirmCaption = useCallback(
    (caption: string): void => {
      const id = captionOverlayId;
      if (id === null) return;
      setStagedImages((prev) =>
        prev.map((s) => (s.id === id ? { ...s, caption } : s)),
      );
      setStagedVideos((prev) =>
        prev.map((s) => (s.id === id ? { ...s, caption } : s)),
      );
      setStagedFiles((prev) =>
        prev.map((s) => (s.id === id ? { ...s, caption } : s)),
      );
      setCaptionOverlayId(null);
    },
    [captionOverlayId],
  );

  const handleCloseCaption = useCallback((): void => {
    setCaptionOverlayId(null);
  }, []);

  // Resolve the tapped attachment into the overlay's display shape.
  const captionOverlayAttachment = useMemo(() => {
    if (captionOverlayId === null) return undefined;
    const img = stagedImages.find((s) => s.id === captionOverlayId);
    if (img !== undefined) {
      return {
        id: img.id,
        kind: 'image' as const,
        previewUri: img.previewUri,
      };
    }
    const vid = stagedVideos.find((s) => s.id === captionOverlayId);
    if (vid !== undefined) {
      return {
        id: vid.id,
        kind: 'video' as const,
        ...(vid.posterUri !== undefined ? { previewUri: vid.posterUri } : {}),
        ...(vid.probed?.objectUrl !== undefined ? { videoUri: vid.probed.objectUrl } : {}),
      };
    }
    const doc = stagedFiles.find((s) => s.id === captionOverlayId);
    if (doc !== undefined) {
      return {
        id: doc.id,
        kind: 'file' as const,
        name: doc.file.name,
        ...(doc.previewUri !== undefined ? { previewUri: doc.previewUri } : {}),
      };
    }
    return undefined;
  }, [captionOverlayId, stagedImages, stagedVideos, stagedFiles]);

  // Full staged set in tray order → drives the overlay's carousel so the
  // user can page between staged attachments while captioning. The active
  // index tracks `captionOverlayId`; navigating re-points it via
  // `onIndexChange` so `handleConfirmCaption` always targets the on-screen
  // item.
  const captionOverlayItems = useMemo(
    () => [
      ...stagedImages.map((s) => ({
        id: s.id,
        kind: 'image' as const,
        ...(s.previewUri !== undefined ? { previewUri: s.previewUri } : {}),
      })),
      ...stagedVideos.map((s) => ({
        id: s.id,
        kind: 'video' as const,
        ...(s.posterUri !== undefined ? { previewUri: s.posterUri } : {}),
        ...(s.probed?.objectUrl !== undefined ? { videoUri: s.probed.objectUrl } : {}),
      })),
      ...stagedFiles.map((s) => ({
        id: s.id,
        kind: 'file' as const,
        name: s.file.name,
        ...(s.previewUri !== undefined ? { previewUri: s.previewUri } : {}),
      })),
    ],
    [stagedImages, stagedVideos, stagedFiles],
  );
  const captionOverlayIndex = useMemo(
    () =>
      captionOverlayId === null
        ? -1
        : captionOverlayItems.findIndex((it) => it.id === captionOverlayId),
    [captionOverlayId, captionOverlayItems],
  );

  // ── Per-message upload progress (bug 2). Keyed by the local-echo row
  // id; `messageVariants` reads the fraction to drive the image/doc
  // 'uploading' state's `progress`. Cleared on commit (resolve).
  const [uploadProgressByMessageId, setUploadProgressByMessageId] = useState<
    Record<string, number>
  >(() => ({}));
  const setUploadProgress = useCallback((id: string, fraction: number): void => {
    setUploadProgressByMessageId((prev) => ({ ...prev, [id]: fraction }));
  }, []);
  const clearUploadProgress = useCallback((id: string): void => {
    setUploadProgressByMessageId((prev) => {
      if (prev[id] === undefined) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  // ── Fullscreen media viewer (MediaPreviewScreen). The image variant's
  // `onTapOpen` / the video card's fullscreen control set the opened
  // media's MESSAGE id; the screen renders a carousel over ALL of the
  // conversation's image+video media, opened at that item.
  const [previewMediaId, setPreviewMediaId] = useState<string | null>(null);
  // Saved Call-Notes being previewed in the read-only overlay (footer "Meeting
  // notes" → open). null when closed.
  const [notesPreview, setNotesPreview] = useState<CallNote | null>(null);

  // Open the saved Call-Notes for a call (footer "Meeting notes"). Toasts if the
  // call had no notes.
  const openCallNotes = useCallback(async (callId: string): Promise<void> => {
    const note = await webCallNotesPersister.get(callId);
    if (!note || (note.text.trim().length === 0 && note.items.length === 0)) {
      toastStore.show('No notes were saved for this call.', { type: 'info' });
      return;
    }
    setNotesPreview(note);
  }, []);

  // Download the saved Call-Notes as a Markdown file.
  const downloadCallNotes = useCallback(async (callId: string): Promise<void> => {
    const note = await webCallNotesPersister.get(callId);
    if (!note || (note.text.trim().length === 0 && note.items.length === 0)) {
      toastStore.show('No notes were saved for this call.', { type: 'info' });
      return;
    }
    if (typeof document === 'undefined') return;
    const md = callNotesToMarkdown(note);
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-notes-${(note.title || callId).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  // Imperative bridge to ChatRoom's scroll-into-view, used by the media
  // viewer's "Go to message" option to jump back to a row.
  const scrollToMessageRef = useRef<((target: string) => void) | null>(null);

  // ── PDF document previews (Figma 3412:4900). For each PDF doc message we
  // fetch+decrypt the blob once, keep the decrypted PDF object URL (drives
  // the in-app reader) and render its first page to a PNG object URL
  // (drives the doc tile's page-thumbnail layout in bubble + compact). Keyed
  // by message id; object URLs are revoked on unmount.
  const [docPreviews, setDocPreviews] = useState<
    Record<string, { docUri: string; thumbUri: string; mime: string; filename: string }>
  >({});
  const docPreviewUrlsRef = useRef<string[]>([]);
  const docPreviewInFlightRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    return () => {
      for (const url of docPreviewUrlsRef.current) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* already revoked */
        }
      }
      docPreviewUrlsRef.current = [];
    };
  }, []);

  // ── Decrypted-media object-URL lifecycle (per conversation) ───────────
  // Received video / poster / image blobs are decrypted into
  // `URL.createObjectURL` handles and cached by message id so a tile doesn't
  // re-fetch on every render. `WebChatRoom` is a PERSISTENT instance whose
  // `conversationId` changes WITHOUT remounting, so with no explicit reset these
  // maps — and the multi-MB blobs they reference — accumulate for EVERY
  // conversation visited across the whole session (the blob URLs were never
  // revoked): a large, unbounded memory leak. Mirror the latest maps into refs
  // and, when the conversation changes (or the surface unmounts), revoke every
  // URL and drop the maps + in-flight guards so re-entering the conversation
  // cleanly re-resolves. URLs live untouched while a conversation stays open, so
  // behaviour within a conversation is unchanged; only leaving it reclaims memory.
  const videoUriMapRef = useRef(videoUriByMessageId);
  videoUriMapRef.current = videoUriByMessageId;
  const posterUriMapRef = useRef(posterUriByMessageId);
  posterUriMapRef.current = posterUriByMessageId;
  const imagePreviewMapRef = useRef(imagePreviewByMessageId);
  imagePreviewMapRef.current = imagePreviewByMessageId;
  useEffect(() => {
    // These two Set refs keep a stable object identity for the component's life
    // (only ever mutated via .add/.delete/.clear, never reassigned), so
    // capturing them here is equivalent to reading `.current` at cleanup and
    // satisfies the exhaustive-deps ref-in-cleanup rule. The URL map refs, by
    // contrast, are re-pointed at fresh state every render — the cleanup MUST
    // read their `.current` to revoke the latest set of URLs.
    const videoFetchStarted = videoFetchStartedRef.current;
    const docPreviewInFlight = docPreviewInFlightRef.current;
    return () => {
      const revoke = (u: string): void => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          /* already revoked */
        }
      };
      for (const u of Object.values(videoUriMapRef.current)) revoke(u);
      for (const u of Object.values(posterUriMapRef.current)) revoke(u);
      for (const u of Object.values(imagePreviewMapRef.current)) revoke(u);
      for (const u of docPreviewUrlsRef.current) revoke(u);
      docPreviewUrlsRef.current = [];
      videoFetchStarted.clear();
      docPreviewInFlight.clear();
      // Drop the now-revoked URLs so a later re-projection of the same message
      // id can't render a dead blob handle — the resolve effects re-run against
      // the empty maps and re-fetch on demand.
      setVideoUriByMessageId({});
      setPosterUriByMessageId({});
      setImagePreviewByMessageId({});
      setDocPreviews({});
    };
  }, [conversationId]);

  // ── Pinned-set projection (content hashes of pinned messages). Folded
  // from incoming com.ping.pin/unpin via the onMessage tap above, plus
  // optimistic self-writes in the pin/unpin actions. Drives <PinnedBar>.
  //
  // pin/unpin envelopes are NOT persisted as message rows (they're
  // projected, not rendered), so this set must be CACHED locally per
  // conversation — otherwise it resets to empty on reload and the pinned
  // bar disappears. We seed from localStorage on mount, reload it when the
  // conversation changes, and persist it on every change. (No wire-schema
  // change — the pin/unpin envelopes are untouched.)
  const [pinnedTargets, setPinnedTargets] = useState<Set<string>>(() =>
    loadPinnedTargets(conversationId),
  );
  useEffect(() => {
    setPinnedTargets(loadPinnedTargets(conversationId));
  }, [conversationId]);
  useEffect(() => {
    savePinnedTargets(conversationId, pinnedTargets);
  }, [conversationId, pinnedTargets]);

  const [savedTargets, setSavedTargets] = useState<Set<string>>(() =>
    loadSavedTargets(conversationId),
  );
  useEffect(() => {
    setSavedTargets(loadSavedTargets(conversationId));
  }, [conversationId]);
  useEffect(() => {
    saveSavedTargets(conversationId, savedTargets);
  }, [conversationId, savedTargets]);

  // Encryption-break repair (§ recovery): subscribe to the SDK's stranded-
  // conversation signal so THIS room shows a "Restart secure session" banner
  // when its MLS epoch can't advance (iOS parity — was silently logged).
  const [isConversationStranded, setIsConversationStranded] = useState(false);
  useEffect(() => {
    return subscribeStrandedConversations((ids) => {
      setIsConversationStranded(ids.has(conversationId));
    });
  }, [conversationId]);
  const [restartingSession, setRestartingSession] = useState(false);
  const handleRestartSession = useCallback(() => {
    setRestartingSession(true);
    void (async () => {
      try {
        // Re-establish the secure session: re-drive the recovery/reconcile
        // machinery for this conversation (re-subscribe / re-Welcome), which the
        // sync layer uses to recover a stranded conversation. Clearing the flag
        // optimistically hides the banner; the next stranded poll re-raises it if
        // the epoch still can't advance. (Full iOS-style recreate-and-rekey of a
        // 1:1 is a scoped follow-up — ideally an SDK `restartSession` primitive,
        // same layering argument as the recovery `reAdmitDevice` primitive.)
        clearStrandedConversation(conversationId);
        if (client !== null) {
          await commitPendingProposalsAndSurfaceStranded(client).catch(() => undefined);
        }
        pingToast.success({ title: 'Repairing secure session…' });
      } finally {
        setRestartingSession(false);
      }
    })();
  }, [conversationId, client]);

  // Starred messages (§2.7 0x64) — DeviceGroup-synced flat set. Subscribe to the
  // web store so a star set on another device (applied in the background by
  // `useIncomingMessages`) live-updates the bubble menu label + indicator.
  const [starredTargets, setStarredTargets] = useState<Set<string>>(new Set());
  useEffect(() => {
    return webStarredPersister.subscribe?.((targets) => setStarredTargets(new Set(targets)));
  }, []);
  const starredTargetsRef = useRef(starredTargets);
  useEffect(() => {
    starredTargetsRef.current = starredTargets;
  }, [starredTargets]);

  // Local type carried from the echo phase into the upload phase.
  type PreparedImage = {
    readonly item: {
      readonly file: File;
      readonly previewUri: string;
      readonly caption?: string | undefined;
    };
    readonly localId: string | null;
  };

  // ECHO PHASE — persist EVERY local-echo row (no network) so all image
  // bubbles appear instantly in an 'uploading' state with their staged
  // thumbnail + 0% bar. Returns the prepared rows for the upload phase.
  const echoStagedImages = useCallback(
    async (
      items: ReadonlyArray<{
        readonly file: File;
        readonly previewUri: string;
        readonly caption?: string | undefined;
      }>,
    ): Promise<PreparedImage[]> => {
      const imageItems = items.filter((s) => s.file.type.startsWith('image/'));
      if (imageItems.length === 0) return [];
      log.info({ event: 'chat.attach.image.send.start', count: imageItems.length });
      return Promise.all(
        imageItems.map(async (item) => {
          const localId = await persistPendingAttachment({
            conversationId,
            kind: 'image',
            file: item.file,
            ...(item.caption !== undefined ? { caption: item.caption } : {}),
          });
          if (localId !== null) {
            setImagePreviewByMessageId((prev) => ({ ...prev, [localId]: item.previewUri }));
            setUploadProgress(localId, 0);
            mediaRetrySourceRef.current.set(localId, {
              kind: 'image',
              file: item.file,
              ...(item.caption !== undefined ? { caption: item.caption } : {}),
            });
          }
          return { item, localId };
        }),
      );
    },
    [conversationId, setUploadProgress],
  );

  // UPLOAD PHASE — encrypt + upload + send the already-echoed rows. 1:1
  // runs in PARALLEL (its proxy is a stable handle and the SDK worker
  // serializes the actual send), so the S3 uploads overlap and each
  // bubble's % climbs at once. Groups MUST run sequentially and
  // re-resolve the conversation per send so each picks up the freshly-
  // advanced epoch/seq (a shared proxy would emit stale-seq messages
  // the projection dedupes away).
  const uploadStagedImages = useCallback(
    async (prepared: ReadonlyArray<PreparedImage>): Promise<void> => {
      if (prepared.length === 0) return;
      const sharedConv = await resolveConv();
      if (sharedConv === null) {
        const msg = 'Session not ready. Try again in a moment.';
        setSendError(msg);
        pingToast.error({ title: "Couldn't send image", description: msg });
        prepared.forEach((p) => {
          if (p.localId !== null) clearUploadProgress(p.localId);
        });
        return;
      }
      const runOne = async ({ item, localId }: PreparedImage): Promise<boolean> => {
        const { file, caption } = item;
        const conv = isGroup ? await resolveConv() : sharedConv;
        if (conv === null) {
          if (localId !== null) clearUploadProgress(localId);
          return false;
        }
        try {
          const source = await fileToImageSource(file);
          const { messageId, previewObjectUrl } = await sendImageMessage({
            conv,
            conversationId,
            bundle: bundleForSource(source),
            source,
            ...(localId !== null ? { localId } : {}),
            ...(caption !== undefined ? { caption } : {}),
            ...(localId !== null
              ? { onProgress: (f: number) => setUploadProgress(localId, f) }
              : {}),
          });
          setImagePreviewByMessageId((prev) => ({ ...prev, [messageId]: previewObjectUrl }));
          if (localId !== null) clearUploadProgress(localId);
          return true;
        } catch (err) {
          if (localId !== null) clearUploadProgress(localId);
          log.error({ event: 'chatroom.image.send.failed', err: (err as Error).message });
          setSendError("Couldn't send image. Try again.");
          pingToast.error({
            title: "Couldn't send image",
            description: 'Check your connection and try again.',
          });
          return false;
        }
      };

      let sent = 0;
      if (isGroup) {
        for (const p of prepared) {
          if (await runOne(p)) sent += 1;
        }
      } else {
        const results = await Promise.all(prepared.map(runOne));
        sent = results.filter(Boolean).length;
      }
      if (sent > 0) {
        log.info({ event: 'chat.attach.image.send.ok', sent });
      }
    },
    [conversationId, resolveConv, setUploadProgress, clearUploadProgress, isGroup],
  );

  // Paste / drag-drop feed the staging tray (not an immediate send).
  useEffect(() => {
    registerWebImageUpload(async (files) => {
      stageImageFiles(files);
    });
    return () => registerWebImageUpload(null);
  }, [stageImageFiles]);

  // Composer submit → send the text (if any), then flush the staged
  // attachment tray so text + images land as one logical send. Empty
  // text with staged images still flushes (attachments-only send).
  // Flush the staged non-image files via the encrypted-blob file send.
  type PreparedFile = {
    readonly item: { readonly file: File; readonly caption?: string | undefined };
    readonly localId: string | null;
  };

  // ECHO PHASE — persist all file echoes up front (no network) so every
  // tile appears instantly with a 0% bar + local page thumbnail.
  const echoStagedFiles = useCallback(
    async (
      items: ReadonlyArray<{
        readonly file: File;
        readonly caption?: string | undefined;
      }>,
    ): Promise<PreparedFile[]> => {
      if (items.length === 0) return [];
      return Promise.all(
        items.map(async (item) => {
          const localId = await persistPendingAttachment({
            conversationId,
            kind: 'file',
            file: item.file,
            ...(item.caption !== undefined ? { caption: item.caption } : {}),
          });
          if (localId !== null) {
            setUploadProgress(localId, 0);
            mediaRetrySourceRef.current.set(localId, {
              kind: 'file',
              file: item.file,
              ...(item.caption !== undefined ? { caption: item.caption } : {}),
            });
            // Seed the doc preview from the LOCAL file immediately so the
            // sent tile renders its page thumbnail instantly (no wait for
            // the post-commit S3 fetch / a tap). The decrypted-blob object
            // URL also powers the in-app reader straight away.
            const mime =
              item.file.type !== '' ? item.file.type : 'application/octet-stream';
            const filename = item.file.name;
            const docUri = URL.createObjectURL(item.file);
            docPreviewUrlsRef.current.push(docUri);
            const seedId = localId;
            void (async () => {
              const thumbUri = canPreviewDoc(mime, filename)
                ? await renderDocThumbnail(item.file, mime, filename)
                : undefined;
              if (thumbUri !== undefined) docPreviewUrlsRef.current.push(thumbUri);
              setDocPreviews((prev) => ({
                ...prev,
                [seedId]: { docUri, thumbUri: thumbUri ?? '', mime, filename },
              }));
            })();
          }
          return { item, localId };
        }),
      );
    },
    [conversationId, setUploadProgress],
  );

  // UPLOAD PHASE — encrypt + upload + send the already-echoed file rows.
  const uploadStagedFiles = useCallback(
    async (prepared: ReadonlyArray<PreparedFile>): Promise<void> => {
      if (prepared.length === 0) return;
      const sharedConv = await resolveConv();
      if (sharedConv === null) {
        const msg = 'Session not ready. Try again in a moment.';
        setSendError(msg);
        pingToast.error({ title: "Couldn't send file", description: msg });
        prepared.forEach((p) => {
          if (p.localId !== null) clearUploadProgress(p.localId);
        });
        return;
      }
      const runOne = async ({ item, localId }: PreparedFile): Promise<boolean> => {
        const { file, caption } = item;
        const conv = isGroup ? await resolveConv() : sharedConv;
        if (conv === null) {
          if (localId !== null) clearUploadProgress(localId);
          return false;
        }
        try {
          await sendFileMessage({
            conv,
            conversationId,
            file,
            ...(localId !== null ? { localId } : {}),
            ...(caption !== undefined ? { caption } : {}),
            ...(localId !== null
              ? { onProgress: (f: number) => setUploadProgress(localId, f) }
              : {}),
          });
          if (localId !== null) clearUploadProgress(localId);
          return true;
        } catch (err) {
          if (localId !== null) clearUploadProgress(localId);
          log.error({ event: 'chatroom.file.send.failed', err: (err as Error).message });
          setSendError("Couldn't send file. Try again.");
          pingToast.error({
            title: "Couldn't send file",
            description: 'Check your connection and try again.',
          });
          return false;
        }
      };

      let sent = 0;
      if (isGroup) {
        for (const p of prepared) {
          if (await runOne(p)) sent += 1;
        }
      } else {
        const results = await Promise.all(prepared.map(runOne));
        sent = results.filter(Boolean).length;
      }
      if (sent > 0) {
        log.info({ event: 'chat.attach.file.send.ok', sent });
      }
    },
    [conversationId, resolveConv, setUploadProgress, clearUploadProgress, isGroup],
  );

  type PreparedVideo = {
    readonly item: {
      readonly file: File;
      readonly caption?: string | undefined;
      readonly posterUri?: string | undefined;
      readonly probed?: ProbedVideo | undefined;
    };
    readonly localId: string | null;
  };

  // ECHO PHASE — persist all video echoes up front (seed posters + 0%
  // bars, no network) so every video tile appears instantly.
  const echoStagedVideos = useCallback(
    async (
      items: ReadonlyArray<{
        readonly file: File;
        readonly caption?: string | undefined;
        readonly posterUri?: string | undefined;
        readonly probed?: ProbedVideo | undefined;
      }>,
    ): Promise<PreparedVideo[]> => {
      if (items.length === 0) return [];
      return Promise.all(
        items.map(async (item) => {
          const localId = await persistPendingAttachment({
            conversationId,
            kind: 'video',
            file: item.file,
            ...(item.caption !== undefined ? { caption: item.caption } : {}),
          });
          if (localId !== null) {
            if (item.posterUri !== undefined) {
              setPosterUriByMessageId((prev) => ({ ...prev, [localId]: item.posterUri as string }));
            }
            setUploadProgress(localId, 0);
            mediaRetrySourceRef.current.set(localId, {
              kind: 'video',
              file: item.file,
              ...(item.caption !== undefined ? { caption: item.caption } : {}),
              ...(item.posterUri !== undefined ? { posterUri: item.posterUri } : {}),
              ...(item.probed !== undefined ? { probed: item.probed } : {}),
            });
          }
          return { item, localId };
        }),
      );
    },
    [conversationId, setUploadProgress],
  );

  // UPLOAD PHASE — encrypt + upload + send the already-echoed video rows.
  const uploadStagedVideos = useCallback(
    async (prepared: ReadonlyArray<PreparedVideo>): Promise<void> => {
      if (prepared.length === 0) return;
      const sharedConv = await resolveConv();
      if (sharedConv === null) {
        const msg = 'Session not ready. Try again in a moment.';
        setSendError(msg);
        pingToast.error({ title: "Couldn't send video", description: msg });
        prepared.forEach((p) => {
          if (p.localId !== null) clearUploadProgress(p.localId);
        });
        return;
      }
      const runOne = async ({ item, localId }: PreparedVideo): Promise<boolean> => {
        const { file, caption, probed } = item;
        const conv = isGroup ? await resolveConv() : sharedConv;
        if (conv === null) {
          if (localId !== null) clearUploadProgress(localId);
          return false;
        }
        try {
          const { messageId, videoObjectUrl, posterObjectUrl } = await sendVideoMessage({
            conv,
            conversationId,
            file,
            ...(probed !== undefined ? { probed } : {}),
            ...(localId !== null ? { localId } : {}),
            ...(caption !== undefined ? { caption } : {}),
            ...(localId !== null
              ? { onProgress: (f: number) => setUploadProgress(localId, f) }
              : {}),
          });
          setVideoUriByMessageId((prev) => ({ ...prev, [messageId]: videoObjectUrl }));
          if (posterObjectUrl !== undefined) {
            setPosterUriByMessageId((prev) => ({ ...prev, [messageId]: posterObjectUrl }));
          }
          if (localId !== null) clearUploadProgress(localId);
          return true;
        } catch (err) {
          if (localId !== null) clearUploadProgress(localId);
          log.error({ event: 'chatroom.video.send.failed', err: (err as Error).message });
          setSendError("Couldn't send video. Try again.");
          pingToast.error({
            title: "Couldn't send video",
            description: 'Check your connection and try again.',
          });
          return false;
        }
      };

      let sent = 0;
      if (isGroup) {
        for (const p of prepared) {
          if (await runOne(p)) sent += 1;
        }
      } else {
        const results = await Promise.all(prepared.map(runOne));
        sent = results.filter(Boolean).length;
      }
      if (sent > 0) log.info({ event: 'chat.attach.video.send.ok', sent });
    },
    [conversationId, resolveConv, setUploadProgress, clearUploadProgress, isGroup],
  );

  const handleSendComposite = useCallback(
    async (text: string): Promise<void> => {
      const trimmed = text.trim();
      const stagedImg = stagedImagesRef.current;
      const stagedVid = stagedVideosRef.current;
      const stagedDoc = stagedFilesRef.current;

      // ── 1) INSTANT OPTIMISTIC UI ──────────────────────────────────
      // Clear the tray and persist EVERY attachment's local-echo row up
      // front (no network), so the text bubble AND all attachment tiles
      // (at 0%) appear in the chat at once — instead of the text landing
      // first while the attachments linger in the tray until uploaded.
      if (stagedImg.length > 0) setStagedImages([]);
      if (stagedVid.length > 0) setStagedVideos([]);
      if (stagedDoc.length > 0) setStagedFiles([]);
      const [imgPrepared, vidPrepared, docPrepared] = await Promise.all([
        stagedImg.length > 0 ? echoStagedImages(stagedImg) : Promise.resolve([]),
        stagedVid.length > 0 ? echoStagedVideos(stagedVid) : Promise.resolve([]),
        stagedDoc.length > 0 ? echoStagedFiles(stagedDoc) : Promise.resolve([]),
      ]);

      // ── 2) TEXT SEND ──────────────────────────────────────────────
      // `handleSend` echoes the text bubble instantly too, then awaits
      // its network round-trip. Done BEFORE the attachment uploads so
      // group sends never race each other (stale-seq).
      if (trimmed.length > 0) {
        // Smart link preview: scrape the first URL (sender-side) and embed
        // the scraped metadata INSIDE the text envelope so it renders as a
        // single unified bubble (text + preview) for everyone. Bounded by a
        // short timeout so a slow/blocked unfurl never stalls the send.
        const url = extractFirstUrl(trimmed);
        let linkPreview: UnfurledLink | undefined;
        if (url !== undefined) {
          try {
            linkPreview = await Promise.race([
              unfurlLink(url),
              new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 3500)),
            ]);
          } catch (err) {
            log.debug({ event: 'chatroom.linkpreview.failed', err: (err as Error).message });
          }
        }
        await handleSend(trimmed, undefined, linkPreview);
      }

      // ── 3) ATTACHMENT UPLOADS ─────────────────────────────────────
      // The tiles are already on screen; their % bars now climb as each
      // encrypt/upload/send completes.
      if (imgPrepared.length > 0) {
        try {
          await uploadStagedImages(imgPrepared);
        } finally {
          // Safe to release the staged object URLs now — the committed
          // bubbles point at the export-blob URL swapped in on resolve.
          stagedImg.forEach((s) => URL.revokeObjectURL(s.previewUri));
        }
      }
      if (vidPrepared.length > 0) await uploadStagedVideos(vidPrepared);
      if (docPrepared.length > 0) await uploadStagedFiles(docPrepared);
    },
    [
      handleSend,
      echoStagedImages,
      echoStagedVideos,
      echoStagedFiles,
      uploadStagedImages,
      uploadStagedVideos,
      uploadStagedFiles,
    ],
  );

  // Lazily fetch + decrypt a received video's full blob on first play and
  // mint an object URL the inline player can stream. Idempotent — guarded
  // by `videoFetchStartedRef` so repeated taps don't re-download.
  const resolveVideoBlob = useCallback(
    async (messageId: string, body: string): Promise<string | undefined> => {
      const existing = videoUriByMessageId[messageId];
      if (existing !== undefined) return existing;
      if (videoFetchStartedRef.current.has(messageId)) return undefined;
      videoFetchStartedRef.current.add(messageId);
      const parsed = parseVideoEnvelope(body);
      if (parsed === undefined) return undefined;
      setVideoDownloadProgressById((prev) => ({ ...prev, [messageId]: 0 }));
      try {
        const blob = await fetchAndDecryptMediaBlob(
          parsed.blob_ref,
          undefined,
          parsed.mime,
          (f) => setVideoDownloadProgressById((prev) => ({ ...prev, [messageId]: f })),
        );
        const url = URL.createObjectURL(blob);
        setVideoUriByMessageId((prev) => ({ ...prev, [messageId]: url }));
        setVideoDownloadProgressById((prev) => {
          const next = { ...prev };
          delete next[messageId];
          return next;
        });
        return url;
      } catch (err) {
        videoFetchStartedRef.current.delete(messageId);
        setVideoDownloadProgressById((prev) => {
          const next = { ...prev };
          delete next[messageId];
          return next;
        });
        log.warn({ event: 'chatroom.video.resolve.failed', err: (err as Error).message });
        pingToast.error({
          title: "Couldn't load video",
          description: 'Check your connection and try again.',
        });
        return undefined;
      }
    },
    [videoUriByMessageId],
  );

  const downloadVideoBlob = useCallback(
    async (messageId: string, body: string): Promise<void> => {
      const url = videoUriByMessageId[messageId] ?? (await resolveVideoBlob(messageId, body));
      if (url === undefined || typeof document === 'undefined') return;
      const a = document.createElement('a');
      a.href = url;
      a.download = 'video';
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [videoUriByMessageId, resolveVideoBlob],
  );

  useEffect(() => {
    if (shellHandlers?.closeOverlay) {
      shellHandlers.closeOverlay('imageEditor');
    }
  }, [shellHandlers]);

  const messages = useMemo(
    () => (messagesState.kind === 'populated' ? messagesState.messages : []),
    [messagesState],
  );

  // Viewing a conversation clears its unread badge. Runs on open and
  // whenever new messages land while it's open (incoming bumps the count;
  // this immediately zeroes it for the active chat).
  useEffect(() => {
    void webConversationsPersister
      .update(conversationId, { unreadCount: 0 })
      .catch(() => undefined);
  }, [conversationId, messages.length]);

  // Group roster self-introduction. Receivers only learn a member's
  // device-hex → display name once that member broadcasts `ping.profile`
  // — historically only the inviter did, so everyone else rendered as
  // "…<hex>". On entering a group we broadcast our own profile ONCE so
  // every peer can resolve our messages to our real name. Guarded per
  // conversation id so it fires at most once per room open.
  const groupProfileSentRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!isGroup) return;
    if (groupProfileSentRef.current.has(conversationId)) return;
    groupProfileSentRef.current.add(conversationId);
    void (async () => {
      try {
        const identity = await retrieveIdentity();
        if (identity === null) return;
        const conv = await resolveConv();
        if (conv === null) return;
        const profile = {
          kind: 'ping.profile',
          payload: {
            accountId: identity.accountId,
            displayName: identity.displayName,
            // Group marker — receivers map device→name/account into the
            // group roster (NOT the 1:1 `direct` rename path).
            direct: false,
          },
        };
        await conv.send(new TextEncoder().encode(JSON.stringify(profile)));
        log.info({ event: 'chatroom.group.profileIntro.sent', conversationIdPrefix: conversationId.slice(0, 6) });
      } catch (err) {
        groupProfileSentRef.current.delete(conversationId);
        log.warn({ event: 'chatroom.group.profileIntro.failed', err: (err as Error).message });
      }
    })();
  }, [isGroup, conversationId, resolveConv]);

  // Resolve video POSTERS (small) so tiles show a frame without
  // downloading the full clip. Covers BOTH received videos and our OWN
  // sent ones: in-session, the send path seeds a poster object URL (so the
  // `posterUriByMessageId` guard skips here), but object URLs die on
  // reload — without re-resolving, a self video would show no preview
  // after a refresh. The decrypted poster is cached durably in IndexedDB
  // (`webMediaThumbCache`) keyed by the message's content hash, so once a
  // poster has been resolved the tile NEVER loses it again — a reload
  // reads the cached blob instantly (no S3 round-trip, works offline).
  // Declared AFTER `messages` (it reads it).
  useEffect(() => {
    let cancelled = false;
    for (const m of messages) {
      if (m.body === undefined || m.body.charAt(0) !== '{') continue;
      if (posterUriByMessageId[m.id] !== undefined) continue;
      const parsed = parseVideoEnvelope(m.body);
      if (parsed?.poster_ref === undefined) continue;
      const posterRef = parsed.poster_ref;
      const cacheKey = `vid-poster:${m.contentHash.length > 0 ? m.contentHash : m.id}`;
      void (async () => {
        try {
          let blob = await webMediaThumbCache.get(cacheKey);
          if (blob === undefined) {
            blob = await fetchAndDecryptMediaBlob(posterRef, undefined, 'image/jpeg');
            void webMediaThumbCache.put(cacheKey, blob, 'image/jpeg');
          }
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          setPosterUriByMessageId((prev) =>
            prev[m.id] !== undefined ? prev : { ...prev, [m.id]: url },
          );
        } catch {
          // Poster-less tile is acceptable.
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [messages, posterUriByMessageId]);

  // Reply-quote thumbnails (§2.4 media replies — iOS `ReplyQuoteThumbnail`
  // parity): resolve the ORIGINAL media message's thumbnail so a reply to a
  // photo/video/GIF shows a real square, not just "📷 Photo" text. Keyed by the
  // ORIGINAL message id; images fetch+decrypt (cached durably), video reuses the
  // already-resolved poster, GIF uses the CDN still. Declared AFTER `messages`.
  const [replyThumbByOriginalId, setReplyThumbByOriginalId] = useState<
    Record<string, { uri: string; kind: 'image' | 'video' | 'gif' }>
  >({});
  // Mirror the map into a ref so the resolution effect can read the CURRENT set
  // for its "already resolved" dedup WITHOUT listing the map in its deps (which
  // would re-run + re-scan on every resolved thumbnail — churn), and so the
  // cleanup can revoke the latest object URLs.
  const replyThumbMapRef = useRef(replyThumbByOriginalId);
  replyThumbMapRef.current = replyThumbByOriginalId;
  // Revoke image object URLs + clear the map on conversation switch / unmount —
  // WebChatRoom is a persistent instance whose `conversationId` changes in place,
  // so without this every image reply-thumbnail blob URL (a full-res decrypted
  // image) would accumulate for the whole session. GIF is a CDN http URL (nothing
  // to revoke); video reuses a poster URL revoked by the media cleanup above.
  useEffect(() => {
    return () => {
      for (const entry of Object.values(replyThumbMapRef.current)) {
        if (entry.kind === 'image') {
          try {
            URL.revokeObjectURL(entry.uri);
          } catch {
            /* already revoked */
          }
        }
      }
      setReplyThumbByOriginalId({});
    };
  }, [conversationId]);
  useEffect(() => {
    let cancelled = false;
    // `inReplyTo.messageId` is INCONSISTENT across the two send paths: a SELF
    // reply stores the target's CONTENT HASH, while a received/peer reply stores
    // the parent's LOCAL id (`useIncomingMessages` rebuild). So key the reply-
    // thumb map under BOTH the content hash and the local id of the resolved
    // original — then the enrichment matches whichever `inReplyTo.messageId` uses.
    const referenced = new Set<string>();
    for (const m of messages) {
      const rid = m.inReplyTo?.messageId;
      if (rid !== undefined && rid.length > 0) referenced.add(rid);
    }
    // Also resolve the CURRENT compose-time reply target so the composer pill
    // shows its thumbnail before any reply exists in the timeline.
    if (replyTo !== null) {
      if (replyTo.contentHash.length > 0) referenced.add(replyTo.contentHash);
      if (replyTo.id.length > 0) referenced.add(replyTo.id);
    }
    const setThumb = (
      original: { id: string; contentHash: string },
      value: { uri: string; kind: 'image' | 'video' | 'gif' },
    ): void => {
      setReplyThumbByOriginalId((prev) => {
        if (prev[original.contentHash] !== undefined || prev[original.id] !== undefined) return prev;
        const next = { ...prev };
        if (original.contentHash.length > 0) next[original.contentHash] = value;
        if (original.id.length > 0) next[original.id] = value;
        return next;
      });
    };
    for (const original of messages) {
      const referencedByHash = original.contentHash.length > 0 && referenced.has(original.contentHash);
      const referencedById = original.id.length > 0 && referenced.has(original.id);
      if (!referencedByHash && !referencedById) continue;
      if (
        replyThumbMapRef.current[original.contentHash] !== undefined ||
        replyThumbMapRef.current[original.id] !== undefined
      ) {
        continue;
      }
      const body = original.body;
      if (body === undefined || body.charAt(0) !== '{') continue;
      const gif = parseGifEnvelope(body);
      const gifUrl = gif !== undefined ? (gif as { url?: string }).url : undefined;
      if (typeof gifUrl === 'string' && gifUrl.length > 0) {
        setThumb(original, { uri: gifUrl, kind: 'gif' });
        continue;
      }
      if (parseVideoEnvelope(body) !== undefined) {
        const poster = posterUriByMessageId[original.id];
        if (poster !== undefined) setThumb(original, { uri: poster, kind: 'video' });
        continue;
      }
      const img = parseImageEnvelope(body);
      if (img !== undefined) {
        const cacheKey = `reply-thumb:${original.contentHash.length > 0 ? original.contentHash : original.id}`;
        void (async () => {
          try {
            let blob = await webMediaThumbCache.get(cacheKey);
            if (blob === undefined) {
              blob = await fetchAndDecryptMediaBlob(img.blob_ref, undefined, img.mime);
              void webMediaThumbCache.put(cacheKey, blob, img.mime);
            }
            if (cancelled) return;
            setThumb(original, { uri: URL.createObjectURL(blob), kind: 'image' });
          } catch {
            /* text fallback — a reply without a resolvable thumbnail shows the
               kind-prefixed preview string, unchanged. */
          }
        })();
      }
    }
    return () => {
      cancelled = true;
    };
  }, [messages, posterUriByMessageId, replyTo]);

  // Eagerly render PDF doc previews (Figma 3412:4900): fetch+decrypt each
  // PDF doc blob once, keep its object URL (for the in-app reader) and
  // rasterize page 1 (for the tile's page-thumbnail layout). Declared
  // AFTER `messages` (it reads it). Each id is processed at most once.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      for (const m of messages) {
        const id = m.id;
        if (docPreviews[id] !== undefined || docPreviewInFlightRef.current.has(id)) {
          continue;
        }
        const payload = parseFileEnvelope(m.body);
        // Eagerly render a TILE thumbnail only for docs we can rasterize
        // (PDF via pdf.js, text-like as a page-of-text). Other types get
        // their preview lazily on tap (handleDocTap) so we don't pull every
        // .zip/.docx blob on mount.
        if (payload === undefined || !canPreviewDoc(payload.mime, payload.name)) {
          continue;
        }
        const mime = payload.mime;
        const filename = payload.name;
        docPreviewInFlightRef.current.add(id);
        try {
          const blob = await fetchAndDecryptMediaBlob(payload.blob_ref, undefined, mime);
          if (cancelled) return;
          const docUri = URL.createObjectURL(blob);
          docPreviewUrlsRef.current.push(docUri);
          const thumbUri = await renderDocThumbnail(blob, mime, filename);
          if (cancelled) return;
          if (thumbUri !== undefined) docPreviewUrlsRef.current.push(thumbUri);
          setDocPreviews((prev) => ({
            ...prev,
            [id]: { docUri, thumbUri: thumbUri ?? '', mime, filename },
          }));
        } catch (err) {
          log.debug({
            event: 'chatroom.doc.preview.failed',
            err: (err as Error).message,
          });
        } finally {
          docPreviewInFlightRef.current.delete(id);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [messages, docPreviews]);

  // Open ANY doc in the in-app reader (MediaPreviewScreen doc kind). PDFs
  // and text already have a cached object URL from the eager effect; other
  // types (docx/xlsx/zip…) are fetched+decrypted lazily here on first tap,
  // then the reader renders them inline if the browser can, or shows a
  // download fallback. Genuine fetch failures fall back to a direct
  // download so the file is never a dead end.
  const handleDocTap = useCallback(
    (messageId: string, body: string): void => {
      if (docPreviews[messageId] !== undefined) {
        setPreviewMediaId(messageId);
        return;
      }
      const payload = parseFileEnvelope(body);
      if (payload === undefined) {
        void openDocAttachment(body);
        return;
      }
      if (docPreviewInFlightRef.current.has(messageId)) return;
      docPreviewInFlightRef.current.add(messageId);
      void (async () => {
        try {
          const blob = await fetchAndDecryptMediaBlob(
            payload.blob_ref,
            undefined,
            payload.mime,
          );
          const docUri = URL.createObjectURL(blob);
          docPreviewUrlsRef.current.push(docUri);
          const thumbUri = await renderDocThumbnail(blob, payload.mime, payload.name);
          if (thumbUri !== undefined) docPreviewUrlsRef.current.push(thumbUri);
          setDocPreviews((prev) => ({
            ...prev,
            [messageId]: {
              docUri,
              thumbUri: thumbUri ?? '',
              mime: payload.mime,
              filename: payload.name,
            },
          }));
          setPreviewMediaId(messageId);
        } catch (err) {
          log.warn({ event: 'chatroom.doc.tap.failed', err: (err as Error).message });
          void openDocAttachment(body);
        } finally {
          docPreviewInFlightRef.current.delete(messageId);
        }
      })();
    },
    [docPreviews],
  );

  // ── Game projection from PERSISTED messages. Re-derives every game
  // session by folding the conversation's game envelopes in order, so
  // the previous game's outcome/winner survives a reload (the in-memory
  // fold resets to empty on mount). Both our own moves (local echo) and
  // peer moves are persisted, so this is the single authoritative source;
  // the optimistic `applyGameEvent` calls above just make the local
  // player's actions feel instant before this reconciles.
  const persistedGameEvents = useMemo(() => {
    const events: GameEvent[] = [];
    for (const m of messages) {
      if (m.body === undefined || m.body.length === 0 || m.body.charAt(0) !== '{') {
        continue;
      }
      const parsed = parseEnvelope(m.body);
      if (parsed === undefined || !isGameKind(parsed.kind) || !('body' in parsed)) {
        continue;
      }
      // Attribute the actor: our own rows → '__self__' (matches the
      // optimistic folds); peer rows → their account id.
      const by = m.isSelf ? '__self__' : m.senderId;
      const evt = gameEventFromParsed(parsed.kind, parsed.body, by);
      if (evt !== null) events.push(evt);
    }
    return events;
  }, [messages]);
  useEffect(() => {
    replaceFromEvents(persistedGameEvents);
  }, [persistedGameEvents, replaceFromEvents]);

  // Backfill the device→account/name maps when a message's sender can't be
  // resolved from the current roster (a member whose `ping.profile` we never got,
  // or who just linked a NEW device). Delegates to the SHARED
  // `backfillGroupDeviceMaps` — the same authoritative `GET /v1/devices/{account}`
  // path hydration runs on boot for every conversation — so open-chat and
  // background paths can never drift. `force` is passed here because an
  // unresolved sender means a device we don't have, even for an already-mapped
  // member. Cheap: a no-op once every sender resolves.
  useEffect(() => {
    if (!isGroup || localConv?.memberAccountIds === undefined) return;

    const peerIds = localConv.memberAccountIds;
    const displayNames = localConv.memberDisplayNames ?? {};
    const deviceNames = localConv.memberDeviceNames ?? {};
    const deviceAccounts = localConv.memberDeviceAccounts ?? {};

    const unresolvedMessageSenders = messages.some((m) => {
      if (m.isSelf) return false;
      const label = resolveGroupSenderLabel(m.senderId, {
        memberDisplayNames: displayNames,
        memberDeviceNames: deviceNames,
        memberDeviceAccounts: deviceAccounts,
      });
      return isUnresolvedGroupSenderLabel(m.senderId, label);
    });

    const mappedAccounts = new Set(Object.values(deviceAccounts));
    const someMemberUnmapped = peerIds.some(
      (id) => id.length > 0 && !mappedAccounts.has(id),
    );
    if (!someMemberUnmapped && !unresolvedMessageSenders) return;

    let cancelled = false;
    void (async () => {
      if (cancelled) return;
      await backfillGroupDeviceMaps(
        conversationId,
        peerIds,
        displayNames,
        deviceAccounts,
        unresolvedMessageSenders,
      ).catch(() => false);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    conversationId,
    isGroup,
    localConv?.memberAccountIds,
    localConv?.memberDeviceNames,
    localConv?.memberDeviceAccounts,
    localConv?.memberDisplayNames,
    messages,
  ]);

  const [memberAvatarUris, setMemberAvatarUris] = useState<Record<string, string>>({});
  // Bumped when a peer broadcasts `peer_profile_updated` so member avatars
  // re-resolve in realtime (their cache entry is cleared below).
  const [avatarRefreshNonce, setAvatarRefreshNonce] = useState(0);

  // Realtime peer-avatar invalidation: the BE pushes `peer_profile_updated`
  // ({ account_id }) to 1:1 peers when someone changes their profile. Drop that
  // account's cached avatar and re-resolve so the new picture shows live.
  useEffect(() => {
    const transport = getCurrentTransport();
    if (transport === null || typeof transport.onAccountEvent !== 'function') return;
    const sub = transport.onAccountEvent((event) => {
      if (event.type !== 'peer_profile_updated') return;
      const acct = typeof event.account_id === 'string' ? event.account_id : null;
      if (acct === null) return;
      memberAvatarCache.delete(acct);
      setAvatarRefreshNonce((n) => n + 1);
    });
    return () => sub.unsubscribe();
  }, []);

  // Stable key: the effect must re-run only when the SET of members changes,
  // NOT on every render (memberAccountIds is a fresh array ref each time). The
  // old version re-fired constantly, each run looping `searchUsers` per member
  // → a /v1/users/search 429 storm. Now it's cache-backed + member-set-keyed.
  const memberIdsKey = (localConv?.memberAccountIds ?? []).join(',');
  const persistedMemberAvatars = localConv?.memberAvatarUris;
  // Derive the render map (accountId → avatar URL) from the PERSISTED row —
  // instant on open, correct on reload, available offline — overlaid with the
  // in-process cache (which holds the freshest realtime `peer_profile_updated`
  // refresh). No network here; the directory refresh happens in the hydrate
  // effect below, which writes back to the row and re-triggers this derive.
  useEffect(() => {
    if (!isGroup || localConv?.memberAccountIds === undefined) {
      setMemberAvatarUris({});
      return;
    }
    const accountIds = localConv.memberAccountIds ?? [];
    const persisted = persistedMemberAvatars ?? {};
    const derived: Record<string, string> = {};
    for (const id of accountIds) {
      const cached = memberAvatarCache.get(id);
      const url = typeof cached === 'string' && cached.length > 0 ? cached : persisted[id];
      if (typeof url === 'string' && url.length > 0) derived[id] = url;
    }
    setMemberAvatarUris(derived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroup, memberIdsKey, persistedMemberAvatars, avatarRefreshNonce]);

  // Proactive identity hydration — the "never see a member without a name or
  // avatar" guarantee. Resolves EVERY member (group roster or 1:1 peer) by
  // ACCOUNT ID and persists name + avatar onto the Dexie row, so the roster,
  // 1:1 header and message bubbles show the real identity the moment the chat
  // opens (not only after Chat Info is opened) and it survives reload/offline.
  // Re-runs when the member set changes, on realtime profile updates, and when
  // the network comes back (a previously-failed fetch retries).
  useEffect(() => {
    void hydrateMemberProfiles(conversationId);
    const onOnline = (): void => {
      void hydrateMemberProfiles(conversationId);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
      }
    };
  }, [conversationId, memberIdsKey, avatarRefreshNonce]);

  // Refresh the peer avatar whenever Chat Info opens. A 1:1's `avatarUri` is a
  // PRESIGNED S3 url that expires (~5 min); unlike a group it isn't re-resolved
  // from a stable `avatarMediaId` on its own, so a chat left open long enough
  // would show the peer's INITIALS in the Chat-Info header once the url 403s
  // (ProfileAvatar falls back to initials on image error). Re-hydrating on open
  // fetches a fresh url so the modal always shows the real picture.
  useEffect(() => {
    if (!chatSettingsOpen) return;
    void hydrateMemberProfiles(conversationId);
    // While Chat Info is open, converge the roster LIVE: any MLS Commit on this
    // conversation (a peer/admin adding or removing a member) fires
    // `onConversationUpdated`; re-run the authoritative `GET /members` reconcile
    // so the open Members tab updates without a close+reopen. Bounded to the open
    // modal so it never becomes a global per-commit hydrate storm.
    const c = client as unknown as {
      onConversationUpdated?: (h: (id: Uint8Array) => void) => () => void;
    };
    if (typeof c.onConversationUpdated !== 'function') return;
    const unsub = c.onConversationUpdated((id) => {
      try {
        if (bytesToHexLocal(id).toLowerCase() === conversationId.toLowerCase()) {
          void hydrateMemberProfiles(conversationId);
        }
      } catch {
        /* non-hex id — ignore */
      }
    });
    return unsub;
  }, [chatSettingsOpen, conversationId, client]);

  // Local user's own avatar — the directory member fetch above never
  // includes self, so resolve it separately (by the identity's display
  // name, matched on accountId). Powers the live-location map pin for
  // self shares (Figma 3629:6788); falls back to initials on a miss.
  const [selfAvatarUri, setSelfAvatarUri] = useState<string | undefined>(undefined);
  // The self-profile store is the live source of truth (updated instantly on an
  // avatar change in Settings); fall back to the directory-resolved value.
  const storeSelfAvatar = useSelfProfile((s) => s.avatarUrl);
  const resolvedSelfAvatar = storeSelfAvatar ?? selfAvatarUri;
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const id = await retrieveIdentity();
      if (id === null || cancelled || id.displayName.length === 0) return;
      try {
        const res = await searchUsers({ query: id.displayName, limit: 10, skipReauth: true });
        const match = res.users.find((u) => u.id === id.accountId);
        if (!cancelled && match?.avatarUri !== undefined && match.avatarUri.length > 0) {
          setSelfAvatarUri(match.avatarUri);
        }
      } catch {
        // Directory miss — the bubble/pin falls back to initials.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [localAccountId]);

  const groupRosterMaps = useMemo(
    () => ({
      memberDisplayNames: localConv?.memberDisplayNames ?? {},
      memberDeviceNames: localConv?.memberDeviceNames ?? {},
      memberDeviceAccounts: localConv?.memberDeviceAccounts ?? {},
    }),
    [
      localConv?.memberDisplayNames,
      localConv?.memberDeviceNames,
      localConv?.memberDeviceAccounts,
    ],
  );

  const resolveTypingDisplayName = useCallback(
    (senderDevice: Uint8Array) => {
      const deviceKey = deviceKeyFromBytes(senderDevice);
      const label = resolveGroupSenderLabel(deviceKey, groupRosterMaps);
      if (isGroup) {
        return isUnresolvedGroupSenderLabel(deviceKey, label) ? 'Someone' : label;
      }
      return isUnresolvedGroupSenderLabel(deviceKey, label) ? headerTitle : label;
    },
    [groupRosterMaps, headerTitle, isGroup],
  );

  const ephemeralTyping = useEphemeralTyping({
    conversationId,
    conversationIdBytes,
    getClient: getCurrentEphemeralClient,
    resolveDisplayName: resolveTypingDisplayName,
    enabled: isFeatureEnabled('FF_EPHEMERAL_WS'),
    // This hook is only mounted for the OPEN chat, so a peer-typing-start here
    // is always the active conversation — play a soft typing chime.
    onPeerTypingStart: () => messageSoundManager.playTyping(),
  });

  const ephemeralPresence = useEphemeralPresence({
    conversationId,
    conversationIdBytes,
    getClient: getCurrentEphemeralClient,
    enabled: isFeatureEnabled('FF_EPHEMERAL_WS'),
  });

  const [directoryPresence, setDirectoryPresence] = useState<ChatPresence>(null);

  useEffect(() => {
    if (!isFeatureEnabled('FF_EPHEMERAL_WS')) return;
    if (isGroup || headerTitle === 'New chat' || headerTitle === 'Group chat') {
      setDirectoryPresence(null);
      return;
    }
    let cancelled = false;
    searchUsers({ query: headerTitle, limit: 5, skipReauth: true })
      .then((res) => {
        if (cancelled) return;
        const exact = res.users.find(
          (u) => u.displayName.toLowerCase() === headerTitle.toLowerCase(),
        );
        const raw = exact?.presence;
        if (raw === 'online' || raw === 'away' || raw === 'offline') {
          setDirectoryPresence(raw);
        } else if (raw === 'busy') {
          setDirectoryPresence('away');
        } else {
          setDirectoryPresence(null);
        }
      })
      .catch(() => {
        if (!cancelled) setDirectoryPresence(null);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, headerTitle, isGroup]);

  const peerPresence: ChatPresence = isGroup
    ? null
    : ephemeralPresence.peerPresence ?? directoryPresence;

  notifyComposerSentRef.current = ephemeralTyping.notifyComposerSent;

  const senderNames = useMemo((): Record<string, string> | undefined => {
    if (!isGroup) return undefined;
    const out: Record<string, string> = {};
    for (const m of messages) {
      if (m.isSelf) continue;
      // Never leak the raw device-hash tail as a display name: until the
      // roster hydrates, `resolveGroupSenderLabel` returns `…a3f9c1`. Mirror
      // the typing-indicator path and substitute a neutral "Member".
      const label = resolveGroupSenderLabel(m.senderId, groupRosterMaps);
      out[m.senderId] = isUnresolvedGroupSenderLabel(m.senderId, label) ? 'Member' : label;
    }
    return out;
  }, [isGroup, messages, groupRosterMaps]);

  const senderAvatars = useMemo(():
    | Record<string, { initials: string; imageUri?: string }>
    | undefined => {
    if (!isGroup) return undefined;
    const out: Record<string, { initials: string; imageUri?: string }> = {};
    for (const m of messages) {
      if (m.isSelf) continue;
      const rawLabel = resolveGroupSenderLabel(m.senderId, groupRosterMaps);
      // Substitute the neutral placeholder BEFORE deriving initials so an
      // unresolved sender never renders the ellipsis-tail avatar ("…").
      const label = isUnresolvedGroupSenderLabel(m.senderId, rawLabel) ? 'Member' : rawLabel;
      const accountId = resolveGroupSenderAccountId(m.senderId, groupRosterMaps);
      const imageUri =
        accountId !== undefined ? memberAvatarUris[accountId] : undefined;
      out[m.senderId] = groupSenderAvatarFromLabel(label, imageUri);
    }
    return out;
  }, [isGroup, messages, groupRosterMaps, memberAvatarUris]);

  // Resolve a poll voter's account id → { initials, avatarUrl } for the
  // per-option avatar stack, reusing the same roster/avatar sources as
  // group sender avatars.
  const resolvePollVoter = useCallback(
    (accountId: string): { initials: string; avatarUrl?: string; name: string } => {
      const isSelfVoter =
        accountId === localAccountId || accountId === '__local__';
      // Never surface a raw account id as a "name": in a group with an
      // unhydrated member, fall back to a neutral "Member" placeholder for the
      // votes-panel list (the avatar stack already uses initials only).
      const resolvedName = isSelfVoter
        ? 'You'
        : localConv?.memberDisplayNames?.[accountId] ?? (!isGroup ? headerTitle : undefined);
      const name = resolvedName ?? 'Member';
      // `memberAvatarUris` is only populated for GROUPS. In a 1:1 (e.g. the
      // call-ended tile's roster stack) it's empty, so self + the single peer
      // would fall back to initials. Resolve self from the self-profile avatar
      // and the 1:1 peer from the conversation (header) avatar so real pictures
      // show in the stack.
      const imageUri =
        memberAvatarUris[accountId] ??
        (isSelfVoter ? resolvedSelfAvatar : !isGroup ? headerAvatarUri : undefined);
      const a = groupSenderAvatarFromLabel(resolvedName ?? name, imageUri);
      return a.imageUri !== undefined
        ? { initials: a.initials, avatarUrl: a.imageUri, name }
        : { initials: a.initials, name };
    },
    [
      localAccountId,
      localConv?.memberDisplayNames,
      isGroup,
      headerTitle,
      memberAvatarUris,
      resolvedSelfAvatar,
      headerAvatarUri,
    ],
  );

  // Resolve a reactor's account id → display identity for the
  // "who reacted" hover popover (Figma 3992:74151). Same roster/avatar
  // sources as poll-voter avatars, plus the display name + self flag.
  const resolveReactor = useCallback(
    (accountId: string): ReactionReactor => {
      const isSelf =
        accountId === localAccountId || accountId === '__local__';
      const name = isSelf
        ? 'You'
        : localConv?.memberDisplayNames?.[accountId] ??
          (!isGroup ? headerTitle : accountId.slice(0, 6));
      const imageUri = memberAvatarUris[accountId];
      const a = groupSenderAvatarFromLabel(name, imageUri);
      return {
        accountId,
        displayName: name,
        isSelf,
        initials: a.initials,
        ...(a.imageUri !== undefined ? { avatarUrl: a.imageUri } : {}),
      };
    },
    [localAccountId, localConv?.memberDisplayNames, isGroup, headerTitle, memberAvatarUris],
  );

  /** Tapping an @mention or a sender avatar opens that user's profile in
   *  the right-utility panel. `raw` can be a per-message sender id (avatar
   *  tap), a resolved account id (mention with a populated `mentions`
   *  array), OR a bare @handle (mention the receive path didn't resolve).
   *  We canonicalise it to an account id and attach the display
   *  name + avatar the chat already knows, so the panel renders real
   *  identity immediately instead of the empty "Member" placeholder while
   *  the directory round-trip resolves. */
  /** Shared resolution for both the avatar tap (→ popover) and the in-text
   *  mention tap (→ full modal): canonicalises `raw` to an account id and
   *  attaches the display name + avatar the chat already knows. */
  const resolveProfileTarget = useCallback(
    (raw: string): { accountId: string; hint: { displayName?: string; avatarUri?: string } } | null => {
      if (raw.length === 0) return null;

      // 1:1 — the only "other" person is the peer. Use the peer's base64url
      // ACCOUNT id (`peerAccountId`), NOT the conversation id: the latter is the
      // hex MLS/BE conversation id, which `GET /v1/users/{id}/profile` rejects
      // with a 400 (and the directory keys profiles by account id).
      if (!isGroup) {
        const peerId = localConv?.peerAccountId ?? raw;
        const peerAvatar =
          memberAvatarUris[peerId] ??
          memberAvatarUris[conversationId] ??
          memberAvatarUris[raw];
        return {
          accountId: peerId,
          hint: {
            ...(headerTitle !== 'New chat' ? { displayName: headerTitle } : {}),
            ...(peerAvatar !== undefined ? { avatarUri: peerAvatar } : {}),
          },
        };
      }

      // Group — resolve a canonical account id from the per-message sender
      // id (via roster maps), fall back to `raw` when it already is one.
      const fromRoster = resolveGroupSenderAccountId(raw, groupRosterMaps);
      let accountId = fromRoster ?? raw;
      const names = localConv?.memberDisplayNames ?? {};

      // `raw` may be a bare handle ("@jane" → "jane"). If it isn't a known
      // account id, match it against roster display names (the handle is
      // typically the name without spaces / a name fragment).
      if (names[accountId] === undefined) {
        const needle = raw.replace(/^@/, '').toLowerCase();
        const hit = Object.entries(names).find(
          ([id, nm]) =>
            id.toLowerCase().endsWith(needle) ||
            nm.toLowerCase().replace(/\s+/g, '') === needle ||
            nm.toLowerCase() === needle,
        );
        if (hit !== undefined) accountId = hit[0];
      }

      const label =
        names[accountId] ??
        resolveGroupSenderLabel(raw, groupRosterMaps) ??
        undefined;
      const avatar = memberAvatarUris[accountId];
      return {
        accountId,
        hint: {
          ...(label !== undefined && label.length > 0 ? { displayName: label } : {}),
          ...(avatar !== undefined ? { avatarUri: avatar } : {}),
        },
      };
    },
    [
      isGroup,
      conversationId,
      headerTitle,
      memberAvatarUris,
      groupRosterMaps,
      localConv?.memberDisplayNames,
      localConv?.peerAccountId,
    ],
  );

  // In-text @mention tap → full profile modal.
  const handleMentionPress = useCallback(
    (raw: string): void => {
      const t = resolveProfileTarget(raw);
      if (t === null) return;
      shellHandlers?.openUserProfile(t.accountId as unknown as UserId, t.hint);
    },
    [resolveProfileTarget, shellHandlers],
  );

  // Avatar tap → profile popover anchored to the tapped avatar.
  const handleAvatarPress = useCallback(
    (raw: string, anchorRef: RefObject<unknown>): void => {
      const t = resolveProfileTarget(raw);
      if (t === null) return;
      shellHandlers?.openMiniProfile(t.accountId as unknown as UserId, anchorRef);
    },
    [resolveProfileTarget, shellHandlers],
  );

  // Read-receipt name/avatar resolvers for group chats. Memoized (not inline in
  // the JSX) because they are forwarded to EVERY MessageRow; a fresh identity
  // per render would defeat MessageRow's React.memo and re-render the whole
  // visible message window on any parent re-render (typing pulses, presence,
  // hover). readBy[].userId = hex(sender_device) (64-char MLS hash);
  // memberDisplayNames is keyed by account_id, memberDeviceAccounts maps
  // device_hash → account_id, memberDeviceNames maps device_hash → name.
  const resolveReaderName = useCallback(
    (userId: string): string | undefined => {
      const accountId = localConv?.memberDeviceAccounts?.[userId];
      return (
        localConv?.memberDisplayNames?.[userId] ??
        (accountId !== undefined ? localConv?.memberDisplayNames?.[accountId] : undefined) ??
        localConv?.memberDeviceNames?.[userId]
      );
    },
    [localConv?.memberDeviceAccounts, localConv?.memberDisplayNames, localConv?.memberDeviceNames],
  );
  const resolveReaderAvatar = useCallback(
    (userId: string): string | undefined => {
      const accountId = localConv?.memberDeviceAccounts?.[userId];
      return (
        memberAvatarUris[userId] ??
        (accountId !== undefined ? memberAvatarUris[accountId] : undefined)
      );
    },
    [localConv?.memberDeviceAccounts, memberAvatarUris],
  );

  const mentionCandidates = useMemo(() => {
    if (isGroup) {
      const names = localConv?.memberDisplayNames;
      if (names === undefined) return [];
      return Object.entries(names).map(([userId, displayName]) => ({
        userId,
        displayName,
      }));
    }
    if (headerTitle === 'New chat') return [];
    return [{ userId: conversationId, displayName: headerTitle }];
  }, [isGroup, localConv?.memberDisplayNames, conversationId, headerTitle]);

  // Keep the send-time ref in sync with the memoized candidate set.
  useEffect(() => {
    mentionCandidatesRef.current = mentionCandidates;
  }, [mentionCandidates]);

  // Header subtitle shows presence/members; live "X is typing…" is
  // surfaced separately by the in-list typing indicator (passed via
  // `typingLabel`) so the two cues don't duplicate each other.
  const headerSubtitle = useMemo(() => {
    if (isGroup) {
      return memberCount > 0 ? `${memberCount} members` : undefined;
    }
    if (peerPresence === 'online') return 'Online';
    if (peerPresence === 'away') return 'Away';
    if (peerPresence === 'offline') return 'Offline';
    return undefined;
  }, [isGroup, memberCount, peerPresence]);

  // ---------- Image pick + paste pipeline ----------
  //
  // Web sends immediately after file pick/paste (encrypt → multipart
  // upload → MLS). No crop/draw editor overlay on this platform.
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  // Mount a hidden `<input type=file>` once per host so the
  // `+ → Photos` menu can trigger the browser's native picker
  // without rendering chrome of its own. The input gets removed
  // on unmount; click() is invoked imperatively when the menu
  // entry fires.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.style.display = 'none';
    document.body.appendChild(input);
    imageInputRef.current = input;
    return () => {
      input.remove();
      imageInputRef.current = null;
    };
  }, []);

  const handlePickImages = useCallback(async (): Promise<void> => {
    const input = imageInputRef.current;
    if (input === null) return;
    // Reset so picking the same file twice in a row still fires
    // `change` (without this, Chrome ignores a re-selection of
    // an identical filename).
    input.value = '';
    await new Promise<void>((resolve) => {
      const onChange = (): void => {
        input.removeEventListener('change', onChange);
        const fileList = input.files;
        const files: File[] = [];
        if (fileList !== null) {
          for (let i = 0; i < fileList.length; i++) {
            const f = fileList.item(i);
            if (f !== null) files.push(f);
          }
        }
        stageImageFiles(files);
        resolve();
      };
      input.addEventListener('change', onChange);
      input.click();
    });
  }, [stageImageFiles]);

  const handlePasteImage = useCallback(
    (file: File) => {
      stageImageFiles([file]);
    },
    [stageImageFiles],
  );

  // Hidden `<input type=file>` (any type) so the `+ → Add files` menu
  // entry can trigger the native file picker without rendering chrome.
  const docInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.style.display = 'none';
    document.body.appendChild(input);
    docInputRef.current = input;
    return () => {
      input.remove();
      docInputRef.current = null;
    };
  }, []);

  const handlePickDoc = useCallback(async (): Promise<void> => {
    const input = docInputRef.current;
    if (input === null) return;
    input.value = '';
    await new Promise<void>((resolve) => {
      const onChange = (): void => {
        input.removeEventListener('change', onChange);
        const fileList = input.files;
        const files: File[] = [];
        if (fileList !== null) {
          for (let i = 0; i < fileList.length; i++) {
            const f = fileList.item(i);
            if (f !== null) files.push(f);
          }
        }
        // Route images to the image tray, everything else to the file
        // tray, so a mixed selection from the generic picker still
        // stages correctly.
        stageImageFiles(files);
        stageVideoFiles(files);
        stageFiles(files);
        resolve();
      };
      input.addEventListener('change', onChange);
      input.click();
    });
  }, [stageImageFiles, stageVideoFiles, stageFiles]);

  // Camera capture overlay (web getUserMedia → JPEG File → image tray).
  const [cameraOpen, setCameraOpen] = useState(false);
  const handleCaptureCamera = useCallback((): void => {
    setCameraOpen(true);
  }, []);

  // Drag-and-drop staging. The host owns the DOM listeners on a
  // wrapping div; images stage as image tiles, everything else as file
  // tiles. `dragDepthRef` counts enter/leave so nested elements don't
  // prematurely clear the overlay.
  const [dragActive, setDragActive] = useState(false);
  const dragDepthRef = useRef(0);
  const handleDragEnter = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    dragDepthRef.current += 1;
    if (e.dataTransfer?.types?.includes('Files')) {
      setDragActive(true);
    }
  }, []);
  const handleDragOver = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDragActive(false);
    }
  }, []);
  const handleDrop = useCallback(
    (e: React.DragEvent): void => {
      e.preventDefault();
      dragDepthRef.current = 0;
      setDragActive(false);
      const dropped: File[] = [];
      const list = e.dataTransfer?.files;
      if (list) {
        for (let i = 0; i < list.length; i++) {
          const f = list.item(i);
          if (f !== null) dropped.push(f);
        }
      }
      if (dropped.length === 0) return;
      stageImageFiles(dropped);
      stageVideoFiles(dropped);
      stageFiles(dropped);
    },
    [stageImageFiles, stageVideoFiles, stageFiles],
  );

  const isLoading = messagesState.kind === 'loading';
  const error = messagesState.kind === 'error' ? messagesState.error.message : null;

  // Live call state. `activeCallId` is the raw active callId without any
  // conversationId gating — the conversationId comparison was causing false
  // nulls and is unnecessary since callIds are globally unique per call.
  const activeCallId = useCallStore((s) => s.active?.callId ?? null);
  const remoteParticipantCount = useCallStore((s) => {
    let n = 0;
    for (const p of s.participants) if (!p.isLocal) n += 1;
    return n;
  });
  // Pipe-separated callIds whose tiles should show "Call started" (not "Call
  // ongoing") because no second person has joined yet:
  //   1. Outgoing caller alone in the LiveKit room (no remote participants)
  //   2. Incoming ring not yet answered (callee hasn't joined)
  // Using a joined string gives Zustand a stable equality surface (Object.is
  // on a primitive) so the selector doesn't re-render on every call.
  const callingCallIdsKey = useCallStore((s) => {
    const ids: string[] = [];
    if (
      s.active !== null &&
      s.active.direction === 'outgoing' &&
      !s.participants.some((p) => !p.isLocal)
    ) {
      ids.push(s.active.callId);
    }
    for (const [id, ring] of s.rings) {
      if (ring.status === 'ringing') ids.push(id);
    }
    return ids.join('|');
  });
  // Subscribe to the raw participants array (stable reference unless participants
  // actually change). Using .map() inside the selector creates a new array on
  // every render → getSnapshot never returns the same reference → infinite loop.
  const callParticipantsRaw = useCallStore((s) => s.participants);
  // Live roster (account ids) per call_id from `com.ping.call_roster` presence
  // pings — lets the ongoing-call tile show a live count/avatars for viewers
  // who are NOT in the call (see calls-drift.md §23).
  const liveRosterByCallId = useCallStore((s) => s.liveRosterByCallId);
  // Derive the minimal name+accountId shape outside the selector so the
  // reference only changes when participants actually change (join/leave/rename).
  const liveCallParticipants = useMemo(
    () =>
      callParticipantsRaw.map((p) => ({
        name: p.name,
        accountId: p.accountId as string | undefined,
        // Carry `isLocal` so the ongoing-call tile can resolve OUR OWN avatar
        // from the self-profile picture — LiveKit's local participant metadata
        // often has no `accountId`, so it can't go through the account-keyed
        // resolver and would otherwise fall back to initials.
        isLocal: p.isLocal,
      })),
    [callParticipantsRaw],
  );

  // ── Calls: fold the call_invite + call_end pair into ONE tile per call.
  // A call is exactly ONE timeline tile (the invite, in the position the call
  // started). When a matching `call_end` exists, the invite tile flips to its
  // terminal state (ended / missed / failed) and the separate `call_end` row is
  // SUPPRESSED — never two tiles for one call, never a stale "Call ongoing".
  // This is the single source of truth, derived from persisted messages so it
  // survives reload. `terminalByCallId` carries the resolved end-state;
  // `suppressedCallEndIds` are end rows hidden because their invite shows it;
  // `ongoingInvite` is the most recent still-live call (no end yet) for the
  // header pill + join wiring.
  const callTiles = useMemo(() => {
    const terminalByCallId = new Map<
      string,
      {
        callState: 'ended' | 'missed';
        status: string;
        durationLabel: string;
        actionable: boolean;
        // Forward-compat: persisted account-id roster of everyone who joined
        // (from `call_end.participants`). Undefined until the SDK emits it —
        // see calls-participant-roster-spec.md.
        participants?: readonly string[] | undefined;
      }
    >();
    const inviteByCallId = new Map<
      string,
      { callId: string; callGroupId: string; media: 'audio' | 'video'; contentHash: string }
    >();
    // ALL call_end rows per call_id (a 1:1 call produces TWO — the leaver posts
    // `call_end{hangup}` and the survivor posts `call_end{left}` once the sole
    // remote drops — and a group's last-leaver path can race too). Must track
    // every one, not just the latest, or the un-tracked duplicate leaks a second
    // "Call ended" tile next to the folded invite tile.
    const endMsgIdsByCallId = new Map<string, string[]>();
    const inviteOrder: string[] = [];
    // First call_invite MESSAGE id per call_id → the canonical tile. Later
    // invites for the SAME call_id (e.g. a mid-call "add participant" re-invite
    // in a group) are suppressed so a call session shows exactly ONE tile.
    const canonicalInviteMsgByCallId = new Map<string, string>();
    const suppressedInviteIds = new Set<string>();
    // Content hashes that have ≥1 thread reply (parent_id points at them) →
    // drives the "Call threads" red dot. A thread reply is a com.ping.text with
    // payload.thread, or the com.ping.thread_root anchor.
    const threadedRoots = new Set<string>();
    for (const m of messages) {
      const body = m.body;
      if (body.length === 0 || body.charAt(0) !== '{') continue;
      const parsed = parseEnvelope(body);
      if (parsed === undefined) continue;
      // Any envelope carrying a `parent_id` is a thread reply (incl. the
      // thread_root anchor) → its target root has thread activity.
      const parentId =
        'parent_id' in parsed && typeof parsed.parent_id === 'string' ? parsed.parent_id : undefined;
      if (parentId !== undefined && parentId.length > 0) threadedRoots.add(parentId);
      if (parsed.kind === 'com.ping.call_invite') {
        const b = parsed.body as { call_id?: string; call_group_id?: string; media?: 'audio' | 'video' };
        if (b.call_id !== undefined && b.call_group_id !== undefined && b.media !== undefined) {
          const prior = canonicalInviteMsgByCallId.get(b.call_id);
          if (prior !== undefined) {
            // A later re-invite for the same call → suppress its tile.
            suppressedInviteIds.add(m.id);
          } else {
            canonicalInviteMsgByCallId.set(b.call_id, m.id);
            inviteByCallId.set(b.call_id, {
              callId: b.call_id,
              callGroupId: b.call_group_id,
              media: b.media,
              contentHash: m.contentHash,
            });
            inviteOrder.push(b.call_id);
          }
        }
      } else if (parsed.kind === 'com.ping.call_end') {
        const b = parsed.body as { call_id?: string; participants?: readonly string[] };
        if (b.call_id === undefined) continue;
        const ends = endMsgIdsByCallId.get(b.call_id);
        if (ends) ends.push(m.id);
        else endMsgIdsByCallId.set(b.call_id, [m.id]);
        // Reuse deriveVariant's exact end→tile mapping so terminal copy never drifts.
        const v = deriveVariantFromBody(body, { message: m });
        if (v?.kind === 'call') {
          // UNION the roster across every `call_end` for this call (a 1:1 posts
          // two; a group last-leaver races) so a complete participant list
          // survives even when an individual end row carries a thin/absent
          // roster — the terminal tile then always shows who was in the call.
          const prev = terminalByCallId.get(b.call_id);
          const merged = new Set<string>(prev?.participants ?? []);
          if (Array.isArray(b.participants)) {
            for (const a of b.participants) if (typeof a === 'string' && a.length > 0) merged.add(a);
          }
          terminalByCallId.set(b.call_id, {
            callState: v.callState === 'missed' ? 'missed' : 'ended',
            status: v.status,
            durationLabel: v.durationLabel,
            actionable: v.actionable ?? false,
            ...(merged.size > 0 ? { participants: [...merged] } : {}),
          });
        }
      }
    }
    // Guarantee exactly ONE tile per call_id (calls.md §3.6). When the invite
    // tile is present it shows the terminal state, so EVERY call_end row is
    // hidden. When there's no invite in view (e.g. an offline bystander who only
    // synced the end), keep a single call_end row as the tile and hide the rest —
    // so the common 1:1 "both parties posted call_end" case never doubles up.
    const suppressedCallEndIds = new Set<string>();
    for (const [callId, msgIds] of endMsgIdsByCallId) {
      const hideFrom = inviteByCallId.has(callId) ? 0 : 1;
      for (let i = hideFrom; i < msgIds.length; i += 1) suppressedCallEndIds.add(msgIds[i]!);
    }
    // Most recent still-live call (invite with no terminal) in this conversation.
    let ongoingInvite:
      | { callId: string; callGroupId: string; media: 'audio' | 'video'; contentHash: string }
      | null = null;
    for (let i = inviteOrder.length - 1; i >= 0; i -= 1) {
      const cid = inviteOrder[i]!;
      if (!terminalByCallId.has(cid)) {
        ongoingInvite = inviteByCallId.get(cid) ?? null;
        break;
      }
    }
    const callIds = Array.from(
      new Set<string>([...inviteByCallId.keys(), ...endMsgIdsByCallId.keys()]),
    );
    return {
      terminalByCallId,
      suppressedCallEndIds,
      suppressedInviteIds,
      threadedRoots,
      ongoingInvite,
      callIds,
    };
  }, [messages]);

  // Which calls have saved notes → drives the "Meeting notes" red dot. Notes
  // live in Dexie (per-callId), so resolve async whenever the call set changes.
  const [callsWithNotes, setCallsWithNotes] = useState<Set<string>>(new Set());
  const callIdsKey = callTiles.callIds.join(',');
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const present = new Set<string>();
      await Promise.all(
        callTiles.callIds.map(async (cid) => {
          try {
            const note = await webCallNotesPersister.get(cid);
            if (note && (note.text.trim().length > 0 || note.items.length > 0)) present.add(cid);
          } catch {
            /* ignore */
          }
        }),
      );
      if (!cancelled) setCallsWithNotes(present);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callIdsKey]);

  // Header live-call pill: "Call ongoing · Tap to join" (bystander) or
  // "In call · N" (viewer is in it). Participant count is only known when in it.
  const headerActiveCall = useMemo<ChatActiveCallStatus | undefined>(() => {
    const ongoing = callTiles.ongoingInvite;
    if (ongoing === null) return undefined;
    const joined = activeCallId === ongoing.callId;
    return {
      joined,
      ...(joined ? { participantCount: remoteParticipantCount + 1 } : {}),
      onPress: () => {
        const st = useCallStore.getState();
        if (st.active?.callId === ongoing.callId) {
          st.setMinimized(false);
        } else {
          void st.joinCall({
            callId: ongoing.callId,
            callGroupId: ongoing.callGroupId,
            media: ongoing.media,
            conversationId,
            title: headerTitle,
            isGroup,
            ...(ongoing.contentHash.length > 0 ? { threadRoot: ongoing.contentHash } : {}),
          });
        }
      },
    };
  }, [callTiles.ongoingInvite, activeCallId, remoteParticipantCount, conversationId, headerTitle, isGroup]);

  // Per-thread last-read wall-clock, keyed by the thread's root content hash.
  // Sourced from the conversation-scoped `read_pointers` table under the
  // `thread:<id>` namespace, which is DeviceGroup-synced — so the thread unread
  // badge is consistent across the user's devices. Drives `entry.unread` below.
  const [threadReadAt, setThreadReadAt] = useState<Record<string, number>>({});
  useEffect(() => {
    const apply = (
      rows: readonly { conversationId: string; lastReadAt: number }[],
    ): void => {
      const next: Record<string, number> = {};
      for (const r of rows) {
        if (r.conversationId.startsWith(THREAD_READ_POINTER_PREFIX)) {
          next[r.conversationId.slice(THREAD_READ_POINTER_PREFIX.length)] = r.lastReadAt;
        }
      }
      setThreadReadAt(next);
    };
    void webReadPointersPersister.list().then(apply);
    return webReadPointersPersister.subscribe?.(apply) ?? (() => undefined);
  }, []);

  // ── Threads (bug 4). Group incoming/outgoing messages by their
  // envelope `parent_id` (a parent's contentHash) so replies nest under
  // the root. We build:
  //   - `threadAffordances[rootMessageId]` — the {count, onPress,
  //     participants, lastReplierName, preview} card the root bubble
  //     renders below itself (driven by `<ThreadPreviewCard>`).
  //   - `timelineMessages` — the MAIN bubble list with every
  //     reply-carrying message (parent_id set) filtered OUT, so threaded
  //     replies live only in the thread panel + the count card.
  const { threadAffordances, timelineMessages, joinRequests, replyRootHashByMessageId } = useMemo(() => {
    // contentHash → root message id (only roots — i.e. messages WITHOUT
    // a parent_id — are valid thread anchors). Also keep the reverse map
    // (rootId → contentHash) so a reply that only knows its root by LOCAL
    // ID (the receiver path — see below) can still recover the root hash
    // the thread gateway / affordance onPress matches on.
    const hashToRootId = new Map<string, string>();
    const rootIdToHash = new Map<string, string>();
    for (const m of messages) {
      if (m.contentHash.length === 0) continue;
      hashToRootId.set(m.contentHash, m.id);
      rootIdToHash.set(m.id, m.contentHash);
    }

    const resolveReplierName = (m: Message): string => {
      if (m.isSelf) return 'You';
      if (isGroup) {
        const label = resolveGroupSenderLabel(m.senderId, groupRosterMaps);
        return isUnresolvedGroupSenderLabel(m.senderId, label) ? 'Someone' : label;
      }
      return headerTitle;
    };

    // Thread-root markers (`com.ping.thread_root`) establish — cross-peer —
    // WHICH roots have a thread. The sender emits one before the first
    // thread reply; the RECEIVER persists it (it never renders a bubble).
    // This is the only stable cross-peer thread signal on the receiver,
    // because incoming `com.ping.text` replies persist only their plain
    // text body (the wire `parent_id` is NOT kept in the row body), so
    // `parseEnvelope(m.body).parent_id` is undefined there. We resolve
    // each marker's parent (root) hash → root local id and treat that root
    // as a genuine thread anchor. The marker rows themselves are excluded
    // from the timeline (and never counted as replies).
    const threadRootIds = new Set<string>();
    const markerIds = new Set<string>();
    for (const m of messages) {
      const parsed = parseEnvelope(m.body);
      if (parsed?.kind !== 'com.ping.thread_root') continue;
      markerIds.add(m.id);
      // The marker's root is whichever message its `parent_id` (carried in
      // the parsed envelope, or — on the receiver, where the marker body is
      // empty — reconstructed onto `inReplyTo.messageId`) points at.
      const markerParentHash =
        'parent_id' in parsed && parsed.parent_id !== undefined
          ? parsed.parent_id
          : undefined;
      const rootByHash =
        markerParentHash !== undefined && markerParentHash.length > 0
          ? hashToRootId.get(markerParentHash)
          : undefined;
      const rootId =
        rootByHash ??
        (m.inReplyTo?.messageId !== undefined && m.inReplyTo.messageId.length > 0
          ? m.inReplyTo.messageId
          : undefined);
      if (rootId !== undefined) threadRootIds.add(rootId);
    }

    // rootId → accumulating affordance data.
    const acc = new Map<
      string,
      {
        count: number;
        unread: number;
        lastTs: number;
        lastReplierName: string;
        preview: string;
        participantNames: Set<string>;
        rootContentHash: string;
      }
    >();
    const replyIds = new Set<string>();
    // Reply message id → its thread root's CONTENT HASH. Lets a jump-to-message
    // request (e.g. from the Activity feed) open the right thread panel when its
    // target turns out to be a folded thread reply rather than a timeline row.
    const replyRootHashByMessageId = new Map<string, string>();
    // Game state-update envelopes (move/outcome/join/reveal) fold into the
    // live game tile via useGameSessions — they must NOT render as their
    // own timeline rows. Only `com.ping.game_create` shows a tile.
    const gameUpdateIds = new Set<string>();
    // Group-join requests (owner-approval invite flow) ride as a JSON envelope
    // and must NEVER render as a raw bubble — fold them out + collect them so a
    // dedicated Approve banner can surface the pending one(s).
    const joinRequestIds = new Set<string>();
    const joinRequests: GroupJoinRequestItem[] = [];

    for (const m of messages) {
      // Thread-root markers never render their own row.
      if (markerIds.has(m.id)) continue;

      const joinReq = decodeGroupJoinRequest(m.body);
      if (joinReq !== null) {
        joinRequestIds.add(m.id);
        joinRequests.push({
          id: m.id,
          isSelf: m.isSelf === true,
          senderId: m.senderId,
          groupId: joinReq.groupId,
          ...(joinReq.requesterDisplayName !== undefined
            ? { requesterDisplayName: joinReq.requesterDisplayName }
            : {}),
          timestamp: m.timestamp,
        });
        continue;
      }

      const parsed = parseEnvelope(m.body);
      if (
        parsed !== undefined &&
        (parsed.kind === 'com.ping.game_move' ||
          parsed.kind === 'com.ping.game_outcome' ||
          parsed.kind === 'com.ping.game_join' ||
          parsed.kind === 'com.ping.game_reveal')
      ) {
        gameUpdateIds.add(m.id);
        continue;
      }
      // Meeting sidecars (§2.13) persist as full rows so they fold into
      // `meetingState` from the messages table (like game moves), but they must
      // NOT render their own bubble — only `com.ping.meeting` (the create) shows
      // the card. Fold them out of the visible timeline here.
      if (
        parsed !== undefined &&
        (parsed.kind === 'com.ping.meeting_update' ||
          parsed.kind === 'com.ping.meeting_rsvp' ||
          parsed.kind === 'com.ping.meeting_cancel')
      ) {
        gameUpdateIds.add(m.id);
        continue;
      }

      // Resolve this message's thread root + its content hash, trying the
      // SENDER path first then the RECEIVER path:
      //   • Sender thread reply — the local echo persists the FULL envelope
      //     JSON, so `parseEnvelope(m.body).parent_id` is the root hash.
      //   • Receiver thread reply — the body is plain text (no parent_id),
      //     but `useIncomingMessages` reconstructs the parent onto
      //     `inReplyTo.messageId` (the root's LOCAL id). We only treat it
      //     as a thread reply when that root is a known thread anchor
      //     (a `thread_root` marker established it) — otherwise it's an
      //     inline reply-quote that must STAY in the main timeline.
      // Read `parent_id` + the self-identifying `thread` flag straight off
      // the raw envelope JSON. `parseEnvelope` doesn't surface custom
      // payload fields, and the reply's FULL envelope is persisted on BOTH
      // sender (local echo) and receiver (parent_id keeps the full body),
      // so this is symmetric.
      let rawParentId: string | undefined;
      let threadTagged = false;
      if (m.body.length > 0 && m.body.charAt(0) === '{') {
        try {
          const raw = JSON.parse(m.body) as {
            parent_id?: unknown;
            payload?: { thread?: unknown };
          };
          if (typeof raw.parent_id === 'string' && raw.parent_id.length > 0) {
            rawParentId = raw.parent_id;
          }
          threadTagged = raw.payload?.thread === true;
        } catch {
          // not JSON — leave as a normal message
        }
      }

      let rootId: string | undefined;
      let rootContentHash: string | undefined;
      if (threadTagged && rawParentId !== undefined) {
        // SELF-IDENTIFYING thread reply — fold out of the timeline
        // regardless of whether the `thread_root` marker has arrived. This
        // is the robustness fix: no marker-ordering / marker-loss race, and
        // it behaves identically for sender + receiver.
        rootContentHash = rawParentId;
        rootId = hashToRootId.get(rawParentId);
        if (rootId === undefined) {
          // Root row not loaded yet — still fold (never spill) ; the
          // affordance card appears once the root resolves. Record the root
          // hash so a jump-to-message can still open the thread by its root.
          replyIds.add(m.id);
          replyRootHashByMessageId.set(m.id, rootContentHash);
          continue;
        }
      } else if (rawParentId !== undefined) {
        // Has `parent_id` but NOT thread-tagged → an inline reply-quote OR
        // a legacy marker-based thread reply. Only fold when the root is a
        // genuine thread anchor (a `thread_root` marker established it);
        // otherwise it's an inline reply that STAYS in the timeline.
        const candidateRootId = hashToRootId.get(rawParentId);
        if (candidateRootId !== undefined && threadRootIds.has(candidateRootId)) {
          rootId = candidateRootId;
          rootContentHash = rawParentId;
        }
      } else {
        const inReplyRootId = m.inReplyTo?.messageId;
        if (
          inReplyRootId !== undefined &&
          inReplyRootId.length > 0 &&
          threadRootIds.has(inReplyRootId)
        ) {
          rootId = inReplyRootId;
          rootContentHash = rootIdToHash.get(inReplyRootId);
        }
      }

      // No resolvable thread root → leave in the main timeline (a normal
      // message, an inline reply-quote, or a genuinely orphaned reply whose
      // root we never received).
      if (rootId === undefined || rootContentHash === undefined) continue;

      replyIds.add(m.id);
      replyRootHashByMessageId.set(m.id, rootContentHash);
      const replierName = resolveReplierName(m);
      const entry = acc.get(rootId) ?? {
        count: 0,
        unread: 0,
        lastTs: 0,
        lastReplierName: replierName,
        preview: '',
        participantNames: new Set<string>(),
        // The thread identifier the gateway matches on is the root's
        // CONTENT HASH (= each reply's parent_id), NOT the message id.
        rootContentHash,
      };
      entry.count += 1;
      // Unread = a peer reply newer than this thread's DeviceGroup-synced
      // last-read pointer (`thread:<rootContentHash>`). Own replies never count.
      if (!m.isSelf && m.timestamp > (threadReadAt[rootContentHash] ?? 0)) {
        entry.unread += 1;
      }
      entry.participantNames.add(replierName);
      if (m.timestamp >= entry.lastTs) {
        entry.lastTs = m.timestamp;
        entry.lastReplierName = replierName;
        entry.preview = lastMessagePreview(m.body) ?? '';
      }
      acc.set(rootId, entry);
    }

    const affordances: Record<
      string,
      {
        count: number;
        unreadCount?: number | undefined;
        onPress: () => void;
        participants?:
          | ReadonlyArray<{ initials: string; imageUri?: string | undefined }>
          | undefined;
        lastReplierName?: string | undefined;
        preview?: string | undefined;
      }
    > = {};
    for (const [rootId, entry] of acc) {
      affordances[rootId] = {
        count: entry.count,
        ...(entry.unread > 0 ? { unreadCount: entry.unread } : {}),
        onPress: () => {
          // Open with the root's CONTENT HASH (what the gateway matches
          // replies against), not the message id — passing the id loaded
          // an empty thread (no reply's parent_id equals a message id).
          shellHandlers?.openThread(entry.rootContentHash as ThreadIdentifier);
        },
        // Order the avatar stack by recency so `participants[0]` (the avatar the
        // preview card renders) belongs to the SAME person as `lastReplierName`.
        // Insertion order put the FIRST replier first → in a group the avatar
        // and the name belonged to different people.
        participants: [
          entry.lastReplierName,
          ...Array.from(entry.participantNames).filter(
            (name) => name !== entry.lastReplierName,
          ),
        ].map((name) => ({
          initials: initialsFromName(name),
        })),
        lastReplierName: entry.lastReplierName,
        ...(entry.preview.length > 0 ? { preview: entry.preview } : {}),
      };
    }

    const timeline = messages.filter(
      (m) =>
        !replyIds.has(m.id) &&
        !gameUpdateIds.has(m.id) &&
        !joinRequestIds.has(m.id) &&
        !markerIds.has(m.id) &&
        // Duplicate call_end rows fold into their invite tile (one tile per call).
        !callTiles.suppressedCallEndIds.has(m.id) &&
        // Duplicate call_invite rows (re-invites for the same call) → one tile.
        !callTiles.suppressedInviteIds.has(m.id),
    );
    return {
      threadAffordances: affordances,
      timelineMessages: timeline,
      joinRequests,
      replyRootHashByMessageId,
    };
  }, [messages, isGroup, groupRosterMaps, headerTitle, shellHandlers, callTiles, threadReadAt]);

  // Overlay resolved reply-quote thumbnails onto the timeline's `inReplyTo`
  // (render-time only — blob URLs aren't persisted).
  const timelineWithReplyThumbs = useMemo<typeof timelineMessages>(() => {
    if (Object.keys(replyThumbByOriginalId).length === 0) return timelineMessages;
    return timelineMessages.map((m) => {
      const rid = m.inReplyTo?.messageId;
      const thumb = rid !== undefined ? replyThumbByOriginalId[rid] : undefined;
      if (thumb === undefined || m.inReplyTo === undefined) return m;
      return {
        ...m,
        inReplyTo: { ...m.inReplyTo, thumbnailUri: thumb.uri, thumbnailKind: thumb.kind },
      };
    });
  }, [timelineMessages, replyThumbByOriginalId]);

  // ── Jump-to-message (Activity feed → this conversation) ──────────────────
  // A surface (the Activity feed) may request that we reveal a specific
  // message after routing here. We resolve it once THIS conversation's
  // messages have loaded: a folded thread reply opens the thread panel on its
  // root; anything else scrolls the timeline to the row. The request survives
  // the route switch (module singleton) and the async message load — the
  // effect re-runs as `messages` streams in until it finds + consumes it.
  const [jumpTick, setJumpTick] = useState(0);
  useEffect(() => subscribeChatJump(() => setJumpTick((n) => n + 1)), []);
  useEffect(() => {
    const target = peekChatJump();
    if (target === null || target.conversationId !== conversationId) return;
    // No specific message → the conversation is already open; nothing to reveal.
    if (target.messageId === undefined || target.messageId.length === 0) {
      clearChatJump();
      return;
    }
    // Wait for the target row to stream in before acting (don't consume yet).
    const exists = messages.some((m) => m.id === target.messageId);
    if (!exists) return;
    const rootHash = replyRootHashByMessageId.get(target.messageId);
    if (rootHash !== undefined && rootHash.length > 0 && shellHandlers?.openThread) {
      // Folded thread reply → open the thread panel anchored on its root.
      shellHandlers.openThread(rootHash as ThreadIdentifier);
    } else {
      // Timeline row → scroll + highlight it (defer a frame for layout).
      const id = target.messageId;
      requestAnimationFrame(() => scrollToMessageRef.current?.(id));
    }
    clearChatJump();
  }, [jumpTick, conversationId, messages, replyRootHashByMessageId, shellHandlers]);

  // ── Group-join requests (owner-approval invite flow) ────────────────────
  // A `/g/<approver>/<groupId>` link lands a join request in THIS 1-1 (the peer
  // is the requester). Surface an Approve banner for the most recent request
  // RECEIVED from the peer (never our own sent one), until approved/dismissed.
  const [dismissedJoinRequestIds, setDismissedJoinRequestIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [approvingJoinRequest, setApprovingJoinRequest] = useState(false);
  const pendingJoinRequest = useMemo(() => {
    const received = joinRequests
      .filter((r) => !r.isSelf && !dismissedJoinRequestIds.has(r.id))
      .sort((a, b) => b.timestamp - a.timestamp);
    return received[0] ?? null;
  }, [joinRequests, dismissedJoinRequestIds]);
  // The approver resolves the group's display name from their local roster.
  const pendingJoinGroupName =
    pendingJoinRequest !== null
      ? allConversations.find((c) => c.id === pendingJoinRequest.groupId)?.name
      : undefined;

  const handleApproveJoinRequest = useCallback((): void => {
    if (pendingJoinRequest === null || client === null) return;
    const req = pendingJoinRequest;
    // The 1-1 peer IS the requester — trust that over the (cosmetic) payload name.
    const requesterAccountId = localConv?.peerAccountId ?? req.senderId;
    if (requesterAccountId.length === 0) {
      pingToast.error({ title: "Couldn't identify who to add." });
      return;
    }
    setApprovingJoinRequest(true);
    void (async () => {
      try {
        await addWebGroupMembers({
          client: client as unknown as Parameters<typeof addWebGroupMembers>[0]['client'],
          conversationId: req.groupId,
          members: [
            { accountId: requesterAccountId, displayName: req.requesterDisplayName ?? headerTitle },
          ],
        });
        setDismissedJoinRequestIds((prev) => new Set(prev).add(req.id));
        pingToast.success({ title: 'Added to the group' });
        // Best-effort confirmation back to the requester.
        try {
          const conv = await resolveConv();
          await conv?.send(
            encodeEnvelopeJsonBytes({
              kind: 'com.ping.text',
              payload: { body: "You've been added to the group ✅" },
            }),
          );
        } catch {
          /* non-fatal */
        }
        log.info({ event: 'chat.groupJoinRequest.approved', groupPrefix: req.groupId.slice(0, 8) });
      } catch (err) {
        pingToast.error({
          title: isEpochConflictError(err)
            ? EPOCH_DESYNC_MESSAGE
            : "Couldn't add them to the group. Try again.",
        });
        log.warn({ event: 'chat.groupJoinRequest.approve.failed', err: (err as Error).message });
      } finally {
        setApprovingJoinRequest(false);
      }
    })();
  }, [pendingJoinRequest, client, localConv?.peerAccountId, headerTitle, resolveConv]);

  const handleDismissJoinRequest = useCallback((): void => {
    if (pendingJoinRequest === null) return;
    setDismissedJoinRequestIds((prev) => new Set(prev).add(pendingJoinRequest.id));
  }, [pendingJoinRequest]);

  // Compute per-message variants from envelope-shaped `body` strings.
  // Sender-side writes the full envelope JSON via `sendVoiceMessage`;
  // receiver-side `useIncomingMessages` (shared, packages/ui) persists
  // the same envelope JSON for `com.ping.voice` so both directions hit
  // this branch and dispatch the right `ChatMessageVariant`.
  useEffect(() => {
    let cancelled = false;
    for (const m of messages) {
      // Desktop inline images: decode base64 → Blob → object URL
      // directly (no S3 fetch). Handled before the blob_ref branch
      // because the two share the `com.ping.image` kind.
      const inline = parseInlineImageEnvelope(m.body);
      if (inline !== undefined) {
        try {
          const blob = base64ToBlob(inline.bytes, inline.mime);
          const url = URL.createObjectURL(blob);
          setImagePreviewByMessageId((prev) => {
            if (prev[m.id] !== undefined) {
              URL.revokeObjectURL(url);
              return prev;
            }
            if (cancelled) {
              URL.revokeObjectURL(url);
              return prev;
            }
            return { ...prev, [m.id]: url };
          });
        } catch (err) {
          log.warn({
            event: 'chatroom.image.inline.decode.failed',
            messageId: m.id,
            err: (err as Error).message,
          });
        }
        continue;
      }
      const payload = parseImageEnvelope(m.body);
      if (payload === undefined) continue;
      // Skip pending local-echo placeholders (sentinel blob_ref) — their
      // thumbnail is the staged preview already in the map; the real
      // blob_ref envelope is patched in post-upload (the `.then` guard
      // below also dedupes if a preview is already present).
      if (payload.blob_ref.media_id === 'pending') continue;
      void fetchAndDecryptMediaBlob(payload.blob_ref)
        .then((blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          setImagePreviewByMessageId((prev) => {
            if (prev[m.id] !== undefined) {
              URL.revokeObjectURL(url);
              return prev;
            }
            return { ...prev, [m.id]: url };
          });
        })
        .catch((err) => {
          if (cancelled) return;
          log.warn({
            event: 'chatroom.image.fetch.failed',
            messageId: m.id,
            err: (err as Error).message,
          });
          // Surface a failed state so the tile shows Retry, not a stuck shimmer.
          setFailedImageIds((prev) => {
            if (prev.has(m.id)) return prev;
            const next = new Set(prev);
            next.add(m.id);
            return next;
          });
        });
    }
    return () => {
      cancelled = true;
    };
  }, [messages]);

  // Explicit Retry for a received image that failed to resolve — clears the
  // failed flag and re-attempts the fetch+decrypt (re-minting a fresh presigned
  // GET happens inside `fetchAndDecryptMediaBlob`).
  const handleImageRetry = useCallback((message: Message) => {
    const payload = parseImageEnvelope(message.body);
    if (payload === undefined) return;
    setFailedImageIds((prev) => {
      if (!prev.has(message.id)) return prev;
      const next = new Set(prev);
      next.delete(message.id);
      return next;
    });
    void fetchAndDecryptMediaBlob(payload.blob_ref)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        setImagePreviewByMessageId((prev) => {
          if (prev[message.id] !== undefined) {
            URL.revokeObjectURL(url);
            return prev;
          }
          return { ...prev, [message.id]: url };
        });
      })
      .catch((err) => {
        log.warn({
          event: 'chatroom.image.retry.failed',
          messageId: message.id,
          err: (err as Error).message,
        });
        setFailedImageIds((prev) => {
          if (prev.has(message.id)) return prev;
          const next = new Set(prev);
          next.add(message.id);
          return next;
        });
      });
  }, []);

  // Retry a FAILED media UPLOAD (image / file / video) — re-runs the full
  // encrypt + upload + send from the cached source `File`, reusing the SAME
  // local-echo row id so the tile flips failed → uploading → sent in place.
  // The per-helper failure path re-marks the row failed if the retry also
  // fails, so the "Couldn't send. Try again." affordance returns. When the
  // source is gone (post-reload cache miss) we can't re-upload, so guide the
  // user to re-attach instead of silently doing nothing.
  const handleMediaRetry = useCallback(
    (message: Message): void => {
      const cached = mediaRetrySourceRef.current.get(message.id);
      if (cached === undefined) {
        pingToast.error({
          title: "Couldn't retry the upload",
          description: 'Re-attach the file to send it again.',
        });
        return;
      }
      void (async () => {
        const conv = await resolveConv();
        if (conv === null) {
          pingToast.error({
            title: 'Not connected',
            description: 'Try again in a moment.',
          });
          return;
        }
        // Flip failed → pending so the tile shows the uploading state again.
        await webMessagesPersister
          .update(message.id, { deliveryState: 'pending' })
          .catch(() => undefined);
        setUploadProgress(message.id, 0);
        try {
          if (cached.kind === 'image') {
            const source = await fileToImageSource(cached.file);
            const { messageId, previewObjectUrl } = await sendImageMessage({
              conv,
              conversationId,
              bundle: bundleForSource(source),
              source,
              localId: message.id,
              ...(cached.caption !== undefined ? { caption: cached.caption } : {}),
              onProgress: (f: number) => setUploadProgress(message.id, f),
            });
            setImagePreviewByMessageId((prev) => ({ ...prev, [messageId]: previewObjectUrl }));
          } else if (cached.kind === 'file') {
            await sendFileMessage({
              conv,
              conversationId,
              file: cached.file,
              localId: message.id,
              ...(cached.caption !== undefined ? { caption: cached.caption } : {}),
              onProgress: (f: number) => setUploadProgress(message.id, f),
            });
          } else {
            const { messageId, videoObjectUrl, posterObjectUrl } = await sendVideoMessage({
              conv,
              conversationId,
              file: cached.file,
              localId: message.id,
              ...(cached.probed !== undefined ? { probed: cached.probed } : {}),
              ...(cached.caption !== undefined ? { caption: cached.caption } : {}),
              onProgress: (f: number) => setUploadProgress(message.id, f),
            });
            setVideoUriByMessageId((prev) => ({ ...prev, [messageId]: videoObjectUrl }));
            if (posterObjectUrl !== undefined) {
              setPosterUriByMessageId((prev) => ({ ...prev, [messageId]: posterObjectUrl }));
            }
          }
          clearUploadProgress(message.id);
          mediaRetrySourceRef.current.delete(message.id);
        } catch (err) {
          clearUploadProgress(message.id);
          // The send helper already re-marked the row failed → the retry
          // affordance returns.
          log.error({ event: 'chatroom.media.retry.failed', err: (err as Error).message });
          pingToast.error({
            title: "Couldn't send",
            description: 'Check your connection and try again.',
          });
        }
      })();
    },
    [conversationId, resolveConv, setUploadProgress, clearUploadProgress],
  );
  // Wire the ref the message-level `handleRetry` reads (defined earlier).
  handleMediaRetryRef.current = handleMediaRetry;

  // ── Poll-vote projection. Maintains a live OR-set tally per poll
  // (keyed by the poll message's contentHash) by subscribing to the
  // Dexie-backed `webPollVotesPersister` for every poll message in the
  // conversation. Mirrors the reactions projection but per-poll.
  const [pollVotesByTarget, setPollVotesByTarget] = useState<
    Record<
      string,
      {
        counts: Record<number, number>;
        myVote?: number;
        // Full multi-select set the local account holds (all option indices).
        myVotes?: number[];
        total: number;
        // Per-option voter account ids → resolved to avatars at render.
        votersByOption: Record<number, string[]>;
      }
    >
  >({});
  // Poll whose "View votes" panel is open (keyed by poll target/contentHash).
  const [viewVotesPollTarget, setViewVotesPollTarget] = useState<string | null>(null);
  useEffect(() => {
    const targets = messages
      .filter((m) => isPollMessage(m.body))
      .map((m) => m.contentHash)
      .filter((h): h is string => !!h && h.length > 0);
    if (targets.length === 0) return;
    const me = localAccountId ?? undefined;
    const apply = (target: string, rows: ReadonlyArray<{ actorAccountId: string; optionIndex: number }>) => {
      const counts: Record<number, number> = {};
      const votersByOption: Record<number, string[]> = {};
      const myVotes: number[] = [];
      const actors = new Set<string>();
      for (const r of rows) {
        counts[r.optionIndex] = (counts[r.optionIndex] ?? 0) + 1;
        (votersByOption[r.optionIndex] ??= []).push(r.actorAccountId);
        actors.add(r.actorAccountId);
        if (me !== undefined && r.actorAccountId === me) myVotes.push(r.optionIndex);
      }
      // `myVote` (single, back-compat) = first of the local account's rows;
      // `myVotes` = the full multi-select set.
      const myVote = myVotes.length > 0 ? myVotes[0] : undefined;
      setPollVotesByTarget((prev) => ({
        ...prev,
        [target]: {
          counts,
          total: actors.size,
          votersByOption,
          ...(myVote !== undefined ? { myVote } : {}),
          ...(myVotes.length > 0 ? { myVotes } : {}),
        },
      }));
    };
    const unsubs = targets.map((target) => {
      void webPollVotesPersister.listByPoll(target).then((rows) => apply(target, rows));
      return webPollVotesPersister.subscribe?.(target, (rows) => apply(target, rows)) ?? (() => {});
    });
    return () => unsubs.forEach((u) => u());
  }, [messages, localAccountId]);

  // ── Meetings (§2.13) — fold captured events into live card state ──────────
  // Every `com.ping.meeting*` event for this conversation is captured into
  // `webMeetingEventsPersister` — peers via `useIncomingMessages`, our own via
  // the optimistic self-writes below — keyed on the MLS-AUTHENTICATED sender
  // account. We fold them through the shared `reduceMeeting` (creator-authority
  // / LWW / absorbing-cancel / RSVP-LWW) to derive the live card state.
  const [meetingState, setMeetingState] = useState<MeetingState>(EMPTY_MEETING_STATE);
  const [editingMeeting, setEditingMeeting] = useState<{
    meetingId: string;
    initial: MeetingModalInitial;
  } | null>(null);
  // Device-local meeting reminders (§2.13) for THIS conversation → the card's
  // reminder row (creator's own "remind me" choice; not on the wire). The
  // AppShell-mounted scheduler owns actually firing them.
  const [meetingReminderMinutes, setMeetingReminderMinutes] = useState<Record<string, number>>({});
  useEffect(() => {
    const project = (rows: ReadonlyArray<{ meetingId: string; conversationId: string; reminderMinutes: number }>): void => {
      const next: Record<string, number> = {};
      for (const r of rows) {
        if (r.conversationId === conversationId) next[r.meetingId] = r.reminderMinutes;
      }
      setMeetingReminderMinutes(next);
    };
    void webMeetingRemindersPersister.list().then(project);
    return webMeetingRemindersPersister.subscribe(project);
  }, [conversationId]);
  // Fold the live card state from the MESSAGES table — exactly like games
  // (`persistedGameEvents`). All four `com.ping.meeting*` kinds persist as
  // full-envelope rows (the create renders the card; `_update`/`_rsvp`/`_cancel`
  // are filtered out of the visible timeline via `meetingUpdateIds` above), so
  // BOTH our own echoes and peer events reach every member through the same
  // reliable delivery path text/edits/games use — the old separate-table capture
  // never propagated RSVP/edit to peers. Attribution: our OWN rows → our account
  // (so the creator gate + RSVP-LWW hold and the creator's card shows their
  // authority); peer rows → the sender's account resolved from the group roster
  // (so the responses list maps each voter to a name + avatar).
  const persistedMeetingState = useMemo<MeetingState>(() => {
    const wireToKind = (kind: string): MeetingEvent['kind'] | undefined =>
      kind === 'com.ping.meeting'
        ? 'create'
        : kind === 'com.ping.meeting_update'
          ? 'update'
          : kind === 'com.ping.meeting_rsvp'
            ? 'rsvp'
            : kind === 'com.ping.meeting_cancel'
              ? 'cancel'
              : undefined;
    const rows: Array<{
      kind: MeetingEvent['kind'];
      body: unknown;
      senderAccountId: string;
      at: number;
      id: string;
    }> = [];
    for (const m of messages) {
      if (m.body === undefined || m.body.length === 0 || m.body.charAt(0) !== '{') continue;
      const parsed = parseEnvelope(m.body);
      if (parsed === undefined || !('body' in parsed)) continue;
      const kind = wireToKind(parsed.kind);
      if (kind === undefined) continue;
      const senderAccountId = m.isSelf
        ? (localAccountId ?? '__self__')
        : (resolveGroupSenderAccountId(m.senderId, groupRosterMaps) ?? m.senderId);
      rows.push({
        kind,
        body: (parsed as { body: unknown }).body,
        senderAccountId,
        at: m.timestamp,
        id: m.id,
      });
    }
    // Fold oldest-first so a create establishes the record before its sidecars;
    // the reducer's LWW handles genuinely out-of-order events.
    rows.sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
    let state = EMPTY_MEETING_STATE;
    for (const r of rows) {
      state = reduceMeeting(
        state,
        { kind: r.kind, body: r.body } as unknown as MeetingEvent,
        { senderAccountId: r.senderAccountId, at: r.at },
      );
    }
    return state;
  }, [messages, localAccountId, groupRosterMaps]);
  useEffect(() => {
    setMeetingState(persistedMeetingState);
  }, [persistedMeetingState]);

  // Send a meeting sidecar (rsvp / update / cancel) + optimistically fold OUR
  // OWN event locally (self-sends don't echo via onMessage), keyed on our
  // authenticated account so the creator-authority / RSVP-LWW rules hold.
  const sendMeetingSidecar = useCallback(
    async (
      kind: 'update' | 'rsvp' | 'cancel',
      meetingId: string,
      payload: Record<string, unknown>,
    ): Promise<void> => {
      const identity = await retrieveIdentity();
      const wireKind: 'com.ping.meeting_update' | 'com.ping.meeting_rsvp' | 'com.ping.meeting_cancel' =
        kind === 'update'
          ? 'com.ping.meeting_update'
          : kind === 'rsvp'
            ? 'com.ping.meeting_rsvp'
            : 'com.ping.meeting_cancel';
      const envelopeBody = { kind: wireKind, payload };
      // Send to the group. Best-effort: a failure still leaves the local echo so
      // the sender sees their own choice.
      const conv = await resolveConv();
      let sent: { content_hash: Uint8Array; epoch: number | bigint } | null = null;
      if (conv !== null) {
        try {
          sent = (await conv.send(encodeEnvelopeJsonBytes(envelopeBody))) as {
            content_hash: Uint8Array;
            epoch: number | bigint;
          };
        } catch (err) {
          log.warn({ event: 'chatroom.meeting.sidecar.send.failed', err: (err as Error).message });
        }
      }
      // Persist a LOCAL message-row echo of OUR OWN sidecar — self-sends never
      // round-trip via onMessage, so without this our own RSVP/edit/cancel would
      // not fold into `meetingState`. Mirrors the game-move echo; the row is
      // filtered out of the visible timeline by `meetingUpdateIds` and folds into
      // the card via `persistedMeetingState`.
      if (identity) {
        const sentAt = Date.now();
        await sharedPersistSentMessage({
          messages: webMessagesPersister,
          conversations: webConversationsPersister,
          record: {
            id:
              typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `local-meeting-${kind}-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
            conversationId,
            epoch:
              sent !== null
                ? (typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch)
                : 0,
            serverSeq: sentAt,
            contentHash:
              sent !== null
                ? bytesToHexLocal(sent.content_hash)
                : `local-meeting-${kind}-${meetingId}-${sentAt}`,
            senderAccountId: identity.accountId,
            body: JSON.stringify(envelopeBody),
            sentAt,
            deliveryState: sent !== null ? 'sent' : 'failed',
          },
        });
      }
    },
    [conversationId, resolveConv],
  );

  const handleMeetingRsvp = useCallback(
    (meetingId: string, response: MeetingRsvpResponse): void => {
      void sendMeetingSidecar('rsvp', meetingId, buildMeetingRsvpPayload(meetingId, response)).catch(
        (err) => log.warn({ event: 'chatroom.meeting.rsvp.failed', err: (err as Error).message }),
      );
    },
    [sendMeetingSidecar],
  );

  const handleMeetingCancel = useCallback(
    (meetingId: string): void => {
      void sendMeetingSidecar('cancel', meetingId, buildMeetingCancelPayload(meetingId)).catch(
        (err) => log.warn({ event: 'chatroom.meeting.cancel.failed', err: (err as Error).message }),
      );
      // A canceled meeting has nothing to remind about — drop the local reminder.
      void webMeetingRemindersPersister.remove(meetingId).catch(() => undefined);
    },
    [sendMeetingSidecar],
  );

  const handleMeetingEdit = useCallback(
    (meetingId: string): void => {
      const rec = meetingState.meetings[meetingId];
      if (rec === undefined) return;
      setEditingMeeting({
        meetingId,
        initial: {
          kind: rec.autoCall !== undefined ? 'call' : 'meeting',
          title: rec.title,
          ...(rec.description !== undefined ? { description: rec.description } : {}),
          startsAt: rec.startsAt,
          endsAt: rec.endsAt,
          ...(rec.location !== undefined ? { location: rec.location } : {}),
          ...(rec.meetingLink !== undefined ? { meetingLink: rec.meetingLink } : {}),
          ...(rec.autoCall !== undefined ? { autoCallMedia: rec.autoCall.media } : {}),
          // Prefill the creator's existing device-local reminder (else the modal
          // would silently reset it to its 30-min default on every edit). Absent
          // → the modal shows "None" in edit mode (mode-aware default).
          ...(meetingReminderMinutes[meetingId] !== undefined
            ? { reminderMinutes: meetingReminderMinutes[meetingId] }
            : {}),
        },
      });
    },
    [meetingState, meetingReminderMinutes],
  );

  const submitMeetingEdit = useCallback(
    (meetingId: string, draft: MeetingDraft): void => {
      // Send the editable fields as a partial diff (empty string clears an
      // optional; the reducer applies idempotently under the creator-only gate).
      void sendMeetingSidecar(
        'update',
        meetingId,
        buildMeetingUpdatePayload({
          meetingId,
          title: draft.title,
          startsAt: draft.startsAt,
          endsAt: draft.endsAt,
          location: draft.location ?? '',
          meetingLink: draft.meetingLink ?? '',
          description: draft.description ?? '',
        }),
      ).catch((err) =>
        log.warn({ event: 'chatroom.meeting.update.failed', err: (err as Error).message }),
      );
      // Reschedule (or clear) the creator's device-local reminder to match the
      // edited time / choice. `firedAt` is reset (fresh row) so a moved-later
      // meeting can fire again.
      if (draft.reminderMinutes !== undefined && draft.startsAt > Date.now()) {
        void webMeetingRemindersPersister
          .put({
            meetingId,
            conversationId,
            title: draft.title,
            startsAt: draft.startsAt,
            reminderMinutes: draft.reminderMinutes,
          })
          .catch(() => undefined);
      } else {
        void webMeetingRemindersPersister.remove(meetingId).catch(() => undefined);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sendMeetingSidecar],
  );

  const handleOpenMeetingLink = useCallback((url: string): void => {
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      /* best-effort */
    }
  }, []);

  const handleCopyMeetingLink = useCallback((url: string): void => {
    try {
      void navigator.clipboard?.writeText(url);
    } catch {
      /* best-effort */
    }
  }, []);

  // Join / start the Ping call attached to a `call`-type meeting — reuses the
  // header call-launch path (same conversation, peers, title, avatar) with the
  // meeting's configured media.
  // Responses sheet — which meeting's RSVP list is open, resolved to
  // account-keyed display names + avatars from the group roster.
  const [responsesMeetingId, setResponsesMeetingId] = useState<string | null>(null);
  const meetingResponses = useMemo(() => {
    const groups: {
      yes: Array<{ accountId: string; name: string; avatarUri?: string }>;
      maybe: Array<{ accountId: string; name: string; avatarUri?: string }>;
      no: Array<{ accountId: string; name: string; avatarUri?: string }>;
    } = { yes: [], maybe: [], no: [] };
    if (responsesMeetingId === null) return groups;
    const byVoter = meetingState.rsvps[responsesMeetingId] ?? {};
    const names = localConv?.memberDisplayNames ?? {};
    const avatars = localConv?.memberAvatarUris ?? {};
    for (const rsvp of Object.values(byVoter)) {
      const acct = rsvp.voterAccountId;
      const name = acct === localAccountId ? 'You' : (names[acct] ?? 'Member');
      const avatarUri = avatars[acct];
      groups[rsvp.response].push({ accountId: acct, name, ...(avatarUri !== undefined ? { avatarUri } : {}) });
    }
    return groups;
  }, [responsesMeetingId, meetingState, localAccountId, localConv?.memberDisplayNames, localConv?.memberAvatarUris]);
  const responsesTitle =
    responsesMeetingId !== null ? (meetingState.meetings[responsesMeetingId]?.title ?? 'Meeting') : '';

  const handleJoinMeetingCall = useCallback(
    (media: 'audio' | 'video'): void => {
      void useCallStore.getState().startCall({
        conversationId,
        media,
        title: headerTitle,
        isGroup,
        peerAccountIds: callPeerIds,
        peerDisplayNames: callPeerNames,
        ...(headerAvatarUri !== undefined ? { avatarUri: headerAvatarUri } : {}),
      });
    },
    [conversationId, headerTitle, isGroup, callPeerIds, callPeerNames, headerAvatarUri],
  );

  // Live mirrors so `handlePollVote` can read the CURRENT projection + poll
  // bodies (to resolve multi_select + toggle the actor's set) without
  // re-creating the callback on every projection tick.
  const pollVotesByTargetRef = useRef(pollVotesByTarget);
  useEffect(() => {
    pollVotesByTargetRef.current = pollVotesByTarget;
  }, [pollVotesByTarget]);
  const pollMessagesRef = useRef(messages);
  useEffect(() => {
    pollMessagesRef.current = messages;
  }, [messages]);

  const handlePollVote = useCallback(
    (pollTarget: string, optionIndex: number): void => {
      const me = localAccountIdRef.current ?? '__local__';
      const now = Date.now();
      // Resolve whether this poll is multi-select from its create body
      // (the poll message whose contentHash / id is `pollTarget`).
      const pollMsg = pollMessagesRef.current.find(
        (m) => m.contentHash === pollTarget || m.id === pollTarget,
      );
      const multiSelect =
        pollMsg !== undefined && parsePollBody(pollMsg.body)?.multi_select === true;

      // Compute the actor's NEXT full option set. Single-choice replaces
      // with exactly `[optionIndex]`; multi-select toggles membership.
      let nextSet: number[];
      if (multiSelect) {
        const current = pollVotesByTargetRef.current[pollTarget]?.myVotes ?? [];
        nextSet = current.includes(optionIndex)
          ? current.filter((i) => i !== optionIndex)
          : [...current, optionIndex];
      } else {
        nextSet = [optionIndex];
      }

      // Optimistic local OR-set replace (atomic delete-then-insert).
      if (webPollVotesPersister.replaceActorVotes !== undefined) {
        void webPollVotesPersister.replaceActorVotes(pollTarget, me, nextSet, now);
      } else {
        void (async () => {
          await webPollVotesPersister.deleteByActor(pollTarget, me);
          for (const idx of nextSet) {
            await webPollVotesPersister.put({
              id: `${pollTarget}:${me}:${idx}`,
              pollTarget,
              actorAccountId: me,
              optionIndex: idx,
              votedAt: now,
            });
          }
        })();
      }

      void (async () => {
        try {
          const conv = await resolveConv();
          if (conv === null) return;
          await conv.send(
            encodeEnvelopeJsonBytes({
              kind: 'com.ping.poll_vote',
              // Multi-select ships the FULL set in `choices`; single-choice
              // additionally carries `option_index` for back-compat.
              payload: multiSelect
                ? { target: pollTarget, choices: nextSet, actorAccountId: me }
                : {
                    target: pollTarget,
                    choices: nextSet,
                    option_index: optionIndex,
                    actorAccountId: me,
                  },
            }),
          );
        } catch (err) {
          log.warn({ event: 'chatroom.poll.vote.failed', err: (err as Error).message });
        }
      })();
    },
    [resolveConv],
  );

  const messageVariants = useMemo<Record<string, ChatMessageVariant>>(() => {
    const out: Record<string, ChatMessageVariant> = {};
    for (const m of messages) {
      const body = m.body;
      if (body.length === 0 || body.charAt(0) !== '{') continue;
      const voice = parseVoiceEnvelope(body);
      if (voice !== undefined) {
        out[m.id] = {
          kind: 'voice',
          state: m.isSelf ? 'sent' : 'received',
          durationMs: voice.durationMs,
          // Placeholder waveform — overridden by the shared
          // `useVoicePlayback` hook once `extractWaveform` decodes the
          // real amplitude buckets via AudioContext.
          waveform: PLACEHOLDER_WAVEFORM,
          progress: 0,
          // Legacy no-op — replaced live by the shared hook when the
          // <VoicePlaybackProvider> resolves a driver.
          onTogglePlay: () => {
            log.info({ event: 'chatroom.voice.togglePlay', messageId: m.id });
          },
          // Opt-in shared playback path. Both web (Blob URL minted
          // from the base64 envelope) and macOS-base64 paths flow
          // through the same driver via `VoiceSource`.
          resolveVoiceSource: resolveWebVoiceSource,
          // Record-time transcript (Web Speech), when the sender's
          // client captured one. Rides in the `com.ping.voice` envelope.
          ...(voice.transcript !== undefined && voice.transcript.length > 0
            ? { transcript: voice.transcript }
            : {}),
          // Surface a visible error instead of failing silently when the
          // browser can't decode the note's codec / autoplay is blocked.
          onPlaybackError: () => {
            pingToast.error({
              title: "Couldn't play voice note",
              description: 'This audio format may be unsupported on your browser.',
            });
          },
        };
        continue;
      }
      const gif = parseGifEnvelope(body);
      if (gif !== undefined) {
        // Render GIFs through the existing `ImageAttachmentCard`
        // primitive — same chrome (300w card, radius 16, padding 2)
        // as static-image attachments. The `imageUri` carries the
        // Tenor CDN URL; the receiver fetches it directly. We do
        // NOT bake the GIF bytes into the MLS envelope (too large) —
        // the CDN URL is opaque w.r.t. account identity.
        const ratio = gif.width > 0 && gif.height > 0 ? gif.width / gif.height : 1;
        out[m.id] = {
          kind: 'image',
          state: 'uploaded',
          aspectRatio: ratio,
          imageUri: gif.url,
          // Stickers render with no bubble/background chrome — just the image.
          ...(gif.sticker === true ? { isSticker: true } : {}),
          onTapOpen: () => {
            log.info({ event: 'chatroom.gif.tap', messageId: m.id });
          },
        };
        continue;
      }
      // Desktop inline-image envelopes (`bytes` base64, no `blob_ref`).
      // The fetch effect above mints the object URL into
      // `imagePreviewByMessageId`; here we resolve dims + state the
      // same way the blob_ref branch does.
      const inlineImage = parseInlineImageEnvelope(body);
      if (inlineImage !== undefined) {
        const ratio =
          inlineImage.w > 0 && inlineImage.h > 0
            ? inlineImage.w / inlineImage.h
            : 1;
        const previewUri = imagePreviewByMessageId[m.id];
        const progress = uploadProgressByMessageId[m.id];
        const uploading = m.deliveryState === 'pending';
        // A failed UPLOAD → 'failed' (the "Couldn't send. Try again." /
        // "Resend image" affordance + a working re-upload), NOT a phantom
        // 'uploaded'/'loading' tile. 'uploading' ONLY while genuinely sending
        // (pending); a sent image whose blob URL hasn't resolved yet (e.g.
        // right after a reload) is 'loading' → shimmer skeleton.
        const sendFailed = m.deliveryState === 'failed';
        const imageState = sendFailed
          ? 'failed'
          : uploading
            ? 'uploading'
            : previewUri !== undefined
              ? 'uploaded'
              : 'loading';
        out[m.id] = {
          kind: 'image',
          state: imageState,
          aspectRatio: ratio,
          ...(uploading ? { progress } : {}),
          ...(previewUri !== undefined ? { imageUri: previewUri } : {}),
          onTapOpen: () => {
            if (previewUri !== undefined) setPreviewMediaId(m.id);
          },
          ...(sendFailed ? { onRetry: () => handleMediaRetry(m) } : {}),
        };
        continue;
      }
      const image = parseImageEnvelope(body);
      if (image !== undefined) {
        const ratio = image.w > 0 && image.h > 0 ? image.w / image.h : 1;
        const previewUri = imagePreviewByMessageId[m.id];
        const progress = uploadProgressByMessageId[m.id];
        // Outgoing local-echo still in-flight: drive the explicit
        // 'uploading' state + live % off the host's progress map. Once
        // `commitSentMessage` flips `deliveryState` off 'pending' (and
        // the progress entry is cleared) the bubble settles to
        // 'uploaded' with the export-blob preview.
        const uploading = m.deliveryState === 'pending';
        // Two distinct failure modes both surface the 'failed' tile:
        //  • SEND failure (own upload threw → deliveryState 'failed') → retry
        //    re-runs the encrypt+upload+send (`handleMediaRetry`).
        //  • DOWNLOAD failure (received image fetch+decrypt failed) → retry
        //    re-fetches the blob (`handleImageRetry`). Only when no preview
        //    has resolved (a late success clears it).
        const sendFailed = m.deliveryState === 'failed';
        const downloadFailed = previewUri === undefined && failedImageIds.has(m.id);
        const failed = sendFailed || downloadFailed;
        const imageState = uploading
          ? 'uploading'
          : failed
            ? 'failed'
            : previewUri !== undefined
              ? 'uploaded'
              : 'loading';
        out[m.id] = {
          kind: 'image',
          state: imageState,
          aspectRatio: ratio,
          ...(uploading ? { progress } : {}),
          ...(image.caption !== undefined ? { caption: image.caption } : {}),
          ...(previewUri !== undefined ? { imageUri: previewUri } : {}),
          onTapOpen: () => {
            if (previewUri !== undefined) setPreviewMediaId(m.id);
          },
          ...(failed
            ? { onRetry: () => (sendFailed ? handleMediaRetry(m) : handleImageRetry(m)) }
            : {}),
        };
        continue;
      }
      const video = parseVideoEnvelope(body);
      if (video !== undefined) {
        const ratio = video.w > 0 && video.h > 0 ? video.w / video.h : 9 / 16;
        const posterUri = posterUriByMessageId[m.id];
        const videoUri = videoUriByMessageId[m.id];
        const progress = uploadProgressByMessageId[m.id];
        const uploading = m.deliveryState === 'pending';
        // Failed UPLOAD → 'failed' tile ("Resend video" + working re-upload).
        const sendFailed = m.deliveryState === 'failed';
        const downloadProgress = videoDownloadProgressById[m.id];
        const downloading = downloadProgress !== undefined && videoUri === undefined;
        out[m.id] = {
          kind: 'video',
          state: sendFailed ? 'failed' : uploading ? 'uploading' : 'uploaded',
          aspectRatio: ratio,
          mimeType: video.mime,
          ...(video.dur_ms > 0 ? { durationLabel: formatVideoDuration(video.dur_ms) } : {}),
          ...(video.caption !== undefined ? { caption: video.caption } : {}),
          ...(uploading ? { progress } : {}),
          ...(downloading ? { downloading: true, downloadProgress } : {}),
          ...(posterUri !== undefined ? { posterUri } : {}),
          ...(videoUri !== undefined ? { videoUri } : {}),
          // Lazily fetch + decrypt the full blob on first play (received
          // videos only — the sender already has the local object URL).
          onTapOpen: () => {
            void resolveVideoBlob(m.id, body);
          },
          onDownload: () => {
            void downloadVideoBlob(m.id, body);
          },
          // Fullscreen control opens the shared MediaPreviewScreen (so the
          // inline player in the timeline stays intact). Resolve the blob
          // first so the preview can play it.
          onFullscreen: () => {
            void resolveVideoBlob(m.id, body);
            setPreviewMediaId(m.id);
          },
          ...(sendFailed ? { onRetry: () => handleMediaRetry(m) } : {}),
        };
        continue;
      }
      const poll = parsePollBody(body);
      if (poll !== undefined) {
        const target = m.contentHash && m.contentHash.length > 0 ? m.contentHash : m.id;
        const tally =
          pollVotesByTarget[target] ?? { counts: {}, total: 0, votersByOption: {} };
        const creatorName = m.isSelf
          ? 'You'
          : senderNames?.[m.senderId] ?? headerTitle;
        // Resolve per-option voter account ids → avatar descriptors + names.
        const resolvedVoters: Record<
          number,
          ReadonlyArray<{ initials: string; avatarUrl?: string; name: string }>
        > = {};
        for (const [idx, ids] of Object.entries(tally.votersByOption)) {
          resolvedVoters[Number(idx)] = ids.map(resolvePollVoter);
        }
        out[m.id] = {
          kind: 'poll',
          poll: {
            pollTarget: target,
            question: poll.question,
            options: poll.options,
            multipleChoice: poll.multi_select === true,
            // Anonymous polls suppress the voter stack + hide names in the panel.
            ...(poll.anonymous === true ? { anonymous: true } : {}),
            ...(poll.closes_at !== undefined ? { closesAt: poll.closes_at } : {}),
            creatorName,
            createdAt: m.timestamp,
          },
          counts: tally.counts,
          ...(tally.myVote !== undefined ? { myVote: tally.myVote } : {}),
          ...(tally.myVotes !== undefined ? { myVotes: tally.myVotes } : {}),
          total: tally.total,
          votersByOption: resolvedVoters,
          // "View votes" → open the shared Poll votes panel for this poll.
          onViewVotes: () => setViewVotesPollTarget(target),
          onVote: (optionIndex: number) => handlePollVote(target, optionIndex),
        };
        continue;
      }
      // Registry-driven fallback — every kind beyond the bespoke
      // voice / gif / image bridges above is dispatched off the
      // AppEvent envelope per `docs/architecture/types-and-schemas.md`
      // §2. The registry returns `undefined` for plain text / sidecar
      // / unrenderable kinds so the bubble stays the default text row.
      const docPreview = docPreviews[m.id];
      // Meeting card live context (§2.13) — from the folded `meetingState`. Only
      // supplied once the create has folded; otherwise the card renders
      // display-only from the create body.
      let meetingCtx: Parameters<typeof deriveVariantFromBody>[1]['meeting'];
      // Cheap prefilter: only meeting-create bodies need the extra parse (avoids
      // a second JSON.parse for every non-meeting message in the memo).
      if (body !== undefined && body.includes('"com.ping.meeting"')) {
        const mparsed = parseEnvelope(body);
        if (mparsed?.kind === 'com.ping.meeting') {
          const meetingId = (mparsed.body as { meeting_id?: string }).meeting_id;
          const rec = meetingId !== undefined ? meetingState.meetings[meetingId] : undefined;
          if (meetingId !== undefined && rec !== undefined) {
            const counts = meetingRsvpCounts(meetingState, meetingId);
            const mine =
              localAccountId !== null ? meetingMyRsvp(meetingState, meetingId, localAccountId) : undefined;
            const isCreator = localAccountId !== null && rec.creatorAccountId === localAccountId;
            meetingCtx = {
              status: meetingStatus(rec, Date.now()),
              counts,
              ...(mine !== undefined ? { myRsvp: mine } : {}),
              isCreator,
              title: rec.title,
              startsAt: rec.startsAt,
              endsAt: rec.endsAt,
              ...(rec.location !== undefined ? { location: rec.location } : {}),
              ...(rec.description !== undefined ? { description: rec.description } : {}),
              ...(rec.meetingLink !== undefined ? { meetingLink: rec.meetingLink } : {}),
              ...(meetingReminderMinutes[meetingId] !== undefined
                ? { reminderMinutes: meetingReminderMinutes[meetingId] }
                : {}),
              ...(rec.autoCall !== undefined ? { autoCall: { media: rec.autoCall.media } } : {}),
              onRsvp: (response) => handleMeetingRsvp(meetingId, response),
              onOpenLink: handleOpenMeetingLink,
              onCopyLink: handleCopyMeetingLink,
              onShowResponses: () => setResponsesMeetingId(meetingId),
              ...(rec.autoCall !== undefined
                ? { onJoinCall: (media: 'audio' | 'video') => handleJoinMeetingCall(media) }
                : {}),
              ...(isCreator
                ? {
                    onEdit: () => handleMeetingEdit(meetingId),
                    onCancelMeeting: () => handleMeetingCancel(meetingId),
                  }
                : {}),
            };
          }
        }
      }
      const variant = deriveVariantFromBody(body, {
        message: m,
        ...(meetingCtx !== undefined ? { meeting: meetingCtx } : {}),
        doc: {
          // First-page thumbnail (when rendered) → wide page-preview tile.
          ...(docPreview?.thumbUri !== undefined && docPreview.thumbUri.length > 0
            ? { previewUri: docPreview.thumbUri }
            : {}),
          // Tap opens the in-app reader for ANY doc type (lazy-fetching the
          // blob when there's no eager thumbnail); falls back to download.
          onTapPreview: () => handleDocTap(m.id, body),
        },
      });
      if (variant !== undefined) {
        // Patch the doc variant with the host's live upload % while the
        // local-echo blob streams to S3 (bug 2). `deriveVariantFromBody`
        // returns the parsed doc card; we overlay the 'uploading' state.
        const progress = uploadProgressByMessageId[m.id];
        if (variant.kind === 'doc' && m.deliveryState === 'failed') {
          // Failed UPLOAD → 'failed' doc tile ("Failed to send" + a working
          // re-upload on tap), NOT a phantom 'uploaded' card. Was missing
          // entirely, so a failed file upload rendered as if it sent.
          out[m.id] = { ...variant, state: 'failed', onRetry: () => handleMediaRetry(m) };
        } else if (
          variant.kind === 'doc' &&
          progress !== undefined &&
          m.deliveryState === 'pending'
        ) {
          out[m.id] = { ...variant, state: 'uploading', progress };
        } else if (variant.kind === 'location') {
          // Resolve the sharer's avatar (live shares render it as the map
          // pin) and an onOpen that deep-links to the maps provider. For
          // self we use the local account's avatar; for peers we reuse the
          // group sender avatar source. Coordinates never reach a logger.
          const sharerAvatarUrl = m.isSelf
            ? (localAccountId !== null
                ? memberAvatarUris[localAccountId]
                : undefined) ?? resolvedSelfAvatar
            : senderAvatars?.[m.senderId]?.imageUri;
          const lat = variant.lat;
          const lng = variant.lng;
          out[m.id] = {
            ...variant,
            ...(sharerAvatarUrl !== undefined ? { sharerAvatarUrl } : {}),
            onOpen: () => {
              // Host owns the maps deep link. Opened on user tap only.
              window.open(
                `https://maps.google.com/?q=${lat},${lng}`,
                '_blank',
                'noopener,noreferrer',
              );
            },
          };
        } else if (variant.kind === 'linkPreview') {
          const href = variant.url;
          out[m.id] = {
            ...variant,
            onOpen: () => {
              window.open(href, '_blank', 'noopener,noreferrer');
            },
          };
        } else if (variant.kind === 'call') {
          const callId = variant.callId;
          const terminal =
            callId !== undefined ? callTiles.terminalByCallId.get(callId) : undefined;
          // Footer wiring shared by every call tile: "Call threads" reopens this
          // call's thread (rooted at the call tile's content hash) and "Meeting
          // notes"/Download preview + download the saved Call-Notes.
          const callThread: {
            onOpenCallThread?: () => void;
            callThreadHasMessages?: boolean;
            notes?: { onOpen: () => void; onDownload: () => void; hasContent: boolean };
          } = {};
          // Thread is rooted at the shared call_id (identical for everyone), so
          // the chat tile + the in-call panel + every participant open the SAME
          // one thread per call. (Rooting at the message content hash diverges
          // between the caller's local echo and the received copy → two threads.)
          if (callId !== undefined && shellHandlers?.openThread !== undefined) {
            callThread.onOpenCallThread = () =>
              shellHandlers.openThread(callId as unknown as ThreadIdentifier);
          }
          // Red dot on "Call threads" when this call's thread has any replies.
          callThread.callThreadHasMessages =
            callId !== undefined && callTiles.threadedRoots.has(callId);
          if (callId !== undefined) {
            const cid = callId;
            callThread.notes = {
              onOpen: () => void openCallNotes(cid),
              onDownload: () => void downloadCallNotes(cid),
              hasContent: callsWithNotes.has(cid),
            };
          }
          // Resolve the persisted call roster → avatar stack ONCE, so EVERY
          // terminal tile shows who was in the call — both the invite tile
          // rendering its ended state AND a standalone `call_end` tile (the
          // bystander / recipient case, which previously dropped the roster
          // entirely). Also derive a per-viewer "missed" when the local account
          // never joined a normally-ended call. See calls-participant-roster-spec.md.
          const roster = terminal?.participants;
          const rosterAvatars =
            roster !== undefined && roster.length > 0
              ? roster.slice(0, 3).map((accId) => {
                  const rv = resolvePollVoter(accId);
                  return rv.avatarUrl !== undefined
                    ? { initials: rv.initials, avatarUrl: rv.avatarUrl }
                    : { initials: rv.initials };
                })
              : undefined;
          const rosterExtra = roster !== undefined && roster.length > 3 ? roster.length - 3 : 0;
          const viewerMissed =
            terminal?.callState === 'ended' &&
            roster !== undefined &&
            localAccountId !== null &&
            !roster.includes(localAccountId);
          if (terminal !== undefined && variant.callGroupId !== undefined) {
            // INVITE tile of a call that has ended → render its terminal state
            // in place (ended / missed / failed). One tile per call; no stale
            // "Call ongoing", no duplicate end row (that row is suppressed).
            const media = variant.media;
            const effectiveActionable = viewerMissed ? true : terminal.actionable;
            out[m.id] = {
              ...variant,
              ...callThread,
              callState: viewerMissed ? 'missed' : terminal.callState,
              status: viewerMissed ? 'Call missed' : terminal.status,
              durationLabel: viewerMissed ? 'You missed a call' : terminal.durationLabel,
              actionable: effectiveActionable,
              ...(rosterAvatars !== undefined ? { participants: rosterAvatars } : {}),
              ...(rosterExtra > 0 ? { extraCount: rosterExtra } : {}),
              startedAtMs: undefined, // no live timer once ended
              joined: false,
              // missed/failed stay actionable → "Tap to call back" starts fresh.
              ...(effectiveActionable && media !== undefined
                ? {
                    onJoinLeave: () => {
                      void useCallStore.getState().startCall({
                        conversationId,
                        media,
                        title: headerTitle,
                        isGroup,
                        peerAccountIds: callPeerIds,
                        peerDisplayNames: callPeerNames,
                        ...(headerAvatarUri !== undefined ? { avatarUri: headerAvatarUri } : {}),
                      });
                    },
                  }
                : {}),
            };
          } else if (
            variant.actionable &&
            callId !== undefined &&
            variant.callGroupId !== undefined &&
            variant.media !== undefined
          ) {
            // Genuinely ongoing call (no end yet) → joinable; reflect whether
            // the viewer is already in THIS call ("You're in this call").
            const callGroupId = variant.callGroupId;
            const media = variant.media;
            // Resolve live participants into avatar views. When the viewer is IN
            // this call we have authoritative LiveKit participants; otherwise we
            // fall back to the live-roster presence pings (`com.ping.call_roster`)
            // so a bystander STILL sees the count/avatars update as members join
            // and leave — no longer avatar-less.
            const liveRoster = callId !== undefined ? liveRosterByCallId[callId] : undefined;
            const participants =
              activeCallId === callId
                ? liveCallParticipants.map((p) => {
                    const initials = initialsFromName(p.name);
                    // SELF: LiveKit's local participant usually carries no
                    // `accountId` in metadata, so resolve OUR picture from the
                    // self-profile avatar directly.
                    if (p.isLocal && resolvedSelfAvatar !== undefined) {
                      return { initials, avatarUrl: resolvedSelfAvatar };
                    }
                    // OTHERS: the SAME rich resolver the ended tile uses —
                    // covers group member avatars AND the 1:1 peer/header avatar,
                    // not just the group-only `memberAvatarUris` map (empty in a
                    // 1:1, which is why ongoing 1:1 calls showed initials only).
                    if (p.accountId !== undefined) {
                      const rv = resolvePollVoter(p.accountId);
                      if (rv.avatarUrl !== undefined) return { initials, avatarUrl: rv.avatarUrl };
                    }
                    return { initials };
                  })
                : liveRoster !== undefined && liveRoster.length > 0
                  ? liveRoster.slice(0, 3).map((accId) => {
                      const rv = resolvePollVoter(accId);
                      return rv.avatarUrl !== undefined
                        ? { initials: rv.initials, avatarUrl: rv.avatarUrl }
                        : { initials: rv.initials };
                    })
                  : undefined;
            const participantsExtra =
              activeCallId !== callId && liveRoster !== undefined && liveRoster.length > 3
                ? liveRoster.length - 3
                : 0;
            // The tile shows "Call started" (not "Call ongoing") until a second
            // person has actually joined the call:
            //   - Outgoing caller alone: callingCallIdsKey contains the callId
            //     because direction === 'outgoing' and no remote participants
            //   - Incoming ringing: callingCallIdsKey contains the callId
            //     because the ring.status === 'ringing' (callee hasn't answered)
            // Once a second person joins, both conditions clear and the tile
            // flips to "Call ongoing" naturally.
            const callNotYetOngoing =
              callId !== undefined &&
              callingCallIdsKey.length > 0 &&
              callingCallIdsKey.split('|').includes(callId);
            // Staleness backstop: an invite with NO matching `call_end` (lost,
            // dropped, or undecryptable) would otherwise show "Call ongoing /
            // Join" forever. If the call started long ago, has no terminal, and
            // the viewer isn't in it (and it isn't the local ring/outgoing), fold
            // it to "Call ended" — the tile analogue of the ring-expiry guard
            // (calls.md §6.6). See B4 in the delivery/calls audit.
            const STALE_ONGOING_MS = 6 * 60 * 60 * 1000; // 6 hours
            const isStaleOngoing =
              activeCallId !== callId &&
              !callNotYetOngoing &&
              Date.now() - m.timestamp > STALE_ONGOING_MS;
            if (isStaleOngoing) {
              out[m.id] = {
                ...variant,
                ...callThread,
                callState: 'ended',
                status: 'Call ended',
                durationLabel: 'Call ended',
                actionable: false,
                startedAtMs: undefined,
                joined: false,
              };
            } else {
              out[m.id] = {
                ...variant,
                ...callThread,
                joined: activeCallId === callId,
                ...(callNotYetOngoing
                  ? {
                      status: 'Call started',
                      durationLabel: media === 'video' ? 'Video call' : 'Voice call',
                      startedAtMs: undefined,
                    }
                  : {}),
                ...(participants !== undefined ? { participants } : {}),
                ...(participantsExtra > 0 ? { extraCount: participantsExtra } : {}),
                onJoinLeave: () => {
                  const st = useCallStore.getState();
                  if (st.active?.callId === callId) st.setMinimized(false); // restore window
                  else
                    void st.joinCall({
                      callId,
                      callGroupId,
                      media,
                      conversationId,
                      title: headerTitle,
                      isGroup,
                      // Thread root is derived from call_id inside the store — no
                      // need to pass the (divergent) message content hash.
                    });
                },
              };
            }
          } else {
            // call_end's OWN tile with no invite in view (offline bystander /
            // recipient) → terminal, as derived, NOW WITH the participant roster
            // + per-viewer "missed". Previously this dropped the roster, so a
            // received "Call ended" tile showed no participant avatars at all.
            out[m.id] = {
              ...variant,
              ...callThread,
              ...(viewerMissed
                ? {
                    callState: 'missed' as const,
                    status: 'Call missed',
                    durationLabel: 'You missed a call',
                    actionable: true,
                  }
                : {}),
              ...(rosterAvatars !== undefined ? { participants: rosterAvatars } : {}),
              ...(rosterExtra > 0 ? { extraCount: rosterExtra } : {}),
            };
          }
        } else {
          out[m.id] = variant;
        }
      }
    }
    return out;
  }, [
    messages,
    handleDocTap,
    imagePreviewByMessageId,
    failedImageIds,
    handleImageRetry,
    handleMediaRetry,
    uploadProgressByMessageId,
    pollVotesByTarget,
    meetingState,
    meetingReminderMinutes,
    handleMeetingRsvp,
    handleMeetingCancel,
    handleMeetingEdit,
    handleJoinMeetingCall,
    handleOpenMeetingLink,
    handleCopyMeetingLink,
    senderNames,
    headerTitle,
    handlePollVote,
    resolvePollVoter,
    senderAvatars,
    memberAvatarUris,
    resolvedSelfAvatar,
    localAccountId,
    videoUriByMessageId,
    posterUriByMessageId,
    videoDownloadProgressById,
    resolveVideoBlob,
    downloadVideoBlob,
    docPreviews,
    callTiles,
    activeCallId,
    callingCallIdsKey,
    liveCallParticipants,
    liveRosterByCallId,
    conversationId,
    isGroup,
    callPeerIds,
    callPeerNames,
    headerAvatarUri,
    // `resolvePollVoter` + `resolvedSelfAvatar` are already listed above (they also
    // drive the call tile's participant-stack avatars); no need to repeat them.
    shellHandlers,
    openCallNotes,
    downloadCallNotes,
    callsWithNotes,
  ]);

  // Ordered list of every image/video in the conversation, derived from
  // the variant map — the MediaPreviewScreen carousel pages through these.
  const mediaItems = useMemo<MediaPreviewItem[]>(() => {
    // Sender header (Figma top-left): name + avatar + timestamp. Self
    // rows read "You"; group peers resolve through the roster maps; 1:1
    // peers fall back to the conversation title.
    const initialsOf = (name: string): string =>
      name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p.charAt(0).toUpperCase())
        .join('') || '?';
    // "30/5/26, 22:48" — d/M/yy, HH:mm (matches the Figma header).
    const stampOf = (ts: number): string => {
      const d = new Date(ts);
      const pad = (n: number): string => String(n).padStart(2, '0');
      return `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(-2)}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const header = (m: Message): Partial<MediaPreviewItem> => {
      const name = m.isSelf ? 'You' : senderNames?.[m.senderId] ?? headerTitle;
      const avatarUri = m.isSelf ? resolvedSelfAvatar : senderAvatars?.[m.senderId]?.imageUri;
      const initials = m.isSelf
        ? initialsOf(headerTitle === 'New chat' ? 'You' : headerTitle)
        : senderAvatars?.[m.senderId]?.initials ?? initialsOf(name);
      return {
        senderName: name,
        senderInitials: initials,
        timestampLabel: stampOf(m.timestamp),
        ...(avatarUri !== undefined ? { senderAvatarUri: avatarUri } : {}),
      };
    };
    const out: MediaPreviewItem[] = [];
    for (const m of messages) {
      const v = messageVariants[m.id];
      if (v?.kind === 'image') {
        out.push({
          id: m.id,
          kind: 'image',
          ...header(m),
          ...(v.imageUri !== undefined ? { uri: v.imageUri } : {}),
        });
      } else if (v?.kind === 'video') {
        out.push({
          id: m.id,
          kind: 'video',
          ...header(m),
          ...(v.videoUri !== undefined ? { uri: v.videoUri } : {}),
          ...(v.posterUri !== undefined ? { posterUri: v.posterUri } : {}),
        });
      } else if (v?.kind === 'doc') {
        // Docs join the viewer carousel once we have a decrypted object URL
        // (eager for PDF/text, lazy-on-tap for other types). The reader
        // renders the mime inline when the browser can, else a download
        // fallback.
        const dp = docPreviews[m.id];
        if (dp?.docUri !== undefined && dp.docUri.length > 0) {
          out.push({
            id: m.id,
            kind: 'doc',
            uri: dp.docUri,
            ...header(m),
            ...(dp.thumbUri.length > 0 ? { posterUri: dp.thumbUri } : {}),
            filename: dp.filename,
            mime: dp.mime,
          });
        }
      }
    }
    return out;
  }, [
    messages,
    messageVariants,
    senderNames,
    senderAvatars,
    headerTitle,
    resolvedSelfAvatar,
    docPreviews,
  ]);
  // Group key per media message: a contiguous run of media (image/video/
  // doc) from the SAME sender with no non-media message between them is one
  // "album". The preview carousel is scoped to the tapped item's group so
  // it pages through only the related media, not every media in the chat.
  const mediaGroupKeyByMessageId = useMemo(() => {
    const keys: Record<string, number> = {};
    let group = 0;
    let lastIdx = -2;
    let lastSenderKey = '';
    messages.forEach((m, idx) => {
      const v = messageVariants[m.id];
      const isMedia = v?.kind === 'image' || v?.kind === 'video' || v?.kind === 'doc';
      if (!isMedia) return;
      const senderKey = m.isSelf ? '__self__' : m.senderId;
      if (!(idx === lastIdx + 1 && senderKey === lastSenderKey)) {
        group += 1;
      }
      keys[m.id] = group;
      lastIdx = idx;
      lastSenderKey = senderKey;
    });
    return keys;
  }, [messages, messageVariants]);

  // The media actually shown in the viewer — only the tapped item's group.
  const previewItems = useMemo(() => {
    if (previewMediaId === null) return mediaItems;
    const activeGroup = mediaGroupKeyByMessageId[previewMediaId];
    if (activeGroup === undefined) return mediaItems;
    return mediaItems.filter((it) => mediaGroupKeyByMessageId[it.id] === activeGroup);
  }, [previewMediaId, mediaItems, mediaGroupKeyByMessageId]);
  const previewIndex =
    previewMediaId === null ? -1 : previewItems.findIndex((it) => it.id === previewMediaId);

  // Resolve the pinned-set into the PinnedBar's message previews.
  const pinnedMessages = useMemo(() => {
    if (pinnedTargets.size === 0) return [];
    return messages
      .filter((m) => m.contentHash.length > 0 && pinnedTargets.has(m.contentHash))
      .map((m) => ({
        id: m.id,
        senderName: m.isSelf ? 'You' : senderNames?.[m.senderId] ?? headerTitle,
        preview: lastMessagePreview(m.body) ?? '',
        timestamp: m.timestamp,
      }));
  }, [messages, pinnedTargets, senderNames, headerTitle]);

  // Message-id lists (keyed by row id, not content hash) for the in-list
  // action menu so Pin/Save/Star reflect state (Unpin / Unsave / Unstar) and
  // the pin toggle can decide pin-vs-unpin. `pinnedTargets`/`savedTargets`/
  // `starredTargets` are content-hash sets; ChatRoom marks rows by `m.id`.
  const pinnedMessageIds = useMemo(
    () =>
      pinnedTargets.size === 0
        ? []
        : messages
            .filter((m) => m.contentHash.length > 0 && pinnedTargets.has(m.contentHash))
            .map((m) => m.id),
    [messages, pinnedTargets],
  );
  const savedMessageIds = useMemo(
    () =>
      savedTargets.size === 0
        ? []
        : messages
            .filter((m) => m.contentHash.length > 0 && savedTargets.has(m.contentHash))
            .map((m) => m.id),
    [messages, savedTargets],
  );
  const starredMessageIds = useMemo(
    () =>
      starredTargets.size === 0
        ? []
        : messages
            .filter((m) => m.contentHash.length > 0 && starredTargets.has(m.contentHash))
            .map((m) => m.id),
    [messages, starredTargets],
  );

  // Chat Info → Media: derived from the already-decrypted lightbox media set.
  const chatInfoPhotos = useMemo(
    () =>
      mediaItems
        .filter((m) => m.kind === 'image' && m.uri !== undefined)
        .map((m) => ({ id: m.id, uri: m.uri as string })),
    [mediaItems],
  );
  const chatInfoFiles = useMemo<ChatInfoMediaFile[]>(
    () =>
      mediaItems
        .filter((m) => m.kind === 'doc')
        .map((m) => ({ id: m.id, name: m.filename ?? 'Document', meta: m.senderName ?? '' })),
    [mediaItems],
  );

  // Chat Info → Threads: group replies by their root message; show the root's
  // sender/preview + reply count.
  const chatInfoThreads = useMemo<ChatInfoThread[]>(() => {
    const repliesByRoot = new Map<string, number>();
    for (const m of messages) {
      const rootId = m.inReplyTo?.messageId;
      if (rootId === undefined) continue;
      repliesByRoot.set(rootId, (repliesByRoot.get(rootId) ?? 0) + 1);
    }
    const rootById = new Map(messages.map((m) => [m.id, m]));
    return Array.from(repliesByRoot.entries())
      .map(([rootId, replyCount]): ChatInfoThread | null => {
        const root = rootById.get(rootId);
        if (root === undefined) return null;
        return {
          id: rootId,
          senderName: root.isSelf ? 'You' : senderNames?.[root.senderId] ?? headerTitle,
          preview: lastMessagePreview(root.body) ?? '',
          replyCount,
        };
      })
      .filter((t): t is ChatInfoThread => t !== null && t.preview.length > 0);
  }, [messages, senderNames, headerTitle]);

  // Chat Info → Profile → Saved: resolve the saved-content set into previews.
  const chatInfoSaved = useMemo<ChatInfoMessagePreview[]>(() => {
    if (savedTargets.size === 0) return [];
    return messages
      .filter((m) => m.contentHash.length > 0 && savedTargets.has(m.contentHash))
      .map((m) => ({
        id: m.id,
        senderName: m.isSelf ? 'You' : senderNames?.[m.senderId] ?? headerTitle,
        preview: lastMessagePreview(m.body) ?? '',
        date: new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(
          new Date(m.timestamp),
        ),
      }));
  }, [messages, savedTargets, senderNames, headerTitle]);

  // Chat Info → Media → Links: enumerate `com.ping.link_preview` envelopes.
  const chatInfoLinks = useMemo<ChatInfoMediaLink[]>(() => {
    const out: ChatInfoMediaLink[] = [];
    for (const m of messages) {
      try {
        const parsed = parseEnvelope(m.body);
        if (!parsed || parsed.kind !== 'com.ping.link_preview') continue;
        const body = (parsed as { body?: LinkPreviewBody }).body;
        if (body === undefined || typeof body.url !== 'string') continue;
        out.push({
          id: m.id,
          title: body.title.length > 0 ? body.title : body.url,
          url: body.url,
          ...(body.image_ref?.url !== undefined ? { thumbUri: body.image_ref.url } : {}),
        });
      } catch {
        // Non-envelope / undecodable body — skip.
      }
    }
    return out;
  }, [messages]);

  // Chat Info → Security: a REAL safety number — a cryptographic fingerprint of
  // the conversation's MLS member identity keys, not the (public, non-secret)
  // conversationId. Each member's `user_id` is SHA-256 of their identity public
  // key; we hash the SORTED set of member user_ids so both parties derive the
  // SAME number, and it CHANGES if any member's identity key changes (the whole
  // point of a safety number — detecting a MITM / key swap). Computed async off
  // the SDK roster (`client.members`), so it lives in state, not a sync memo.
  const [chatInfoSafetyNumber, setChatInfoSafetyNumber] = useState<string>('');
  useEffect(() => {
    if (client === null) {
      setChatInfoSafetyNumber('');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const roster = await (
          client as unknown as {
            members(conv: Uint8Array): Promise<{ user_id: Uint8Array }[]>;
          }
        ).members(conversationIdBytes);
        // Stable, symmetric ordering: sort the member identity hashes.
        const ids = roster
          .map((m) => m.user_id)
          .sort((a, b) => {
            const len = Math.min(a.length, b.length);
            for (let i = 0; i < len; i++) {
              const d = (a[i] ?? 0) - (b[i] ?? 0);
              if (d !== 0) return d;
            }
            return a.length - b.length;
          });
        const total = ids.reduce((n, id) => n + id.length, 0);
        const concat = new Uint8Array(total);
        let off = 0;
        for (const id of ids) {
          concat.set(id, off);
          off += id.length;
        }
        const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', concat));
        const hex = Array.from(digest, (b) => b.toString(16).padStart(2, '0'))
          .join('')
          .toUpperCase();
        // 16 groups of 4 hex chars = the full 32-byte fingerprint.
        const formatted = (hex.match(/.{1,4}/g) ?? []).join(' ');
        if (!cancelled) setChatInfoSafetyNumber(formatted);
      } catch (err) {
        // Roster not available yet (conversation not open in the worker) —
        // leave blank rather than show a misleading number.
        if (!cancelled) setChatInfoSafetyNumber('');
        log.warn({
          event: 'chatInfo.safetyNumber.failed',
          error: (err as Error).message,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, conversationId, conversationIdBytes]);

  // ── Chat Info action handlers ──────────────────────────────────────────────

  /** Self-Destruct: persist the TTL locally + broadcast a
   *  `com.ping.disappearing_policy` so peers enforce the same expiry. */
  const applyDisappearingTtl = useCallback(
    (ttlSeconds: number): void => {
      setChatInfoTtl(ttlSeconds);
      void setConversationTtl(conversationId, ttlSeconds);
      void (async () => {
        try {
          const conv = await resolveConv();
          if (conv === null) return;
          await conv.send(
            encodeEnvelopeJsonBytes({
              kind: 'com.ping.disappearing_policy',
              payload: { scope: 'conversation', ttl_seconds: ttlSeconds },
            }),
          );
        } catch (err) {
          log.warn({ event: 'chat.info.selfDestruct.failed', err: (err as Error).message });
        }
      })();
    },
    [conversationId, resolveConv],
  );

  const handleToggleSelfDestruct = useCallback(
    (next: boolean): void => {
      applyDisappearingTtl(next ? (chatInfoTtl > 0 ? chatInfoTtl : DEFAULT_TTL_SECONDS) : 0);
    },
    [applyDisappearingTtl, chatInfoTtl],
  );

  const handlePickSelfDestructDuration = useCallback((): void => {
    const idx = (SELF_DESTRUCT_PRESETS as readonly number[]).indexOf(chatInfoTtl);
    const next = SELF_DESTRUCT_PRESETS[(idx + 1) % SELF_DESTRUCT_PRESETS.length] ?? DEFAULT_TTL_SECONDS;
    applyDisappearingTtl(next);
  }, [applyDisappearingTtl, chatInfoTtl]);

  /** C3: is the LOCAL user an admin/owner of THIS group? Single source of truth
   *  for gating every admin-only Chat-Info action (rename, avatar, add/remove
   *  members, role changes). Fail-closed: a non-group chat, an unknown local
   *  account, or a not-yet-hydrated role map all yield false. The owner (creator,
   *  immutable) is always authorized — even before the role map hydrates. */
  const iAmGroupAdmin = useMemo<boolean>(() => {
    if (!isGroup || localAccountId == null) return false;
    if (
      localConv?.ownerAccountId !== undefined &&
      localAccountId === localConv.ownerAccountId
    ) {
      return true;
    }
    const role = chatInfoRoles[localAccountId];
    return role === 'owner' || role === 'admin';
  }, [isGroup, localAccountId, localConv?.ownerAccountId, chatInfoRoles]);

  /** Make admin / demote — toggles the member's role + broadcasts
   *  `com.ping.member_role` (client-authoritative; server mirrors per B2). */
  const handleMakeAdmin = useCallback(
    (memberId: string): void => {
      // C3: only the owner or a current admin may change roles. Gate locally so
      // a non-admin's action never hits the wire (receivers also enforce this).
      const owner = localConv?.ownerAccountId;
      if (!iAmGroupAdmin) {
        log.warn({ event: 'chat.info.makeAdmin.notAuthorized' });
        return;
      }
      // The owner's role can't be changed.
      if (owner !== undefined && memberId === owner) return;
      const current = chatInfoRoles[memberId] ?? 'member';
      const role: 'admin' | 'member' =
        current === 'admin' || current === 'owner' ? 'member' : 'admin';
      setChatInfoRoles((prev) => ({ ...prev, [memberId]: role }));
      void setConversationMemberRole(conversationId, memberId, role);
      void (async () => {
        try {
          const conv = await resolveConv();
          if (conv === null) return;
          await conv.send(
            encodeEnvelopeJsonBytes({
              kind: 'com.ping.member_role',
              payload: { target_user_id: memberId, role },
            }),
          );
        } catch (err) {
          log.warn({ event: 'chat.info.makeAdmin.failed', err: (err as Error).message });
        }
      })();
    },
    [chatInfoRoles, conversationId, resolveConv, localConv?.ownerAccountId, iAmGroupAdmin],
  );

  /** Remove members — MLS Commit (evict leaves) + local roster patch. */
  const handleRemoveMembers = useCallback(
    (ids: readonly string[]): void => {
      if (ids.length === 0) return;
      // Defense-in-depth: only an admin/owner may evict. The UI hides the
      // affordance for non-admins, but guard here so a bypassed UI can't act.
      if (!iAmGroupAdmin) {
        log.warn({ event: 'chat.info.removeMembers.notAuthorized' });
        return;
      }
      void (async () => {
        try {
          await removeWebGroupMembers({
            client: client as unknown as Parameters<
              typeof removeWebGroupMembers
            >[0]['client'],
            conversationId,
            removeAccountIds: ids,
          });
          setChatInfoRoles((prev) => {
            const next = { ...prev };
            for (const id of ids) delete next[id];
            return next;
          });
        } catch (err) {
          log.warn({ event: 'chat.info.removeMembers.failed', err: (err as Error).message });
        }
      })();
    },
    [client, conversationId, iAmGroupAdmin],
  );

  /** In-conversation search — match the decoded text of locally-cached
   *  messages in THIS conversation. */
  const searchResults = useMemo<InChatSearchResult[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length === 0) return [];
    const out: InChatSearchResult[] = [];
    for (const m of messages) {
      const text = lastMessagePreview(m.body) ?? '';
      if (text.length === 0 || !text.toLowerCase().includes(q)) continue;
      out.push({
        id: m.id,
        senderName: m.isSelf ? 'You' : senderNames?.[m.senderId] ?? headerTitle,
        snippet: text,
        date: new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(
          new Date(m.timestamp),
        ),
      });
      if (out.length >= 50) break;
    }
    return out;
  }, [searchQuery, messages, senderNames, headerTitle]);

  const handleOpenSearch = useCallback((): void => {
    setChatSettingsOpen(false);
    setSearchQuery('');
    setSearchOpen(true);
  }, []);

  const handlePickSearchResult = useCallback((id: string): void => {
    setSearchOpen(false);
    requestAnimationFrame(() => scrollToMessageRef.current?.(id));
  }, []);

  /** Build the shareable invite deep link for this conversation. */
  const buildShareUrl = useCallback((): string => {
    // 1-1: a resolvable invite link to the PEER's account — the recipient's
    // client resolves it (getUserProfile) and starts a chat.
    if (!isGroup) {
      const peer = localConv?.peerAccountId;
      if (peer !== undefined && peer.length > 0) return buildUserInviteLink(peer);
    } else if (localAccountId !== null && localAccountId.length > 0) {
      // GROUP: an MLS group can't be joined from a URL, so the link names US (a
      // current member who can admit) + the group id. The recipient opens it →
      // 1-1 with us + a join request → we approve. See profiles-and-links.md §4b.
      return buildGroupInviteLink(localAccountId, conversationId);
    }
    // Fallback (identity not loaded yet): the internal route (not externally
    // resolvable). Should be transient — localAccountId hydrates on mount.
    const origin = typeof location !== 'undefined' ? location.origin : '';
    return `${origin}/chat/${conversationId}`;
  }, [isGroup, conversationId, localConv?.peerAccountId, localAccountId]);

  /** Share popover → "Copy link": copy the deep link to the clipboard. */
  const handleCopyLink = useCallback((): void => {
    const url = buildShareUrl();
    void (async () => {
      try {
        const nav = typeof navigator !== 'undefined' ? navigator : undefined;
        if (nav?.clipboard !== undefined) {
          await nav.clipboard.writeText(url);
          pingToast.success({ title: 'Link copied' });
        }
      } catch {
        // Clipboard denied — non-fatal.
      }
    })();
  }, [buildShareUrl]);

  /** Share popover → "Share via…": open the native share sheet (clipboard
   *  fallback when the Web Share API is unavailable, e.g. desktop browsers). */
  const handleShare = useCallback((): void => {
    const url = buildShareUrl();
    void (async () => {
      try {
        const nav = typeof navigator !== 'undefined' ? navigator : undefined;
        if (nav !== undefined && typeof nav.share === 'function') {
          await nav.share({ title: headerTitle, text: `Connect with ${headerTitle} on Ping`, url });
        } else if (nav?.clipboard !== undefined) {
          await nav.clipboard.writeText(url);
          pingToast.success({ title: 'Link copied' });
        }
      } catch {
        // The user dismissed the native share sheet — non-fatal.
      }
    })();
  }, [buildShareUrl, headerTitle]);

  /** Contacts the profile can be shared to (Share-profile modal). Only 1:1
   *  conversations expose a `peerAccountId` (→ `accountId`), which is what the
   *  group-invite path needs; group rows are still listed for the 1:1 share
   *  case but can't be added AS a member. */
  const shareContacts = useMemo(
    () =>
      allConversations
        .filter((conv) => conv.id !== conversationId)
        .map((conv) => ({
          id: conv.id,
          name: conv.name ?? 'Chat',
          ...(conv.avatarUri !== undefined ? { avatarUri: conv.avatarUri } : {}),
          ...(typeof conv.peerAccountId === 'string' && conv.peerAccountId.length > 0
            ? { accountId: conv.peerAccountId }
            : {}),
          source: 'contact' as const,
        })),
    [allConversations, conversationId],
  );

  /** Directory search for the Share-profile modal (parity with NewChatModal) —
   *  lets any Ping user be searched and shared to / invited to the group, not
   *  just people you already have a chat with. */
  const handleShareSearchUsers = useCallback(
    async (query: string, signal: AbortSignal): Promise<readonly ShareProfileContact[]> => {
      const trimmed = query.trim();
      if (trimmed.length === 0) return [];
      const res = await searchUsers({ query: trimmed, limit: 20, signal, skipReauth: true });
      const selfAccountId = (await retrieveIdentity())?.accountId;
      return res.users
        .filter((u) => u.id !== selfAccountId)
        .map((u) => {
          const subtitle = [u.role, u.company].filter(Boolean).join(' · ');
          return {
            id: u.id,
            name: u.displayName,
            accountId: u.id,
            source: 'on-ping' as const,
            ...(u.avatarUri !== undefined ? { avatarUri: u.avatarUri } : {}),
            ...(subtitle.length > 0 ? { subtitle } : {}),
          };
        });
    },
    [],
  );

  /** Start (or open) a 1:1 with a directory user by account id — claim their
   *  devices + our own siblings, mint the MLS conversation, and register the
   *  local row. Used by the Share modal when a searched user has no existing
   *  chat. Mirrors the NewChat / global-search start-chat orchestration. */
  const startDirectChatWithAccount = useCallback(
    async (
      accountId: string,
      displayName: string,
    ): Promise<{ conversationId: string; conv: Awaited<ReturnType<typeof getOrCreateMlsConversation>>['conv'] }> => {
      if (client === null) throw new Error('Session not ready. Try again in a moment.');
      const transport = getCurrentTransport();
      if (transport === null) throw new Error('Not connected yet — try again in a moment.');
      const claimed = await claimAllDevicesByAccountId({ accountId });
      const identity = await retrieveIdentity();
      const selfSiblingDevices = identity
        ? (
            await claimAllDevicesByAccountId({ accountId: identity.accountId }).catch(
              () => ({ devices: [] as typeof claimed.devices }),
            )
          ).devices
            .filter((d) => d.deviceId !== identity.deviceId)
            .map((d) => ({ deviceIdHex: d.deviceId, keyPackageBytes: d.keyPackage }))
        : [];
      const meta = {
        inviter: {
          accountId: identity?.accountId ?? '',
          displayName: identity?.displayName ?? 'Someone',
        },
        peer: { accountId: claimed.accountId, displayName },
      };
      const result = await getOrCreateMlsConversation({
        client: client as unknown as Parameters<typeof getOrCreateMlsConversation>[0]['client'],
        transport,
        beConversationId: `acct:${claimed.accountId}`,
        conversationName: `${CONV_META_PREFIX}${JSON.stringify(meta)}`,
        log,
        peerAccountId: claimed.accountId,
        preClaimedPeers: claimed.devices.map((d) => ({
          deviceIdHex: d.deviceId,
          keyPackageBytes: d.keyPackage,
        })),
        ...(selfSiblingDevices.length > 0 ? { selfSiblingDevices } : {}),
      });
      const localConvId = result.conversationIdHex;
      await ensureConversation({
        conversationId: localConvId,
        counterpartDisplayName: displayName,
        kind: 'direct',
        peerAccountId: claimed.accountId,
      });
      return { conversationId: localConvId, conv: result.conv };
    },
    [client],
  );

  /**
   * Share popover → selected contacts.
   *  - GROUP: there's no externally-resolvable group join link (an MLS group
   *    can't be joined from a URL), so "sharing" to contacts ADDS them to the
   *    group directly — claim each peer's device key-packages + one addMembers
   *    Commit/Welcome via `addWebGroupMembers`. This is what actually "adds users
   *    to the group" (see profiles-and-links.md §4b).
   *  - 1-1: send the resolvable profile invite link to each selected contact
   *    (starting a fresh chat first when the pick is a directory-searched user).
   */
  const handleShareToContacts = useCallback(
    async (picked: readonly ShareProfileContact[]): Promise<ShareResult> => {
      if (client === null || picked.length === 0) {
        log.warn({
          event: 'chat.info.shareToContacts.noop',
          hasClient: client !== null,
          count: picked.length,
        });
        return { sent: 0, failed: picked.length };
      }

      if (isGroup) {
        // Add each selected person to the group by ACCOUNT ID — works for both an
        // existing 1:1 contact (accountId = its peer) and a directory-searched
        // user (accountId = their account). A row with no account id (e.g. a
        // group) can't be added as a member → skipped/failed.
        const seen = new Set<string>();
        const toAdd = picked
          .map((c) => ({
            accountId:
              c.accountId ??
              allConversations.find((conv) => conv.id === c.id)?.peerAccountId ??
              undefined,
            displayName: c.name || 'Member',
          }))
          .filter(
            (m): m is { accountId: string; displayName: string } =>
              typeof m.accountId === 'string' &&
              m.accountId.length > 0 &&
              !seen.has(m.accountId) &&
              (seen.add(m.accountId), true),
          );
        if (toAdd.length === 0) {
          log.warn({ event: 'chat.info.shareToGroup.noPeers', count: picked.length });
          return { sent: 0, failed: picked.length };
        }
        log.info({ event: 'chat.info.shareToGroup.begin', count: toAdd.length });
        try {
          const result = await addWebGroupMembers({
            client: client as unknown as Parameters<typeof addWebGroupMembers>[0]['client'],
            conversationId,
            members: toAdd,
          });
          const added = result.addedPeerAccountIds.length;
          log.info({ event: 'chat.info.shareToGroup.ok', added });
          // Anyone not in `addedPeerAccountIds` was already a member (a no-op),
          // not a failure — count them as sent so the banner isn't alarming.
          return { sent: toAdd.length, failed: 0 };
        } catch (err) {
          log.warn({ event: 'chat.info.shareToGroup.failed', err: (err as Error).message });
          return { sent: 0, failed: toAdd.length };
        }
      }

      const url = buildShareUrl();
      const text = `${headerTitle} on Ping\n${url}`;
      log.info({ event: 'chat.info.shareToContacts.begin', count: picked.length });
      const transport = getCurrentTransport();
      if (transport === null) {
        // No live transport → nothing can be delivered; report it instead of
        // silently "sending" into the void.
        log.warn({ event: 'chat.info.shareToContacts.noTransport', count: picked.length });
        return { sent: 0, failed: picked.length };
      }
      let sent = 0;
      let failed = 0;
      for (const contact of picked) {
        try {
          // Prefer an existing conversation row for this pick; otherwise (a
          // directory-searched user) start a 1:1 with them by account id first.
          const existing = allConversations.find((conv) => conv.id === contact.id);
          const targetConvId = existing?.id ?? contact.id;
          let convId = targetConvId;
          let conv: Awaited<ReturnType<typeof getOrCreateMlsConversation>>['conv'];
          if (existing !== undefined) {
            const result = await getOrCreateMlsConversation({
              client: client as unknown as Parameters<typeof getOrCreateMlsConversation>[0]['client'],
              transport,
              beConversationId: existing.id,
              conversationName: existing.name,
              log,
            });
            conv = result.conv;
          } else if (contact.accountId !== undefined && contact.accountId.length > 0) {
            const started = await startDirectChatWithAccount(contact.accountId, contact.name);
            convId = started.conversationId;
            conv = started.conv;
          } else {
            failed += 1;
            log.warn({ event: 'chat.info.shareToContact.unresolvable', id: contact.id });
            continue;
          }
          const sentEnvelope = (await conv.send(
            encodeEnvelopeJsonBytes({ kind: 'com.ping.text', payload: { body: text } }),
          )) as SentMessageEnvelope;
          // Persist locally in the TARGET conversation so the sender actually
          // sees the shared message there (the SDK relays to peers but doesn't
          // echo back to the sending device).
          await persistSentMessage({
            conversationId: convId,
            envelope: sentEnvelope,
            body: text,
            retryRowId: null,
          });
          sent += 1;
          log.info({ event: 'chat.info.shareToContact.ok', targetPrefix: convId.slice(0, 8) });
        } catch (err) {
          failed += 1;
          log.warn({
            event: 'chat.info.shareToContact.failed',
            targetPrefix: contact.id.slice(0, 8),
            err: (err as Error).message,
          });
        }
      }
      log.info({ event: 'chat.info.shareToContacts.done', sent, failed, total: picked.length });
      return { sent, failed };
    },
    [
      client,
      allConversations,
      buildShareUrl,
      headerTitle,
      isGroup,
      conversationId,
      startDirectChatWithAccount,
    ],
  );

  /** Notification-sound controls (Chat Info → Notifications → Sounds). */
  const handlePreviewSound = useCallback((): void => {
    const s = pendingSound ?? chatInfoSound;
    playSoundUrl(s?.dataUrl ?? DEFAULT_SOUND_URL);
  }, [pendingSound, chatInfoSound]);

  const handleBrowseSound = useCallback((): void => {
    void pickAudioFile().then((picked) => {
      if (picked !== null) setPendingSound(picked);
    });
  }, []);

  const handleSaveSound = useCallback((): void => {
    if (pendingSound === null) return;
    saveCustomSound(conversationId, pendingSound);
    setChatInfoSound(pendingSound);
    setPendingSound(null);
  }, [pendingSound, conversationId]);

  const handleResetSound = useCallback((): void => {
    saveCustomSound(conversationId, null);
    setChatInfoSound(null);
    setPendingSound(null);
  }, [conversationId]);

  /** Pick / replace the group avatar (Chat Info → header). Persists locally +
   *  broadcasts `com.ping.group_avatar` so every member's list/header updates. */
  const handlePickAvatar = useCallback((): void => {
    // Defense-in-depth: only an admin/owner may change the group photo.
    if (!iAmGroupAdmin) {
      log.warn({ event: 'chat.info.groupAvatar.notAuthorized' });
      return;
    }
    void pickAvatarDataUrl().then(async (uri) => {
      if (uri === null) return;
      setChatInfoAvatar(uri);
      void setConversationAvatar(conversationId, uri);
      try {
        const identity = await retrieveIdentity();
        const conv = await resolveConv();
        if (conv === null) return;
        // Upload to media up-front so the stable id can (a) be persisted on the
        // row, (b) ride the group meta, and (c) ride the broadcast.
        // CRITICAL: persisting the NEW avatarMediaId keeps it in sync with
        // avatarUri — otherwise `hydrateMemberProfiles` re-resolves the STALE id
        // on the next pass and REVERTS the icon to the old image (the bug where
        // the "X updated the group icon" event shows but the icon never changes).
        const mediaId = await uploadGroupAvatarDataUrl(uri);
        if (mediaId !== undefined && mediaId.length > 0) {
          void setConversationAvatarMediaId(conversationId, mediaId);
        }
        // Bulletproof path: carry the id in the group meta via a
        // GroupContextExtensions commit, so every member (and future joiner)
        // gets it from group STATE. Best-effort — falls back to the broadcast
        // below when the SDK op is unavailable / a member hasn't re-linked yet.
        if (
          isGroup &&
          typeof conv.setConversationName === 'function' &&
          mediaId !== undefined &&
          (identity?.accountId ?? '').length > 0
        ) {
          try {
            await conv.setConversationName(
              buildUpdatedGroupMetaName({
                memberAccountIds: localConv?.memberAccountIds,
                memberDisplayNames: localConv?.memberDisplayNames,
                selfAccountId: identity!.accountId,
                selfDisplayName: identity!.displayName,
                title: localConv?.name,
                avatarMediaId: mediaId,
              }),
            );
          } catch (opErr) {
            log.warn({ event: 'chat.info.groupAvatar.sdkOp.failed', err: (opErr as Error).message });
          }
        }
        // Legacy broadcast (back-compat for members on pre-0.7.6 / not-re-linked).
        // Carry BOTH the inline data URL (immediate render) AND the stable media
        // id (so peers update their row's avatarMediaId and don't revert it via
        // hydration).
        const avatarEnvelope = {
          kind: 'com.ping.group_avatar' as const,
          payload: {
            blob_ref: { url: uri },
            ...(mediaId !== undefined && mediaId.length > 0
              ? { avatarMediaId: mediaId }
              : {}),
            ...(identity !== null ? { actorDisplayName: identity.displayName } : {}),
          },
        };
        const sentAvatar = await conv.send(encodeEnvelopeJsonBytes(avatarEnvelope));
        // Local echo so the ACTOR sees the room event too (peers get it from the
        // broadcast; the actor's own server echo is reconciled, not re-rendered).
        if (identity !== null) {
          await persistLocalRoomEvent({
            conversationId,
            senderAccountId: identity.accountId,
            envelope: avatarEnvelope,
            contentHash: sentAvatar.content_hash,
          });
        }
      } catch (err) {
        log.warn({ event: 'chat.info.groupAvatar.failed', err: (err as Error).message });
      }
    });
  }, [conversationId, resolveConv, isGroup, iAmGroupAdmin, localConv?.memberAccountIds, localConv?.memberDisplayNames, localConv?.name]);

  /** Rename the group (Chat Info → header title). Persists locally + broadcasts
   *  `com.ping.group_name` so every member's list/header updates. No-op for a
   *  blank or unchanged title. */
  const handleEditGroupName = useCallback(
    (next: string): void => {
      const title = next.trim();
      if (title.length === 0 || title === (localConv?.name ?? '').trim()) return;
      // Defense-in-depth: only an admin/owner may rename the group.
      if (!iAmGroupAdmin) {
        log.warn({ event: 'chat.info.groupName.notAuthorized' });
        return;
      }
      void setConversationName(conversationId, title);
      writeCachedConvName(conversationId, title);
      void (async () => {
        try {
          const identity = await retrieveIdentity();
          const conv = await resolveConv();
          if (conv === null) return;
          // Bulletproof path: carry the new title in the group meta via a
          // GroupContextExtensions commit so every member (and future joiner)
          // converges on it from group STATE. Best-effort — falls back to the
          // broadcast below. Preserves the current avatar-media-id.
          if (isGroup && typeof conv.setConversationName === 'function'
              && (identity?.accountId ?? '').length > 0) {
            try {
              await conv.setConversationName(
                buildUpdatedGroupMetaName({
                  memberAccountIds: localConv?.memberAccountIds,
                  memberDisplayNames: localConv?.memberDisplayNames,
                  selfAccountId: identity!.accountId,
                  selfDisplayName: identity!.displayName,
                  title,
                  avatarMediaId: localConv?.avatarMediaId,
                }),
              );
            } catch (opErr) {
              log.warn({ event: 'chat.info.groupName.sdkOp.failed', err: (opErr as Error).message });
            }
          }
          // Legacy broadcast (back-compat for members on pre-0.7.6 / not-re-linked).
          // Push-on-demand convergence: ride the AUTHORITATIVE avatar id + full
          // roster (incl. self) on this reliably-delivered rename, exactly like
          // `com.ping.invite`, so a member that missed the group_name/invite
          // hydration still converges name + picture + member list off this event
          // (union-merged on receipt, never shrinks). Mirrors
          // `sendGroupHydrationMessages`.
          const selfAcct = identity?.accountId;
          const rosterIds = [
            ...(localConv?.memberAccountIds ?? []),
            ...(selfAcct !== undefined && selfAcct.length > 0 ? [selfAcct] : []),
          ];
          const rosterNames: Record<string, string> = {
            ...(localConv?.memberDisplayNames ?? {}),
            ...(selfAcct !== undefined && selfAcct.length > 0 && identity !== null
              ? { [selfAcct]: identity.displayName }
              : {}),
          };
          const nameEnvelope = {
            kind: 'com.ping.group_name' as const,
            payload: {
              name: title,
              ...(identity !== null ? { actorDisplayName: identity.displayName } : {}),
              ...(localConv?.avatarMediaId !== undefined && localConv.avatarMediaId.length > 0
                ? { avatarMediaId: localConv.avatarMediaId }
                : {}),
              ...(rosterIds.length > 0 ? { memberAccountIds: rosterIds } : {}),
              ...(Object.keys(rosterNames).length > 0
                ? { memberDisplayNames: rosterNames }
                : {}),
            },
          };
          const sentName = await conv.send(encodeEnvelopeJsonBytes(nameEnvelope));
          // Local echo so the ACTOR sees the rename event bubble too.
          if (identity !== null) {
            await persistLocalRoomEvent({
              conversationId,
              senderAccountId: identity.accountId,
              envelope: nameEnvelope,
              contentHash: sentName.content_hash,
            });
          }
        } catch (err) {
          log.warn({ event: 'chat.info.groupName.failed', err: (err as Error).message });
        }
      })();
    },
    [conversationId, resolveConv, isGroup, iAmGroupAdmin, localConv?.name, localConv?.memberAccountIds, localConv?.memberDisplayNames, localConv?.avatarMediaId],
  );

  /** Verify identity — per-device "I checked the safety number" flag. */
  const handleVerifyIdentity = useCallback((): void => {
    const next = !chatInfoVerified;
    setChatInfoVerified(next);
    void setConversationSafetyVerified(conversationId, next);
  }, [chatInfoVerified, conversationId]);

  /** Report — open the reason picker (C5). The actual POST happens on submit
   *  with the chosen reason + consent, replacing the old hardcoded
   *  reason:'other'/consent:false. */
  const handleReportUser = useCallback((): void => {
    setChatSettingsOpen(false);
    setReportSheetOpen(true);
  }, []);

  const handleReportSubmit = useCallback(
    (reason: ReportReason, consent: boolean): void => {
      setReportSheetOpen(false);
      void (async () => {
        try {
          await postReport({
            eventId: '',
            conversationId: bytesToB64urlLocal(conversationIdBytes),
            reportedAccountId: localConv?.peerAccountId ?? '',
            reason,
            consent,
          });
          log.info({ event: 'chat.info.report.ok', conversationId, reason });
        } catch (err) {
          log.warn({ event: 'chat.info.report.failed', err: (err as Error).message });
        }
      })();
    },
    [conversationId, conversationIdBytes, localConv?.peerAccountId],
  );

  /** Block — local block list (per-device, immediate UI hide) PLUS the
   *  server-mirrored block (C2): `POST /v1/blocks` so the block survives
   *  reinstall AND message-service suppresses the blocked account's delivery to
   *  us at fanout. Best-effort on the network call; the local set always
   *  applies so the UI hides the peer immediately. */
  const handleBlockUser = useCallback((): void => {
    const peer = localConv?.peerAccountId;
    if (peer !== undefined && peer.length > 0) {
      addLocalBlock(peer);
      void post('/v1/blocks', { account_id: peer }).catch((err: unknown) => {
        log.warn({ event: 'chat.block.serverFailed', error: (err as Error).message });
      });
    }
    setChatSettingsOpen(false);
  }, [localConv?.peerAccountId]);

  const handleUnpinMessage = useCallback(
    (id: string): void => {
      const target = messages.find((m) => m.id === id)?.contentHash;
      if (target === undefined || target.length === 0) return;
      setPinnedTargets((prev) => {
        const next = new Set(prev);
        next.delete(target);
        return next;
      });
      void (async () => {
        try {
          const [identity, conv] = await Promise.all([retrieveIdentity(), resolveConv()]);
          if (conv === null) return;
          await conv.send(
            encodeEnvelopeJsonBytes({
              kind: 'com.ping.unpin',
              payload: {
                target,
                ...(identity !== null ? { actorDisplayName: identity.displayName } : {}),
              },
            }),
          );
        } catch (err) {
          log.warn({ event: 'chatroom.unpin.failed', err: (err as Error).message });
        }
      })();
    },
    [messages, resolveConv],
  );

  return (
    <>
    <div
      className="relative flex flex-1 min-h-0 flex-col"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
    {isConversationStranded ? (
      <StrandedRepairBanner onRestart={handleRestartSession} busy={restartingSession} />
    ) : null}
    {pendingJoinRequest !== null ? (
      <GroupJoinRequestBanner
        requesterName={pendingJoinRequest.requesterDisplayName ?? headerTitle}
        {...(headerAvatarUri !== undefined ? { requesterAvatarUri: headerAvatarUri } : {})}
        {...(pendingJoinGroupName !== undefined && pendingJoinGroupName.length > 0
          ? { groupName: pendingJoinGroupName }
          : {})}
        onApprove={handleApproveJoinRequest}
        onDismiss={handleDismissJoinRequest}
        busy={approvingJoinRequest}
      />
    ) : null}
    <ReactionReactorResolverProvider resolve={resolveReactor}>
    <ChatRoom
      conversationId={conversationId}
      peerName={headerTitle}
      pressEnterToSend={enterToSend}
      {...(headerAvatarUri !== undefined ? { peerAvatarUri: headerAvatarUri } : {})}
      gameSessions={gameSessions}
      onGameMove={handleGameMove}
      {...(!isGroup && peerPresence !== null ? { peerPresence } : {})}
      {...(headerSubtitle !== undefined ? { peerSubtitle: headerSubtitle } : {})}
      {...(headerActiveCall !== undefined ? { activeCall: headerActiveCall } : {})}
      messages={timelineWithReplyThumbs}
      {...(isGroup ? { isGroup: true as const } : {})}
      {...(senderNames !== undefined ? { senderNames } : {})}
      {...(senderAvatars !== undefined ? { senderAvatars } : {})}
      onComposerTextChange={ephemeralTyping.onComposerTextChange}
      {...(ephemeralTyping.typingSubtitle !== null &&
      ephemeralTyping.typingSubtitle.length > 0
        ? { typingLabel: ephemeralTyping.typingSubtitle }
        : {})}
      isLoading={isLoading}
      error={error}
      inviteWarning={inviteWarning}
      sendError={sendError}
      canSend={client !== null && hasLeftGroup === false}
      composerReadOnly={hasLeftGroup}
      onSend={handleSendComposite}
      onScheduleMessage={handleScheduleMessage}
      scheduledMessages={scheduledPending}
      onCancelScheduled={handleCancelScheduled}
      onRetry={handleRetry}
      stagedAttachments={stagedTrayItems}
      onRemoveStagedAttachment={handleRemoveStagedAttachment}
      onPressStagedAttachment={handlePressStagedAttachment}
      threadAffordances={threadAffordances}
      onStartVoice={handleStartVoice}
      onSendVoiceMessage={handleSendVoice}
      onCancelVoiceMessage={handleCancelVoice}
      subscribeAmplitudes={subscribeAmplitudes}
      gifProvider={gifProviderRef.current}
      onSendGif={handleSendGif}
      stickerProvider={stickerProviderRef.current}
      onSendSticker={handleSendSticker}
      messageVariants={messageVariants}
      mentionCandidates={mentionCandidates}
      onPickImages={handlePickImages}
      onPickDoc={handlePickDoc}
      onCaptureCamera={handleCaptureCamera}
      onPasteImage={handlePasteImage}
      onAudioCall={() =>
        void useCallStore.getState().startCall({
          conversationId,
          media: 'audio',
          title: headerTitle,
          isGroup,
          peerAccountIds: callPeerIds,
          peerDisplayNames: callPeerNames,
          ...(headerAvatarUri !== undefined ? { avatarUri: headerAvatarUri } : {}),
        })
      }
      onVideoCall={() =>
        void useCallStore.getState().startCall({
          conversationId,
          media: 'video',
          title: headerTitle,
          isGroup,
          peerAccountIds: callPeerIds,
          peerDisplayNames: callPeerNames,
          ...(headerAvatarUri !== undefined ? { avatarUri: headerAvatarUri } : {}),
        })
      }
      onSearch={handleOpenSearch}
      onMore={() => {
        if (isGroup && groupCreateEnabled) {
          setAddMembersOpen(true);
          return;
        }
        log.info({ event: 'chat.header.more', conversationId });
      }}
      onReactPress={handleReactPress}
      onReplyPressFromHover={handleReplyPress}
      onReplyPress={handleReplyPress}
      onEditPressFromHover={handleEditPress}
      onMenuAction={handleMenuAction}
      pinnedMessageIds={pinnedMessageIds}
      savedMessageIds={savedMessageIds}
      starredMessageIds={starredMessageIds}
      pinnedMessages={pinnedMessages}
      onUnpinMessage={handleUnpinMessage}
      replyTo={replyTo}
      {...(replyTo !== null && replyThumbByOriginalId[replyTo.contentHash] !== undefined
        ? {
            replyToThumbnailUri: replyThumbByOriginalId[replyTo.contentHash]!.uri,
            replyToThumbnailKind: replyThumbByOriginalId[replyTo.contentHash]!.kind,
          }
        : {})}
      onCancelReply={handleCancelReply}
      editingMessage={editingMessage}
      onSubmitEdit={handleSubmitEdit}
      onCancelEdit={handleCancelEdit}
      scrollToMessageRef={scrollToMessageRef}
      reactionsByMessageId={reactionsByMessageId}
      onToggleReaction={handleToggleReactionForMessage}
      onMentionPress={handleMentionPress}
      onAvatarPress={handleAvatarPress}
      onOpenChatSettings={() => setChatSettingsOpen(true)}
      {...(isGroup && localConv?.memberDisplayNames !== undefined
        ? { resolveReaderName }
        : {})}
      {...(isGroup ? { resolveReaderAvatar } : {})}
      {...(isGroup && authoritativeNonSelfCount > 0
        ? { groupNonSelfMemberCount: authoritativeNonSelfCount }
        : {})}
    />
    </ReactionReactorResolverProvider>
    <InChatSearchOverlay
      open={searchOpen}
      query={searchQuery}
      onQueryChange={setSearchQuery}
      results={searchResults}
      onPick={handlePickSearchResult}
      onClose={() => setSearchOpen(false)}
    />
    <DragDropOverlay visible={dragActive} />
    </div>
    {/* Chat Settings panel (1:1 / group info + members), opened from the
        ChatRoomHeader avatar/name. */}
    <ChatInfoModal
      visible={chatSettingsOpen}
      onClose={() => setChatSettingsOpen(false)}
      isGroup={isGroup}
      hasLeftGroup={hasLeftGroup}
      title={headerTitle}
      {...(isGroup
        ? { subtitle: `${memberCount} member${memberCount === 1 ? '' : 's'}` }
        : {})}
      members={mlsRosterMembers.length > 0 ? mlsRosterMembers : chatSettingsMembers}
      // Admin-only: gate add/remove-member affordances on the local user's role,
      // not merely on "this is a group". Non-admins see a read-only roster.
      canManageMembers={isGroup && iAmGroupAdmin}
      {...(headerAvatarUri !== undefined ? { avatarUri: headerAvatarUri } : {})}
      {...(isGroup && iAmGroupAdmin ? { onPickAvatar: handlePickAvatar } : {})}
      {...(isGroup && iAmGroupAdmin ? { onEditGroupName: handleEditGroupName } : {})}
      pinnedMessages={pinnedMessages.map(
        (p): ChatInfoMessagePreview => ({
          id: p.id,
          senderName: p.senderName,
          preview: p.preview,
          date: new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(
            new Date(p.timestamp),
          ),
        }),
      )}
      photos={chatInfoPhotos}
      files={chatInfoFiles}
      links={chatInfoLinks}
      threads={chatInfoThreads}
      savedMessages={chatInfoSaved}
      selfDestructEnabled={chatInfoTtl > 0}
      selfDestructDuration={formatTtl(chatInfoTtl > 0 ? chatInfoTtl : DEFAULT_TTL_SECONDS)}
      onToggleSelfDestruct={handleToggleSelfDestruct}
      onPickSelfDestructDuration={handlePickSelfDestructDuration}
      encryptionKey={chatInfoSafetyNumber}
      verified={chatInfoVerified}
      notificationSound={(pendingSound ?? chatInfoSound)?.name ?? DEFAULT_SOUND_NAME}
      soundDirty={pendingSound !== null}
      onPreviewSound={handlePreviewSound}
      onBrowseSound={handleBrowseSound}
      onSaveSound={handleSaveSound}
      onResetSound={handleResetSound}
      notificationLevel={
        localConv?.notificationLevel === undefined
          ? 'default'
          : (localConv.notificationLevel as ChatInfoNotificationLevel)
      }
      onNotificationLevelChange={(level: ChatInfoNotificationLevel) => {
        // UI 'default' maps to the per-conversation 'all' (use global prefs);
        // 'all'/'mentions'/'off' persist verbatim. setNotificationLevel keeps
        // the legacy `isMuted` flag in sync so the gate honours both.
        const persisted = level === 'default' ? 'all' : level;
        void setNotificationLevel(conversationId, persisted);
      }}
      favorite={chatInfoFavorite}
      onToggleFavorite={(next) => {
        setChatInfoFavorite(next);
        void setConversationFavorite(conversationId, next);
      }}
      nickname={chatInfoNickname}
      onSaveNickname={(next) => {
        setChatInfoNickname(next);
        void setConversationNickname(conversationId, next);
      }}
      onClearChat={() => {
        void clearConversationMessages(conversationId);
        setChatSettingsOpen(false);
      }}
      onPolls={() => {
        setChatSettingsOpen(false);
        shellHandlers?.openPollComposer();
      }}
      onVoiceCall={() => {
        setChatSettingsOpen(false);
        void useCallStore.getState().startCall({
          conversationId,
          media: 'audio',
          title: headerTitle,
          isGroup,
          peerAccountIds: callPeerIds,
          peerDisplayNames: callPeerNames,
          ...(headerAvatarUri !== undefined ? { avatarUri: headerAvatarUri } : {}),
        });
      }}
      onVideoCall={() => {
        setChatSettingsOpen(false);
        void useCallStore.getState().startCall({
          conversationId,
          media: 'video',
          title: headerTitle,
          isGroup,
          peerAccountIds: callPeerIds,
          peerDisplayNames: callPeerNames,
          ...(headerAvatarUri !== undefined ? { avatarUri: headerAvatarUri } : {}),
        });
      }}
      onSearch={handleOpenSearch}
      onShare={handleShare}
      onCopyLink={handleCopyLink}
      shareContacts={shareContacts}
      onShareToContacts={handleShareToContacts}
      onShareSearchUsers={handleShareSearchUsers}
      onViewMemberProfile={(memberId) => {
        setChatSettingsOpen(false);
        shellHandlers?.openUserProfile(memberId as unknown as UserId);
      }}
      onAddMemberContact={(memberId) => {
        // Persist the member as a contact via the real contacts API (was a
        // no-op log). Fire-and-forget — the contacts list liveQuery picks the
        // new row up; a 4xx is logged, not surfaced, to match the other
        // Chat-Info member actions' silent-success pattern.
        void addContact({ userId: memberId }).catch((err: unknown) =>
          log.warn({ event: 'chat.info.addContact.failed', error: (err as Error).message }),
        );
      }}
      onMakeAdmin={handleMakeAdmin}
      onRemoveMembers={handleRemoveMembers}
      {...(isGroup && groupCreateEnabled && iAmGroupAdmin
        ? {
            onAddMembers: () => {
              setChatSettingsOpen(false);
              setAddMembersOpen(true);
            },
          }
        : {})}
      onVerifyIdentity={handleVerifyIdentity}
      onBlockUser={handleBlockUser}
      onReportUser={handleReportUser}
      {...(onDeleteConversation !== undefined
        ? {
            onLeaveOrDelete: () => {
              setChatSettingsOpen(false);
              void (async () => {
                if (isGroup) {
                  // LEAVE A GROUP — propagate + non-destructive.
                  // OPTIMISTIC read-only FIRST (LOCAL-only): set `leftAt` on the row
                  // NOW so the composer flips to the read-only "You left" state
                  // IMMEDIATELY — before the (possibly slow, network-bound) leave
                  // broadcast + MLS self-remove below. The `useConversations`
                  // liveQuery → `localConv.leftAt` → `hasLeftGroup` re-emits on this
                  // write and re-renders the composer read-only. LOCAL-only on
                  // purpose: the authoritative `markConversationLeft` at the END of
                  // this branch also tells the SERVER (POST /leave), which marks us
                  // departed and could reject the still-a-member `com.ping.kick`
                  // broadcast — so the server leave MUST stay after the broadcast.
                  try {
                    await markConversationLeftLocal(conversationId);
                  } catch {
                    /* optimistic UI only — the authoritative mark runs at the end */
                  }
                  const identity = await retrieveIdentity();
                  const leaveEnvelope =
                    identity !== null
                      ? {
                          kind: 'com.ping.kick' as const,
                          payload: {
                            target_user_id: identity.accountId,
                            reason: 'left',
                            actorDisplayName: identity.displayName,
                            targetDisplayName: identity.displayName,
                          },
                        }
                      : null;
                  //  1. Broadcast `com.ping.kick` with `reason: 'left'` and
                  //     target = OUR account id WHILE STILL A MEMBER, so every
                  //     other member's receive handler prunes us from their
                  //     roster immediately and renders "X left". (Done before the
                  //     self-remove: after it we may not be able to send.)
                  let sentLeaveHash: Uint8Array | null = null;
                  try {
                    const conv = await resolveConv();
                    if (identity !== null && conv !== null && leaveEnvelope !== null) {
                      const sent = await conv.send(encodeEnvelopeJsonBytes(leaveEnvelope));
                      sentLeaveHash = sent.content_hash;
                      // Record the epoch we left at (a proposal doesn't advance it,
                      // so this is the current epoch). The re-invite detector clears
                      // `leftAt` only on a LIVE update at a HIGHER epoch — so a
                      // replayed old Welcome on reconnect can't reactivate the
                      // composer, but a genuine re-add (higher epoch) does.
                      await setConversationLeftEpoch(conversationId, Number(sent.epoch));
                    }
                  } catch (err) {
                    log.warn({
                      event: 'chat.leave.broadcast_failed',
                      conversationIdPrefix: conversationId.slice(0, 8),
                      error: (err as Error).message,
                    });
                  }
                  //  1b. ALWAYS write the local "You left" echo — even when the
                  //      broadcast send failed (offline, SDK group already torn
                  //      down, transport not resumed). Otherwise the composer goes
                  //      silently read-only with no explanation. Real hash when the
                  //      broadcast landed (server echo dedupes on `&contentHash`);
                  //      a random 32-byte hash otherwise (no echo will collide).
                  if (identity !== null && leaveEnvelope !== null) {
                    try {
                      const synthHash = new Uint8Array(32);
                      if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
                        crypto.getRandomValues(synthHash);
                      }
                      await persistLocalRoomEvent({
                        conversationId,
                        senderAccountId: identity.accountId,
                        envelope: leaveEnvelope,
                        contentHash: sentLeaveHash ?? synthHash,
                      });
                    } catch (err) {
                      log.warn({
                        event: 'chat.leave.echo_failed',
                        conversationIdPrefix: conversationId.slice(0, 8),
                        error: (err as Error).message,
                      });
                    }
                  }
                  //  2. MLS self-Remove PROPOSAL — a remaining member commits it
                  //     (MLS forbids committing your own removal); the server then
                  //     stops delivering this chat to us.
                  try {
                    await (
                      client as unknown as {
                        leaveConversation?: (id: Uint8Array) => Promise<void>;
                      }
                    ).leaveConversation?.(conversationIdBytes);
                  } catch (err) {
                    log.warn({
                      event: 'chat.leave.mls_failed',
                      conversationIdPrefix: conversationId.slice(0, 8),
                      error: (err as Error).message,
                    });
                  }
                  //  3. AUTHORITATIVE non-destructive leave: re-stamp `leftAt`,
                  //     mirror it to sibling devices, and tell the SERVER (POST
                  //     /leave → `op=removed` delta). Deliberately AFTER the
                  //     `com.ping.kick` broadcast + MLS self-remove above, because
                  //     the server-side leave marks us departed and would reject the
                  //     still-a-member broadcast. The composer already went read-only
                  //     via the optimistic LOCAL mark at the top; row + history stay.
                  await markConversationLeft(conversationId);
                  return;
                }
                // 1:1 "Delete chat" — self-Remove proposal then destructive local
                // delete (unchanged behaviour; a 1:1 with no live MLS group just
                // falls through to the local delete).
                try {
                  await (
                    client as unknown as {
                      leaveConversation?: (id: Uint8Array) => Promise<void>;
                    }
                  ).leaveConversation?.(conversationIdBytes);
                } catch (err) {
                  log.warn({
                    event: 'chat.leave.mls_failed',
                    conversationIdPrefix: conversationId.slice(0, 8),
                    error: (err as Error).message,
                  });
                }
                onDeleteConversation();
              })();
            },
          }
        : {})}
    />
    {/* Forward-message conversation picker — opened by the action
        menu's Forward entry. */}
    <ForwardOverlay
      open={forwardSource !== null}
      conversations={forwardTargets}
      onPick={handleForwardPick}
      onClose={() => setForwardSource(null)}
    />
    {/* Report reason picker (C5) — opened from Chat Info → Report. */}
    <ReportReasonSheet
      open={reportSheetOpen}
      reportedName={convName}
      onSubmit={handleReportSubmit}
      onClose={() => setReportSheetOpen(false)}
    />
    {/* Fullscreen media viewer — carousel scoped to the tapped media's
        contiguous same-sender group, opened by an image tap or a video
        card's fullscreen control. */}
    <MediaPreviewScreen
      open={previewIndex >= 0}
      items={previewItems}
      index={previewIndex >= 0 ? previewIndex : 0}
      onIndexChange={(i) => setPreviewMediaId(previewItems[i]?.id ?? null)}
      onClose={() => setPreviewMediaId(null)}
      onDownload={(item) => {
        const uri = item.uri;
        if (uri === undefined || typeof document === 'undefined') return;
        const a = document.createElement('a');
        a.href = uri;
        a.download =
          item.kind === 'doc'
            ? item.filename ?? 'document'
            : item.kind === 'video'
              ? 'video'
              : 'image';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }}
      onOpenExternal={(item) => {
        if (item.uri !== undefined) window.open(item.uri, '_blank', 'noopener,noreferrer');
      }}
      onForward={(item) => {
        const msg = messages.find((mm) => mm.id === item.id);
        if (msg !== undefined) {
          setPreviewMediaId(null);
          setForwardSource(msg);
        }
      }}
      onReply={(item) => {
        const msg = messages.find((mm) => mm.id === item.id);
        if (msg !== undefined) {
          setPreviewMediaId(null);
          setReplyTo(msg);
        }
      }}
      onCopy={(item) => {
        // Copy the decoded image bytes to the clipboard (best-effort —
        // requires a secure context + ClipboardItem support).
        if (item.kind !== 'image' || item.uri === undefined) return;
        const uri = item.uri;
        void (async () => {
          try {
            const ClipboardItemCtor = (
              globalThis as { ClipboardItem?: typeof ClipboardItem }
            ).ClipboardItem;
            if (
              typeof navigator === 'undefined' ||
              navigator.clipboard === undefined ||
              ClipboardItemCtor === undefined
            )
              return;
            const res = await fetch(uri);
            const blob = await res.blob();
            const type = blob.type.length > 0 ? blob.type : 'image/png';
            await navigator.clipboard.write([new ClipboardItemCtor({ [type]: blob })]);
            pingToast.success({ title: 'Image copied' });
          } catch (err) {
            log.debug({ event: 'chatroom.mediaPreview.copy_failed', err: (err as Error).message });
          }
        })();
      }}
      onGoToMessage={(item) => {
        setPreviewMediaId(null);
        // Defer past the overlay teardown so the row is laid out before
        // we scroll/highlight it.
        requestAnimationFrame(() => scrollToMessageRef.current?.(item.id));
      }}
      onDelete={(item) => {
        const msg = messages.find((mm) => mm.id === item.id);
        if (msg !== undefined) {
          setPreviewMediaId(null);
          handleMenuAction('remove', msg);
        }
      }}
    />
    {/* Read-only Call-Notes preview (footer "Meeting notes" → open). */}
    {notesPreview !== null ? (
      <div
        onClick={() => setNotesPreview(null)}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 2147483600,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, width: '100%' }}>
          <View className="max-h-[80vh] w-full rounded-[16px] border border-[#222429] bg-[#15171C] p-4">
            <View className="mb-3 flex-row items-center justify-between gap-2">
              <Text
                numberOfLines={1}
                className="min-w-0 flex-1 text-[18px] font-bold text-[#F5F6F8]"
              >
                {notesPreview.title || 'Call notes'}
              </Text>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Download notes"
                onPress={() => void downloadCallNotes(notesPreview.id)}
                className="h-8 items-center justify-center rounded-[8px] bg-[#222429] px-3 active:opacity-80"
              >
                <Text className="text-[13px] font-bold text-[#E8EAED]">Download</Text>
              </Pressable>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Close notes"
                onPress={() => setNotesPreview(null)}
                className="h-8 w-8 items-center justify-center rounded-full active:bg-white/[0.08]"
              >
                <Text className="text-[18px] text-[#E8EAED]">×</Text>
              </Pressable>
            </View>
            {/* Scroll region — overflowY is a runtime web concern, not a class. */}
            <div style={{ overflowY: 'auto', minHeight: 0 }}>
              {notesPreview.text.trim().length > 0 ? (
                <Text className="text-[14px] leading-[20px] text-[#F5F6F8]">
                  {notesPreview.text}
                </Text>
              ) : null}
              {notesPreview.items.length > 0 ? (
                <View className="mt-3">
                  <Text className="mb-2 text-[16px] font-bold text-[#F5F6F8]">Action items</Text>
                  {notesPreview.items.map((it) => (
                    <View key={it.id} className="mb-1.5 flex-row items-center gap-2">
                      <View
                        className={`h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border ${
                          it.done ? 'border-[#537EFF] bg-[#537EFF]' : 'border-[#2F333B]'
                        }`}
                      >
                        {it.done ? <Text className="text-[11px] text-white">✓</Text> : null}
                      </View>
                      <Text
                        className={`flex-1 text-[14px] leading-[20px] ${
                          it.done ? 'text-[#8C929E] line-through' : 'text-[#F5F6F8]'
                        }`}
                      >
                        {it.text}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </div>
          </View>
        </div>
      </div>
    ) : null}
    {/* Staged-attachment caption overlay (bug 3b) — opened by tapping a
        tray tile. The host owns the caption draft + applies it to the
        staged item on confirm. */}
    <AttachmentPreviewOverlay
      open={captionOverlayId !== null}
      attachments={captionOverlayItems}
      index={captionOverlayIndex >= 0 ? captionOverlayIndex : 0}
      onIndexChange={(i) => setCaptionOverlayId(captionOverlayItems[i]?.id ?? null)}
      {...(captionOverlayAttachment !== undefined
        ? { attachment: captionOverlayAttachment }
        : {})}
      onConfirm={handleConfirmCaption}
      onClose={handleCloseCaption}
      mentionCandidates={mentionCandidates}
    />
    <CameraCaptureOverlay
      open={cameraOpen}
      onCapture={(file) => {
        stageImageFiles([file]);
      }}
      onClose={() => setCameraOpen(false)}
    />
    {isGroup && groupCreateEnabled ? (
      <AddGroupMembersModal
        open={addMembersOpen}
        onDismiss={() => setAddMembersOpen(false)}
        conversationId={conversationId}
        existingMemberIds={localConv?.memberAccountIds ?? []}
      />
    ) : null}
    {/* "Poll votes" panel — opened from a poll card's "View votes". Looks up
        the live poll variant (counts + resolved voters) for the open target. */}
    {(() => {
      if (viewVotesPollTarget === null) return null;
      const pv = Object.values(messageVariants).find(
        (v): v is Extract<ChatMessageVariant, { kind: 'poll' }> =>
          v.kind === 'poll' && v.poll.pollTarget === viewVotesPollTarget,
      );
      if (pv === undefined) return null;
      return (
        <PollVotesPanel
          visible
          poll={pv.poll}
          counts={pv.counts}
          total={pv.total}
          {...(pv.votersByOption !== undefined ? { votersByOption: pv.votersByOption } : {})}
          onClose={() => setViewVotesPollTarget(null)}
        />
      );
    })()}
    {/* Edit-meeting modal (creator-only, opened from the meeting card's ⋯).
        `MeetingModal` uses the shared `@ping/ui` Modal, which portals + draws
        its own scrim, so we just drive `visible` — no RNModal wrapper. */}
    {editingMeeting !== null ? (
      <MeetingModal
        visible
        mode="edit"
        initial={editingMeeting.initial}
        onClose={() => setEditingMeeting(null)}
        onSubmit={(draft) => {
          submitMeetingEdit(editingMeeting.meetingId, draft);
        }}
      />
    ) : null}
    {/* Meeting responses sheet — opened from a card's counts line. */}
    <MeetingResponsesModal
      visible={responsesMeetingId !== null}
      onClose={() => setResponsesMeetingId(null)}
      title={responsesTitle}
      responses={meetingResponses}
    />
    </>
  );
}

// 24-bar placeholder waveform. Heights chosen to read as a sane
// voice clip envelope without leaking any data about the audio. The
// shared `useVoicePlayback` hook replaces this with the decoded
// amplitude buckets via the driver's `extractWaveform` path.
const PLACEHOLDER_WAVEFORM: readonly number[] = [
  0.3, 0.5, 0.7, 0.9, 0.6, 0.4, 0.5, 0.8, 0.7, 0.5, 0.3, 0.6, 0.9, 0.7, 0.5, 0.4, 0.6, 0.8, 0.5,
  0.3, 0.4, 0.6, 0.5, 0.3,
];

// Decode a `com.ping.poll` envelope body into the create-poll fields.
// Returns undefined for any other message so the messageVariants loop
// can detect poll messages + drive the in-chat poll result card.
interface ParsedPollBody {
  readonly question: string;
  readonly options: readonly string[];
  readonly multi_select?: boolean;
  readonly anonymous?: boolean;
  readonly closes_at?: number;
}
function parsePollBody(body: string): ParsedPollBody | undefined {
  if (body.length === 0 || body.charAt(0) !== '{') return undefined;
  try {
    const parsed = parseEnvelope(body);
    if (parsed !== undefined && parsed.kind === 'com.ping.poll') {
      return parsed.body as unknown as ParsedPollBody;
    }
  } catch {
    // not a poll envelope
  }
  return undefined;
}
function isPollMessage(body: string): boolean {
  return parsePollBody(body) !== undefined;
}

// Resolver consumed by `<VoiceMessageBubble>` (through the variant
// payload) to translate a persisted message body into a `VoiceSource`
// the shared playback driver can decode. Voice envelopes carry their
// audio bytes as base64 + a `mime` field; the web driver maps that to
// an HTMLAudioElement via a `blob:` URL internally, so the shared
// `macos-base64` source kind is the right wire shape here too.
// Per-conversation pinned-set cache (content hashes). pin/unpin
// envelopes aren't persisted as rows, so the set is cached in
// localStorage to survive reload. Keyed by conversation id.
const PINNED_KEY = (conversationId: string): string =>
  `com.ping.pins.${conversationId}`;

function loadPinnedTargets(conversationId: string): Set<string> {
  if (typeof localStorage === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(PINNED_KEY(conversationId));
    if (raw === null) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr)
      ? new Set(arr.filter((x): x is string => typeof x === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

function savePinnedTargets(conversationId: string, set: Set<string>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PINNED_KEY(conversationId), JSON.stringify([...set]));
  } catch {
    // Best-effort — a full / disabled storage shouldn't break the chat.
  }
}

// Saved-messages set — same localStorage pattern as pins. `com.ping.save`/
// `com.ping.unsave` are control envelopes (not persisted rows), so the set of
// saved content hashes is cached locally, keyed by conversation id.
const SAVED_KEY = (conversationId: string): string =>
  `com.ping.saved.${conversationId}`;

function loadSavedTargets(conversationId: string): Set<string> {
  if (typeof localStorage === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(SAVED_KEY(conversationId));
    if (raw === null) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr)
      ? new Set(arr.filter((x): x is string => typeof x === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

function saveSavedTargets(conversationId: string, set: Set<string>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SAVED_KEY(conversationId), JSON.stringify([...set]));
  } catch {
    // Best-effort.
  }
}

// Per-conversation custom notification sound (per-device preference). Stored as
// { name, dataUrl } in localStorage; absent = the default "Classic" tone.
const SOUND_KEY = (conversationId: string): string => `com.ping.sound.${conversationId}`;
const DEFAULT_SOUND_NAME = 'Classic';
const DEFAULT_SOUND_URL = '/sounds/message.wav';

interface CustomSound {
  readonly name: string;
  readonly dataUrl: string;
}

function loadCustomSound(conversationId: string): CustomSound | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SOUND_KEY(conversationId));
    if (raw === null) return null;
    const v = JSON.parse(raw) as Partial<CustomSound>;
    return typeof v?.name === 'string' && typeof v?.dataUrl === 'string'
      ? { name: v.name, dataUrl: v.dataUrl }
      : null;
  } catch {
    return null;
  }
}

function saveCustomSound(conversationId: string, sound: CustomSound | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (sound === null) localStorage.removeItem(SOUND_KEY(conversationId));
    else localStorage.setItem(SOUND_KEY(conversationId), JSON.stringify(sound));
  } catch {
    // Best-effort.
  }
}

function playSoundUrl(url: string): void {
  if (typeof Audio === 'undefined') return;
  try {
    const audio = new Audio(url);
    audio.volume = 0.6;
    void audio.play().catch(() => undefined);
  } catch {
    // Autoplay / decode failures are non-fatal for a preview.
  }
}

// Per-device conversation-name cache that OUTLIVES a Dexie row delete, so a
// delete+resume restores the real name (1:1 + group) instead of a placeholder.
const CONV_NAME_KEY = (conversationId: string): string => `com.ping.convname.${conversationId}`;

function isPlaceholderConvName(name: string): boolean {
  const n = name.trim();
  return n.length === 0 || n === 'New chat' || n === 'Group chat';
}

function readCachedConvName(conversationId: string): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(CONV_NAME_KEY(conversationId));
    return v !== null && v.trim().length > 0 ? v : null;
  } catch {
    return null;
  }
}

function writeCachedConvName(conversationId: string, name: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(CONV_NAME_KEY(conversationId), name.trim());
  } catch {
    // Best-effort.
  }
}

// Self-destruct duration presets (seconds). The toggle flips between Off (0)
// and the last/default duration; the duration row cycles through these.
const DEFAULT_TTL_SECONDS = 86_400; // 1 day
const SELF_DESTRUCT_PRESETS = [3_600, 86_400, 604_800] as const; // 1h, 1d, 1w

function formatTtl(seconds: number): string {
  if (seconds <= 0) return 'Off';
  if (seconds % 604_800 === 0) {
    const w = seconds / 604_800;
    return `${w} week${w > 1 ? 's' : ''}`;
  }
  if (seconds % 86_400 === 0) {
    const d = seconds / 86_400;
    return `${d} day${d > 1 ? 's' : ''}`;
  }
  if (seconds % 3_600 === 0) {
    const h = seconds / 3_600;
    return `${h} hour${h > 1 ? 's' : ''}`;
  }
  return `${Math.max(1, Math.round(seconds / 60))} min`;
}

/** URL-safe base64 of a byte array (no padding) — matches the BE's id encoding. */
function bytesToB64urlLocal(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i] as number);
  if (typeof btoa === 'undefined') return '';
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Per-device blocked-accounts set (local-only; cross-device DeviceGroup sync via
// `com.ping.blocked_user_upsert` is a follow-up). Used to hide a blocked peer's
// incoming messages on this device.
const BLOCKED_KEY = 'com.ping.blocked';

function loadLocalBlocks(): Set<string> {
  if (typeof localStorage === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(BLOCKED_KEY);
    if (raw === null) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr)
      ? new Set(arr.filter((x): x is string => typeof x === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

function addLocalBlock(accountId: string): void {
  if (typeof localStorage === 'undefined') return;
  const set = loadLocalBlocks();
  set.add(accountId);
  try {
    localStorage.setItem(BLOCKED_KEY, JSON.stringify([...set]));
  } catch {
    // Best-effort.
  }
}

function resolveWebVoiceSource(message: Message): VoiceSource | null {
  const payload = parseVoiceEnvelope(message.body);
  if (payload === undefined) return null;
  return { kind: 'macos-base64', base64: payload.bytes, mime: payload.mime };
}

// Web-side wrapper around `@ping/ui`'s `persistPendingMessage`. Writes
// a `deliveryState: 'pending'` row with a fresh local id BEFORE the
// SDK send fires. Returns the local id so the caller can patch the
// same row via `commitSentMessage` / `markMessageFailed` once the SDK
// resolves. Returns `null` if no identity is available — the caller
// falls back to the legacy post-send path so the bubble at least
// appears, just with a perceptible delay.
async function persistPendingMessage(params: {
  conversationId: string;
  body: string;
  retryRowId: string | null;
  // Reply parent metadata — populates the local-echo bubble's reply
  // quote so the SENDER sees the same inset peers get (peers rebuild it
  // from the envelope's `parent_id` in useIncomingMessages).
  inReplyTo?: { messageId: string; preview: string; senderName?: string };
}): Promise<string | null> {
  const identity = await retrieveIdentity();
  if (!identity) {
    log.warn({ event: 'chatroom.persistPending.no_identity' });
    return null;
  }
  // Reuse the retry row's id when present — the user clicked retry on
  // a previously-failed bubble, so we want the SAME on-screen position
  // to flip from 'failed' → 'pending' → 'sent' without ever moving.
  const id =
    params.retryRowId ??
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const sentAt = Date.now();
  // Empty contentHash — `commitSentMessage` patches in the real value
  // returned by the SDK's `conv.send`. We keep the empty string (not
  // a synthetic placeholder) so the Dexie `contentHash` index has no
  // ambiguity at lookup time: an "in-flight" row literally has no
  // hash, a "sent" row has the real one.
  await sharedPersistPendingMessage({
    messages: webMessagesPersister,
    conversations: webConversationsPersister,
    record: {
      id,
      conversationId: params.conversationId,
      epoch: 0,
      // Date.now() sorts the pending row to the end of the local-only
      // stream. `commitSentMessage` overwrites with the real value
      // after the SDK assigns one.
      serverSeq: sentAt,
      contentHash: '',
      senderAccountId: identity.accountId,
      body: params.body,
      sentAt,
      deliveryState: 'pending',
      ...(params.inReplyTo !== undefined ? { inReplyTo: params.inReplyTo } : {}),
    },
  });
  return id;
}

// Patches the existing pending row in place — bubble id unchanged, so
// React's keyed reconciliation reuses the same DOM node and the glyph
// just animates from clock → single-check.
async function commitSentMessage(params: {
  id: string;
  conversationId: string;
  envelope: SentMessageEnvelope;
  body: string;
}): Promise<void> {
  const identity = await retrieveIdentity();
  if (!identity) return;
  const contentHashHex = bytesToHexLocal(params.envelope.content_hash);
  const epoch =
    typeof params.envelope.epoch === 'bigint'
      ? Number(params.envelope.epoch)
      : params.envelope.epoch;
  // Stamp the row with the SDK envelope's HLC wall-clock — NOT a fresh
  // Date.now(). The dedup guard in `commitSentMessage` (and the incoming
  // path in useIncomingMessages) keys a message's identity on
  // (contentHash, sentAt), and `content_hash` is plaintext-only — so two
  // identical-text sends ("hi" twice) share the hash and are told apart
  // ONLY by `sentAt`. A fresh Date.now() can return the same millisecond
  // for two rapid sends, collapsing the repeat (the second is deleted and
  // never appears). The HLC wall_ms is distinct per envelope, matching how
  // every other platform persists self-sent rows. See messagePersistence.ts.
  const sentAt = extractEnvelopeWallMs(params.envelope) ?? Date.now();
  const preview = lastMessagePreview(params.body);
  await sharedCommitSentMessage({
    messages: webMessagesPersister,
    conversations: webConversationsPersister,
    id: params.id,
    conversationId: params.conversationId,
    contentHash: contentHashHex,
    epoch,
    serverSeq: sentAt,
    sentAt,
    senderAccountId: identity.accountId,
    ...(preview !== undefined ? { preview } : {}),
  });
}

// Flip an existing pending row to 'failed' so the bubble surfaces a
// retry affordance. No row delete — the user's "Retry" tap reuses
// the same id to come back through `persistPendingMessage`.
async function markMessageFailed(params: { id: string }): Promise<void> {
  await sharedMarkMessageFailed({
    messages: webMessagesPersister,
    id: params.id,
  });
}

// Web-side wrapper around `@ping/ui`'s `persistSentMessage`. Resolves
// the Dexie-persisted identity (for `senderAccountId`) and converts
// the SDK's `MessageEnvelope` into the shared `MessageRecord` shape.
async function persistSentMessage(params: {
  conversationId: string;
  envelope: SentMessageEnvelope;
  body: string;
  retryRowId: string | null;
}): Promise<void> {
  const identity = await retrieveIdentity();
  if (!identity) {
    log.warn({ event: 'chatroom.persistSent.no_identity' });
    return;
  }
  const contentHashHex = bytesToHexLocal(params.envelope.content_hash);
  const epoch =
    typeof params.envelope.epoch === 'bigint'
      ? Number(params.envelope.epoch)
      : params.envelope.epoch;
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  // HLC wall-clock, not Date.now() — keeps identical-text repeats distinct
  // for the dedup guard. See the note in `commitSentMessage` above.
  const sentAt = extractEnvelopeWallMs(params.envelope) ?? Date.now();

  await sharedPersistSentMessage({
    messages: webMessagesPersister,
    conversations: webConversationsPersister,
    record: {
      id,
      conversationId: params.conversationId,
      epoch,
      // Date.now() sorts sent rows to the end of the local-only stream.
      serverSeq: sentAt,
      contentHash: contentHashHex,
      senderAccountId: identity.accountId,
      body: params.body,
      sentAt,
      deliveryState: 'sent',
    },
    ...(params.retryRowId !== null ? { retryRowId: params.retryRowId } : {}),
  });
}

// Grace period after a scheduled message's send_at before the client re-lists
// to reconcile its delivery — covers the gateway delivery worker's tick +
// fan-out latency so the first poll usually already sees the row gone.
const SCHEDULED_DELIVERY_BUFFER_MS = 6_000;

// Self-echo a BACKEND-delivered scheduled message into the sender's own
// timeline. A scheduled message is pre-encrypted under the sender's ratchet
// and delivered later by the gateway worker; it NEVER returns to the sender
// over the wire (OpenMLS can't decrypt your own application message), so this
// local echo — sourced from the preview mirror's plaintext — is the sender's
// only representation of it. The id + content hash are DETERMINISTIC
// (`sched-<conv>-<sendAtMs>`) so a repeated reconcile (timer + mount + a
// re-list race) is idempotent: `put` overwrites in place and the persister's
// contentHash/sentAt dedup guard short-circuits.
async function persistDeliveredScheduledMessage(params: {
  conversationId: string;
  body: string;
  sentAtMs: number;
  contentHash?: string;
}): Promise<void> {
  const identity = await retrieveIdentity();
  if (!identity) {
    log.warn({ event: 'chatroom.schedule.deliver.no_identity' });
    return;
  }
  const stableId = `sched-${params.conversationId}-${params.sentAtMs}`;
  // Use the REAL content hash (captured at schedule time) so read + delivery
  // receipts — which key off content hash — land on this echoed bubble. Fall
  // back to the deterministic id for older mirror rows written before the hash
  // was captured (their receipts simply won't match, as before).
  const contentHash = params.contentHash ?? stableId;
  await sharedPersistSentMessage({
    messages: webMessagesPersister,
    conversations: webConversationsPersister,
    record: {
      id: stableId,
      conversationId: params.conversationId,
      epoch: 0,
      // Sort by the delivery instant so the bubble lands at send_at time.
      serverSeq: params.sentAtMs,
      contentHash,
      senderAccountId: identity.accountId,
      body: params.body,
      sentAt: params.sentAtMs,
      deliveryState: 'sent',
    },
  });
}

async function persistFailedMessage(params: {
  conversationId: string;
  body: string;
  attemptId: string | null;
}): Promise<void> {
  const identity = await retrieveIdentity();
  if (!identity) {
    log.warn({ event: 'chatroom.persistFailed.no_identity' });
    return;
  }
  // Reuse the previous failed row's id on retry so the bubble's
  // position in the list stays put.
  const id =
    params.attemptId ??
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `failed-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const contentHashHex = `failed-${id}`;
  const sentAt = Date.now();

  await sharedPersistFailedMessage({
    messages: webMessagesPersister,
    record: {
      id,
      conversationId: params.conversationId,
      epoch: 0,
      serverSeq: sentAt,
      contentHash: contentHashHex,
      senderAccountId: identity.accountId,
      body: params.body,
      sentAt,
      deliveryState: 'failed',
    },
  });
}

// Human-readable file-size label for the staged file tile, e.g.
// "920 B", "2.1 MB". Keeps one decimal for KB/MB/GB.
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIdx = 0;
  while (value >= 1024 && unitIdx < units.length - 1) {
    value /= 1024;
    unitIdx += 1;
  }
  return `${value.toFixed(1)} ${units[unitIdx]}`;
}

// Pull the HLC wall-clock (ms) off a sent envelope. The SDK assigns a
// distinct HLC per message, so this is the stable, collision-free identity
// for the local-echo dedup — unlike Date.now(), which two rapid identical
// sends can share. Mirrors `extractWallMs` in packages/ui/src/sync/syncEngine.
function extractEnvelopeWallMs(envelope: SentMessageEnvelope): number | null {
  const w = envelope.hlc?.wall_ms;
  if (typeof w === 'number') return w;
  if (typeof w === 'bigint') return Number(w);
  return null;
}

function bytesToHexLocal(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i++) {
    out += (b[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}

// 1-2 char initials from a display name, for the thread affordance's
// participant avatars (matches `groupSenderAvatarFromLabel`'s rule).
function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter((p) => p.length > 0);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]![0] ?? '?').toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase();
}

// Write a pending local-echo row for a staged image / file BEFORE the
// encrypt + multipart upload starts (bug 2). The body is a placeholder
// envelope of the right kind so `parseImageEnvelope` / `parseFileEnvelope`
// (and thus `messageVariants`) immediately renders the right card in an
// 'uploading' state. `sendImageMessage` / `sendFileMessage` patch this
// SAME row's body with the real `blob_ref` envelope + flip it to 'sent'
// on resolve (via the precomputed `localId`). Returns the row id, or
// `null` when no identity is available (caller falls back to the legacy
// post-send insert).
async function persistPendingAttachment(params: {
  conversationId: string;
  kind: 'image' | 'video' | 'file';
  file: File;
  caption?: string | undefined;
}): Promise<string | null> {
  const identity = await retrieveIdentity();
  if (!identity) {
    log.warn({ event: 'chatroom.persistPendingAttachment.no_identity' });
    return null;
  }
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `local-attach-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const sentAt = Date.now();
  const mime = params.file.type !== '' ? params.file.type : 'application/octet-stream';
  // Placeholder blob_ref — non-empty sentinel strings so the envelope
  // parsers accept the shape (the real ref is patched in post-upload).
  const placeholderRef = {
    media_id: 'pending',
    content_key: 'pending',
    content_iv: 'pending',
    hash: 'pending',
  };
  const captionField =
    params.caption !== undefined && params.caption.length > 0
      ? { caption: params.caption }
      : {};
  const body =
    params.kind === 'image'
      ? JSON.stringify({
          kind: 'com.ping.image',
          payload: { blob_ref: placeholderRef, w: 0, h: 0, mime, ...captionField },
        })
      : params.kind === 'video'
        ? JSON.stringify({
            kind: 'com.ping.video',
            payload: {
              blob_ref: placeholderRef,
              w: 0,
              h: 0,
              dur_ms: 0,
              mime,
              ...captionField,
            },
          })
        : JSON.stringify({
            kind: 'com.ping.file',
            payload: {
              blob_ref: placeholderRef,
              name: params.file.name,
              mime,
              size: params.file.size,
              ...captionField,
            },
          });
  await sharedPersistPendingMessage({
    messages: webMessagesPersister,
    conversations: webConversationsPersister,
    record: {
      id,
      conversationId: params.conversationId,
      epoch: 0,
      serverSeq: sentAt,
      // UNIQUE per echo. The `messages` table has a `&contentHash` UNIQUE
      // index, so persisting several pending attachments at once (the
      // "all media appear instantly" path) with an empty hash collapses
      // them to ONE row (ConstraintError on the 2nd+). A per-id sentinel
      // keeps every pending bubble distinct; `commitSentMessage` replaces
      // it with the real wire hash on send. It's not a 64-char hex hash,
      // so reply/pin/dedup lookups never match it.
      contentHash: `pending-${id}`,
      senderAccountId: identity.accountId,
      body,
      sentAt,
      deliveryState: 'pending',
    },
  });
  return id;
}

// Doc tile tap (bug 3a) — fetch + decrypt the blob, then trigger a
// browser download via a transient `<a download>`. Best-effort; logs
// and toasts on failure.
async function openDocAttachment(body: string): Promise<void> {
  const payload = parseFileEnvelope(body);
  if (payload === undefined) return;
  try {
    const blob = await fetchAndDecryptMediaBlob(payload.blob_ref);
    const url = URL.createObjectURL(blob);
    if (typeof document !== 'undefined') {
      const a = document.createElement('a');
      a.href = url;
      a.download = payload.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    // Revoke after a tick so the navigation/download has grabbed it.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  } catch (err) {
    log.warn({ event: 'chatroom.doc.open.failed', err: (err as Error).message });
    pingToast.error({
      title: "Couldn't open file",
      description: 'Check your connection and try again.',
    });
  }
}

// Desktop inline-image envelope shape — `com.ping.image` carrying the
// raw bytes base64-inlined under `bytes` (no `blob_ref`, no S3 upload).
// Mirrors `apps/desktop/.../sendImageMessage.ts`'s
// `parseImageInlineEnvelope`. Web decodes these to a data/object URL so
// desktop-sent images render here (cross-platform parity).
interface InlineImageEnvelopePayload {
  readonly mime: string;
  readonly w: number;
  readonly h: number;
  readonly bytes: string;
}

function parseInlineImageEnvelope(
  body: string | undefined,
): InlineImageEnvelopePayload | undefined {
  if (body === undefined || body.length === 0 || body.charAt(0) !== '{') {
    return undefined;
  }
  try {
    const parsed = JSON.parse(body) as {
      kind?: unknown;
      payload?: Record<string, unknown>;
    };
    if (parsed.kind !== 'com.ping.image') return undefined;
    const p = parsed.payload;
    if (
      p === undefined ||
      // `blob_ref` shape is the web upload path — handled elsewhere.
      p.blob_ref !== undefined ||
      typeof p.bytes !== 'string' ||
      typeof p.mime !== 'string' ||
      typeof p.w !== 'number' ||
      typeof p.h !== 'number'
    ) {
      return undefined;
    }
    return { mime: p.mime, w: p.w, h: p.h, bytes: p.bytes };
  } catch {
    return undefined;
  }
}

// Decode a base64 string into a Blob of the given mime type.
function base64ToBlob(base64: string, mime: string): Blob {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

// Normalize a `Uint8Array | number[]` (the two shapes `onMessage`'s
// IncomingMessage byte fields arrive as) into a `Uint8Array`.
function toUint8(v: Uint8Array | number[]): Uint8Array {
  return v instanceof Uint8Array ? v : Uint8Array.from(v);
}

function bytesLikeToHex(v: Uint8Array | number[]): string {
  return bytesToHexLocal(toUint8(v));
}

const GAME_KINDS: ReadonlySet<string> = new Set([
  'com.ping.game_create',
  'com.ping.game_join',
  'com.ping.game_move',
  'com.ping.game_reveal',
  'com.ping.game_outcome',
  'com.ping.game_abort',
]);

function isGameKind(kind: string): boolean {
  return GAME_KINDS.has(kind);
}

// Map a decoded `com.ping.game_*` ParsedAppEvent body to the
// `useGameSessions` GameEvent union. `senderHex` is the message
// sender's device id, used to attribute `by` / `createdBy` for
// envelopes whose body doesn't carry the actor (the wire bodies are
// actor-implicit — the sender IS the actor).
function gameEventFromParsed(
  kind: string,
  body: AppEventBody,
  senderHex: string,
): GameEvent | null {
  switch (kind) {
    case 'com.ping.game_create': {
      const b = body as GameCreateBody;
      if (b.session_id.length === 0) return null;
      return {
        type: 'create',
        sessionId: b.session_id,
        gameKind: b.game_kind,
        createdBy: senderHex,
      };
    }
    case 'com.ping.game_join': {
      const b = body as GameJoinBody;
      if (b.session_id.length === 0) return null;
      return {
        type: 'join',
        sessionId: b.session_id,
        by: senderHex,
        ...(typeof b.seat === 'number' ? { seat: b.seat } : {}),
      };
    }
    case 'com.ping.game_move': {
      const b = body as GameMoveBody;
      if (b.session_id.length === 0) return null;
      return {
        type: 'move',
        sessionId: b.session_id,
        moveIdx: b.move_idx,
        payload: b.payload,
        by: senderHex,
        ...(b.commit !== undefined ? { commit: b.commit } : {}),
      };
    }
    case 'com.ping.game_reveal': {
      const b = body as GameRevealBody;
      if (b.session_id.length === 0) return null;
      return {
        type: 'reveal',
        sessionId: b.session_id,
        moveIdx: b.move_idx,
        value: b.value,
        by: senderHex,
      };
    }
    case 'com.ping.game_outcome': {
      const b = body as GameOutcomeBody;
      if (b.session_id.length === 0) return null;
      return {
        type: 'outcome',
        sessionId: b.session_id,
        ...(b.winner !== undefined ? { winner: b.winner } : {}),
        ...(b.payouts !== undefined
          ? {
              payouts: b.payouts.map((p) => ({
                userId: p.user_id,
                points: p.points,
              })),
            }
          : {}),
        ...(b.final_state.length > 0 ? { finalState: b.final_state } : {}),
      };
    }
    case 'com.ping.game_abort': {
      const b = body as GameAbortBody;
      if (b.session_id.length === 0) return null;
      return { type: 'abort', sessionId: b.session_id, reason: b.reason };
    }
    default:
      return null;
  }
}

// Decode a 32-char hex conversation id (`019e4fb5…`) into the 16-byte
// representation `client.send` expects.
function hexToBytesLocal(hex: string): Uint8Array {
  const clean = hex.replace(/^0x/, '');
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}
